/**
 * PipelineDetailPage — Heroku-style 5-tab view of one deployed workflow.
 *
 * Mounted at /projects/:projectId/pipelines/:workflowType. Renders inside
 * ProjectLayout so sidebar + project context come from the parent.
 *
 * Tabs:
 *   Overview  — UUID, status, version, CLI commands to trigger
 *   Activity  — past executions of this workflow (DataTable)
 *   Logs      — pointer to `zibby workflow logs -t <uuid>` (no live SSE
 *               viewer in the dashboard yet — CLI is the canonical surface
 *               and we don't reimplement it badly)
 *   Settings  — repo / trigger config (placeholder for v1; reads workflow
 *               row + lets user delete)
 *   Env       — environment variable KEYS (values never returned from the
 *               server, by design — see backend/handlers/workflow-env.js)
 *
 * No new components — uses DataTable + plain divs styled in CSS.
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import PipelineStatus from './PipelineStatus';
import { useParams, useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { trashOutline, copyOutline, documentTextOutline, createOutline, checkmarkOutline, closeOutline, sparklesOutline, cubeOutline, arrowBackOutline, addOutline, flashOutline, eyeOutline, eyeOffOutline, lockClosedOutline } from 'ionicons/icons';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import { artifactLangInfo } from '../ArtifactViewPage/ArtifactViewPage';
import { IonIcon, IonButton, IonSpinner, IonToggle } from '@ionic/react';
import DataTable from '../../components/shared/DataTable/DataTable';
import ExecutionPipeline from '../../components/shared/ExecutionPipeline';
import PipelineGraphView from '../../components/shared/PipelineGraphView/PipelineGraphView';
import api from '../../services/api';
import { useAgent, useAgents, useDeleteAgent } from '../../hooks/useAgents';
import { inheritedAgentVendor } from '../../utils/agentComposition';
import useWorkflowIntegrationGate from '../../hooks/useWorkflowIntegrationGate';
import { queryKeys } from '../../hooks/queryKeys';
import WorkflowLogs from '../../services/workflowLogs';
import Addons from '../../services/addons';
import Credits, { formatBalance, formatRate } from '../../services/credits';
import InsufficientCreditsModal from '../../components/billing/InsufficientCreditsModal';
import SplitButton from '../../components/shared/SplitButton/SplitButton';
import DangerButton from '../../components/shared/DangerButton/DangerButton';
import DangerZone from '../../components/shared/DangerZone/DangerZone';
import LogLine, { parseLogPayload } from '../../components/shared/LogLine';
import PillButton from '../../components/shared/PillButton';
import AuthCard from '../../components/shared/AuthCard/AuthCard';
import ConfirmModal from '../../components/molecules/ConfirmModal/ConfirmModal';
import Field from '../../components/atoms/Field/Field';
import LetterAvatar from '../../components/atoms/LetterAvatar/LetterAvatar';
import JsonInput from '../../components/shared/SchemaForm/JsonInput';
import { WARM_POOL_QUANTITY_OPTIONS } from '../../config/warmPool';
import { isChatRuntime } from '../../utils/chat-runtime';
import { dispatchNodeMapOf } from '../../utils/dispatchMarker';
import { useAgentOptions, findAgentOptionValue } from '../../utils/agent-options';
import Marketplace from '../../services/marketplace';
import { timeAgo } from '../../utils/timeAgo';
import { CronExpressionParser } from 'cron-parser';
import cronstrue from 'cronstrue';
import usageService from '../../services/usage';
import TriggerModal from './TriggerModal';
import UpdateAgentModal from './UpdateAgentModal';
import AgentConfigModal from '../../components/molecules/AgentConfigModal/AgentConfigModal';
import IntegrationsConnectList from '../../components/shared/IntegrationsConnectList/IntegrationsConnectList';
import ConnectOAuthCard from '../../components/shared/ConnectOAuthCard/ConnectOAuthCard';
import useProjects from '../../hooks/useProjects';
import { Stores } from '../../services/stores';
import StoresTable from '../../components/shared/StoresTable/StoresTable';
import './PipelineDetailPage.css';
// Inline run-detail header reuses the standalone PipelineRunDetailPage's
// chrome (status pill / meta row / copy button). Importing its CSS
// keeps that surface DRY — same look on either render path.
import './PipelineRunDetailPage.css';
// The Logs tab reuses the app detail page's `.app-logs-*` log-viewer CSS
// (dark monospace buffer, line numbers, severity stripes, skeleton). That
// stylesheet is self-contained — importing it here keeps the two log
// surfaces visually identical without duplicating the rules.
import '../AppsPage/AppDetailPage.css';
// The webhook AUTHENTICATION methods are rendered as the SAME card the
// Billing page uses for "Dedicated Static IP" / "Warm Pool" (title left,
// description under, Enable/Disable action top-right). We mirror those
// classNames here so the cards look identical — importing the stylesheet
// keeps that surface DRY rather than re-declaring the rules.
import '../../components/billing/AddonCard.css';

const TERMINAL_STATUSES = new Set(['completed', 'success', 'succeeded', 'failed', 'cancelled', 'skipped', 'blocked', 'insufficient_context']);

// isChatRuntime (+ the chat-template slug set) now lives in the shared util
// utils/chat-runtime.js so the marketplace deploy gate and this detail page
// agree on what a Copilot is. Imported at the top of the file.

// Base the marketplace template logos are served from — mirrors the backend
// (handlers/workflows.js: FRONTEND_PUBLIC_URL || 'https://zibby.dev') so the
// detail header renders the same absolute icon the Agents list + sidebar do,
// and it resolves in local dev too (where /marketplace-icons/* isn't served).
const MARKETPLACE_ICON_BASE = 'https://zibby.dev';

/**
 * WorkflowDetailIcon — the workflow's logo shown right before the title in the
 * detail header. Reuses the exact pattern of the Agents-list `WorkflowIcon`
 * (PipelinesPage) and the sidebar `PinnedNavLink`: render the marketplace
 * template logo, fall back to the shared LetterAvatar (generated initial) when
 * there's no slug (CLI-deployed / custom workflow) or the image 404s. The
 * detail endpoint returns `marketplaceSlug` (survives renames); we derive the
 * absolute icon URL from it exactly like the backend list does — no extra fetch.
 */
const WorkflowDetailIcon = ({ marketplaceSlug, iconUrl, label }) => {
  const [failed, setFailed] = useState(false);
  const src = iconUrl || (marketplaceSlug
    ? `${MARKETPLACE_ICON_BASE}/marketplace-icons/${encodeURIComponent(marketplaceSlug)}.png`
    : null);
  if (!src || failed) {
    return <LetterAvatar name={label} className="pipeline-detail-title-icon" />;
  }
  return (
    <img
      src={src}
      onError={() => setFailed(true)}
      className="pipeline-detail-title-icon"
      alt=""
      loading="lazy"
    />
  );
};

/**
 * HelpIcon — tiny question-mark icon next to a section title. Hover
 * reveals the full description as a tooltip. Trades discoverability of
 * always-visible help text for a less cluttered surface — once a user
 * has read the hint once they don't need to re-read it on every visit.
 *
 * CSS-only hover. The `data-hint` attribute is rendered into the
 * ::after pseudo-element via the rule in PipelineDetailPage.css.
 */
function HelpIcon({ hint }) {
  return (
    <span
      className="pipeline-help-icon"
      data-hint={hint}
      tabIndex={0}
      role="tooltip"
      aria-label={hint}
      title={hint /* native title is a fallback for browsers that strip ::after */}
    >
      <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
        <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.3" />
        <text x="8" y="11.5" textAnchor="middle" fontSize="9" fontWeight="600" fill="currentColor">?</text>
      </svg>
    </span>
  );
}

// Common cron presets — one click fills the field. (All ≥5 min interval, which
// the backend enforces.)
const SCHEDULE_PRESETS = [
  { label: 'Every 15 min', cron: '*/15 * * * *' },
  { label: 'Every 30 min', cron: '*/30 * * * *' },
  { label: 'Hourly', cron: '0 * * * *' },
  { label: 'Every 6 hours', cron: '0 */6 * * *' },
  { label: 'Daily · 8am', cron: '0 8 * * *' },
  { label: 'Daily · midnight', cron: '0 0 * * *' },
  { label: 'Weekdays · 9am', cron: '0 9 * * 1-5' },
  { label: 'Weekly · Mon 9am', cron: '0 9 * * 1' },
  { label: 'Weekly · Fri 5pm', cron: '0 17 * * 5' },
  { label: 'Monthly · 1st 9am', cron: '0 9 1 * *' },
];

/* ---------------------------------------------------------------------------
 * Schedule entry shape — RECURRING vs ONE-SHOT.
 *
 * A schedule entry is EITHER recurring (`cron`, no `at`) or one-shot (`at`, no
 * `cron`) — the server enforces the exclusivity on write and never returns
 * both. These three predicates are the ONE place that decides which kind an
 * entry is; the modal's mode seed, the card renderer and the "live" count all
 * derive from them so they cannot disagree.
 *
 *   at      — ALWAYS a canonical UTC ISO instant ("2026-08-13T01:00:00.000Z")
 *   timezone— the IANA zone the user picked; display-only for a one-shot
 *   firedAt — present once it has run. A fired one-shot NEVER runs again.
 * ------------------------------------------------------------------------ */
const isOneShot = (entry) => !!entry?.at && !entry?.cron;
const isFiredOneShot = (entry) => isOneShot(entry) && !!entry.firedAt;
/** A schedule that can still fire. Recurring entries are always live (a
 *  DISABLED recurring entry stays counted, exactly as before this change). */
const isLiveSchedule = (entry) => !isFiredOneShot(entry);
/**
 * Consumed WITHOUT having run — the box was down over the instant and the
 * scheduler retired it rather than firing hours late (`missed` on the wire).
 * Rendering this as "Ran once" would report a run that never happened.
 */
const isMissedOneShot = (entry) => isFiredOneShot(entry) && !!entry.missed;
/**
 * OVERDUE: the instant has passed and nothing has claimed it — the schedule was
 * lost (a deleted EventBridge rule, a box that never came back). It is NOT the
 * same as "about to fire", and it is the one state an operator has to be able to
 * see, so the card says so instead of showing a countdown clamped at zero.
 */
const isOverdueOneShot = (entry) => isOneShot(entry) && !entry.firedAt
  && Date.parse(entry.at) < Date.now() - 60_000;

/**
 * Format a UTC ISO instant for display IN a given IANA zone. Falls back to the
 * browser's locale rendering if the zone is unusable (user-typed IANA can be
 * garbage — the server is the validator, we just don't crash the card).
 */
function formatInstantInZone(iso, timezone) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '—');
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone || 'UTC',
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      // ALWAYS name the zone. This instant is rendered in the ENTRY's zone while
      // "Next run" beside it renders in the BROWSER's — two clocks, one moment,
      // and without a label they read as a bug.
      timeZoneName: 'short',
    }).format(d);
  } catch {
    return d.toLocaleString();
  }
}

/**
 * UTC ISO instant → the `<input type="datetime-local">` value ("YYYY-MM-DDTHH:mm")
 * that shows that instant AS WALL-CLOCK IN `timezone`.
 *
 * ⚠️ NOT `toISOString().slice(0,16)` — that would show the UTC wall clock, so a
 * 9am-in-Shanghai one-shot would open the editor reading 01:00. Intl gives us
 * the zone's own calendar/clock parts; we only re-join them.
 */
function isoToLocalInputValue(iso, timezone) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone || 'UTC',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    if (!parts.year || !parts.hour) return '';
    // Belt for engines that still emit "24" for midnight under h23.
    const hour = parts.hour === '24' ? '00' : parts.hour;
    return `${parts.year}-${parts.month}-${parts.day}T${hour}:${parts.minute}`;
  } catch {
    return '';
  }
}

/**
 * "Is this datetime-local value in the future?" — answered WITHOUT any timezone
 * arithmetic in the browser (the server owns that; we only send the naive value
 * + the zone). Both sides are the same fixed-width "YYYY-MM-DDTHH:mm" string
 * rendered in the SAME zone, so a lexicographic compare IS a chronological one.
 * Returns null when the zone is unusable — then we simply let the server judge.
 */
function isFutureInZone(localValue, timezone) {
  const nowLocal = isoToLocalInputValue(new Date(), timezone);
  if (!nowLocal || !localValue) return null;
  return localValue.slice(0, 16) > nowLocal;
}

/** The browser's IANA zone — the sane default for a "run once at …" wall clock. */
function browserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * ScheduleModal — set / edit / clear a workflow's schedule.
 * Reuses the shared ConfirmModal shell + the atomic Field; Save/Clear live in
 * the body so we control async + error (the modal stays open on failure).
 * POST /workflows/:uuid/schedule {cron|at, timezone} ; DELETE to clear.
 *
 * TWO MODES, mutually exclusive on the wire:
 *   'repeat' (default) — the cron editor, unchanged.
 *   'once'             — a datetime-local + IANA zone. We send the NAIVE value
 *                        verbatim as `at` plus `timezone`; the SERVER resolves
 *                        it to an instant. No timezone math happens here.
 */
function ScheduleModal({ open, onClose, workflowUuid, current, allSchedules = [], onSaved, inputSchema, effectiveMaxRuntimeMinutes }) {
  const [cron, setCron] = useState('');
  // 'repeat' = cron (today's behaviour, the default) | 'once' = a single `at`.
  const [mode, setMode] = useState('repeat');
  // The raw `<input type="datetime-local">` value — "YYYY-MM-DDTHH:mm", the
  // exact naive form the backend accepts. Sent as-is, never converted.
  const [at, setAt] = useState('');
  const [tz, setTz] = useState('UTC');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  // The default input this schedule runs with every fire — collected via the
  // same SchemaForm/JsonInput the manual Trigger modal uses (pre-filled with
  // the workflow's inputSchema defaults, editable). Stored on schedule.input.
  const [schedInput, setSchedInput] = useState({});
  const [inputOk, setInputOk] = useState(true);
  // Optional per-schedule max-run-time override (minutes). Empty = "use the
  // agent default"; a value is stored on schedule.input.maxRuntimeMinutes and
  // applies to every cron fire. Seeded from the schedule's existing override.
  const [maxRuntime, setMaxRuntime] = useState('');
  const hasInputSchema = !!(inputSchema && inputSchema.properties && Object.keys(inputSchema.properties).length > 0);
  const fallbackMaxRuntime = Number.isInteger(effectiveMaxRuntimeMinutes) ? effectiveMaxRuntimeMinutes : 25;

  useEffect(() => {
    if (open) {
      // Mode is SEEDED from the entry's own shape via the one predicate — the
      // card renderer reads the same helper, so the two can't drift.
      const oneShot = isOneShot(current);
      setMode(oneShot ? 'once' : 'repeat');
      setCron(current?.cron || '');
      // Existing one-shot: show the stored UTC instant as wall-clock in ITS zone.
      setAt(oneShot ? isoToLocalInputValue(current.at, current.timezone || 'UTC') : '');
      setTz(current?.timezone || 'UTC');
      setErr('');
      setBusy(false);
      setSchedInput(current?.input || {});
      setInputOk(true);
      const existing = current?.input?.maxRuntimeMinutes;
      setMaxRuntime(Number.isInteger(existing) ? String(existing) : '');
    }
  }, [open, current]);

  // Switching mode also switches which zone default makes sense. A cron is
  // authored in UTC (the field's own label says so) — a wall-clock "run once
  // at 9am" almost never is, so a NEW one-shot defaults to the browser's zone.
  // Editing an existing entry always keeps that entry's stored zone.
  const switchMode = (next) => {
    setMode(next);
    setErr('');
    if (next === 'once') {
      if (!current?.timezone) setTz((prev) => (prev === 'UTC' ? browserTimezone() : prev));
    } else {
      setTz(current?.timezone || 'UTC');
    }
  };

  const oneShotMode = mode === 'once';

  let desc = '';
  try { if (cron.trim()) desc = cronstrue.toString(cron.trim(), { use24HourTimeFormat: false }); } catch { desc = ''; }
  const fiveFields = cron.trim().split(/\s+/).filter(Boolean).length === 5;

  // One-shot validity. `null` from isFutureInZone = "unusable zone" → we don't
  // block; the server 400s with a readable message and we surface it as usual.
  const atValue = at.trim().slice(0, 16);
  const atNotFuture = !!atValue && isFutureInZone(atValue, tz) === false;

  // Per-schedule max-run-time validity (empty = valid → no override sent).
  const maxRuntimeTrimmed = maxRuntime.trim();
  const maxRuntimeNum = maxRuntimeTrimmed === '' ? null : Number(maxRuntimeTrimmed);
  const maxRuntimeInvalid =
    maxRuntimeTrimmed !== '' &&
    (!Number.isInteger(maxRuntimeNum) || maxRuntimeNum < 1 || maxRuntimeNum > 60);

  const save = async () => {
    if (oneShotMode) {
      if (!atValue) { setErr('Pick the date and time to run at'); return; }
      // Re-check against a FRESH "now" (the modal may have sat open a while).
      if (isFutureInZone(atValue, tz) === false) { setErr('Pick a time in the future'); return; }
    } else if (!fiveFields) { setErr('Enter a 5-field cron — e.g. 0 9 * * 1-5'); return; }
    if (hasInputSchema && !inputOk) { setErr('Fix the input below before saving'); return; }
    if (maxRuntimeInvalid) { setErr('Max run time must be a whole number of minutes between 1 and 60'); return; }
    setBusy(true); setErr('');
    try {
      // Merge the optional max-run-time override into the saved schedule input.
      // It's an input field in its own right, so it applies whether or not the
      // agent declares an inputSchema. Cleared field → strip any prior override.
      const baseInput = hasInputSchema ? (schedInput || {}) : {};
      const mergedInput = { ...baseInput };
      if (maxRuntimeNum != null) {
        mergedInput.maxRuntimeMinutes = maxRuntimeNum;
      } else {
        delete mergedInput.maxRuntimeMinutes;
      }
      const sendInput = hasInputSchema || maxRuntimeNum != null;
      const entryBody = {
        // EXACTLY ONE of cron/at — sending both (or neither) is a 400.
        ...(oneShotMode ? { at: atValue } : { cron: cron.trim() }),
        timezone: tz,
        ...(sendInput ? { input: mergedInput } : {}),
      };
      if (current?.id) {
        // Editing an EXISTING entry — targeted upsert by its server id.
        await api.post(`/workflows/${workflowUuid}/schedule`, { ...entryBody, scheduleId: current.id });
      } else if (allSchedules.length > 0) {
        // ADDING alongside existing entries — declarative full replace (the
        // legacy single body would overwrite the default entry, not add).
        //
        // Spent one-shots are ROUND-TRIPPED, not dropped: `id` addresses the
        // stored entry, so the server skips the future/60s-lead checks for an
        // UNCHANGED `at` (it would otherwise 400 on a past instant) and
        // re-attaches `firedAt` from its OWN row — a client can neither forge
        // nor lose that stamp. Filtering them out here would silently erase the
        // user's one-time-run history every time they add a cron, and would
        // disagree with the self-host dashboard, which round-trips them.
        const keep = allSchedules.map((e) => ({
          id: e.id, name: e.name, timezone: e.timezone, input: e.input, enabled: e.enabled,
          ...(isOneShot(e) ? { at: e.at } : { cron: e.cron }),
        }));
        await api.post(`/workflows/${workflowUuid}/schedule`, { schedules: [...keep, entryBody] });
      } else {
        // First schedule — the legacy body (server files it as 'default').
        await api.post(`/workflows/${workflowUuid}/schedule`, entryBody);
      }
      onSaved?.();
      onClose?.();
    } catch (e) {
      // Backend rejects when the input misses a required (no-default) field
      // (the create-time guard). Surface the missing fields.
      const missing = e?.body?.missing;
      setErr(
        Array.isArray(missing) && missing.length
          ? `Missing required input: ${missing.join(', ')}`
          : (e?.body?.error || e?.body?.message || e?.message || 'Failed to save schedule'),
      );
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true); setErr('');
    try {
      // Editing one entry → remove just it; a fresh/first schedule → clear all
      // (identical to before multi-schedule, where one WAS all).
      await api.delete(`/workflows/${workflowUuid}/schedule${current?.id ? `?id=${encodeURIComponent(current.id)}` : ''}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      setErr(e?.body?.error || e?.message || 'Failed to clear schedule');
    } finally { setBusy(false); }
  };

  return (
    <ConfirmModal
      open={open}
      onClose={onClose}
      title={oneShotMode ? 'Schedule (run once)' : 'Schedule (cron)'}
      ariaLabel="Set schedule"
      secondaryLabel=""
      maxWidth={621}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Mode toggle — same chip vocabulary as the cron presets below. */}
        <div style={{ display: 'flex', gap: 8 }} role="group" aria-label="Schedule type">
          {[{ id: 'repeat', label: 'Repeat' }, { id: 'once', label: 'Run once' }].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => switchMode(m.id)}
              aria-pressed={mode === m.id}
              style={{
                fontSize: 12, padding: '5px 12px', borderRadius: 14,
                border: '1px solid rgba(255,255,255,0.18)',
                background: mode === m.id ? 'rgba(110,168,254,0.18)' : 'transparent',
                color: '#ddd', cursor: 'pointer',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        {oneShotMode ? (
          <>
            <div>
              <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Run once at <span style={{ opacity: 0.6 }}>(date and time · {tz || 'UTC'})</span>
              </label>
              <Field
                type="datetime-local"
                value={at}
                onChange={(e) => { setAt(e.target.value); setErr(''); }}
                autoComplete="off"
                disabled={busy}
                status={err || atNotFuture ? 'error' : null}
                aria-label="Date and time to run once at"
              />
              {atNotFuture && !err && (
                <p style={{ fontSize: 12, color: '#ff6b6b', margin: '6px 0 0' }}>
                  That time has already passed — pick a time in the future.
                </p>
              )}
              {!atNotFuture && !err && (
                <p style={{ fontSize: 12, opacity: 0.7, margin: '6px 0 0' }}>
                  Runs exactly once, then stops. It never repeats.
                </p>
              )}
              {err && <p style={{ fontSize: 12, color: '#ff6b6b', margin: '6px 0 0' }}>{err}</p>}
            </div>

            <div>
              <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Timezone <span style={{ opacity: 0.6 }}>(IANA — the zone the time above is read in)</span>
              </label>
              <div style={{ width: 260 }}>
                <Field
                  type="text"
                  value={tz}
                  onChange={(e) => { setTz(e.target.value); setErr(''); }}
                  placeholder="UTC"
                  autoComplete="off"
                  spellCheck={false}
                  disabled={busy}
                  aria-label="IANA timezone the run-once time is interpreted in"
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <div>
              <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
                Cron expression <span style={{ opacity: 0.6 }}>(min hour day month weekday · UTC)</span>
              </label>
              <Field
                type="text"
                value={cron}
                onChange={(e) => { setCron(e.target.value); setErr(''); }}
                placeholder="0 9 * * 1-5"
                autoComplete="off"
                spellCheck={false}
                status={err ? 'error' : null}
              />
              {desc && <p style={{ fontSize: 12, opacity: 0.7, margin: '6px 0 0' }}>{desc}</p>}
              {err && <p style={{ fontSize: 12, color: '#ff6b6b', margin: '6px 0 0' }}>{err}</p>}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {SCHEDULE_PRESETS.map((p) => (
                <button
                  key={p.cron}
                  type="button"
                  onClick={() => { setCron(p.cron); setErr(''); }}
                  style={{
                    fontSize: 12, padding: '5px 10px', borderRadius: 14,
                    border: '1px solid rgba(255,255,255,0.18)',
                    background: cron.trim() === p.cron ? 'rgba(110,168,254,0.18)' : 'transparent',
                    color: '#ddd', cursor: 'pointer',
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </>
        )}

        {hasInputSchema && (
          <div>
            <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
              Saved input <span style={{ opacity: 0.6 }}>(stored on the schedule — {oneShotMode ? 'used for the single run' : 'reused on every cron fire'}; pre-filled with the agent's defaults, edit if needed)</span>
            </label>
            <JsonInput
              schema={inputSchema}
              initialValue={current?.input}
              onChange={(parsed, { ok }) => { setSchedInput(parsed || {}); setInputOk(ok); setErr(''); }}
              disabled={busy}
              hideInternal
              label="Saved schedule input (JSON)"
            />
          </div>
        )}

        <div>
          <label style={{ display: 'block', fontSize: 12, opacity: 0.7, marginBottom: 6 }}>
            Max run time <span style={{ opacity: 0.6 }}>(minutes · optional override for this schedule)</span>
          </label>
          <div style={{ width: 160 }}>
            <Field
              type="number"
              min={1}
              max={60}
              step={1}
              inputMode="numeric"
              value={maxRuntime}
              onChange={(e) => { setMaxRuntime(e.target.value); setErr(''); }}
              placeholder={`Default: ${fallbackMaxRuntime}min`}
              disabled={busy}
              status={maxRuntimeInvalid ? 'error' : null}
              aria-label="Max run time per scheduled run, in minutes"
            />
          </div>
          <p style={{ fontSize: 12, opacity: 0.7, margin: '6px 0 0' }}>
            How long each scheduled run may take before it's stopped. Leave blank
            to use the agent default ({fallbackMaxRuntime} min). Range 1–60.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginTop: 4 }}>
          {/* Keys off the ENTRY existing, not off it having a cron — a one-shot
              is just as deletable (and a spent one especially so). */}
          {current ? (
            <button
              type="button"
              onClick={clear}
              disabled={busy}
              style={{ fontSize: 13, color: '#ff6b6b', background: 'transparent', border: 'none', cursor: busy ? 'default' : 'pointer' }}
            >
              Delete schedule
            </button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              style={{ fontSize: 13, color: '#aaa', background: 'transparent', border: 'none', cursor: 'pointer' }}
            >
              Cancel
            </button>
            <PillButton
              className="pill-button--sm"
              onClick={save}
              disabled={busy || maxRuntimeInvalid || (oneShotMode ? (!atValue || atNotFuture) : !cron.trim())}
              loading={busy}
            >
              Save schedule
            </PillButton>
          </div>
        </div>
      </div>
    </ConfirmModal>
  );
}

/**
 * ScheduleEntryCard — ONE schedule's live card: cron + human description +
 * timezone + next-run countdown (1 Hz tick) + quota warning + Edit/Remove.
 * Per-entry component so each schedule owns its own countdown hooks.
 *
 * Renders BOTH entry kinds off the shared `isOneShot` predicate:
 *   recurring  — cronstrue description + cron-parser countdown (unchanged).
 *   one-shot   — "Runs once · <instant in its zone>" + a countdown to `at`;
 *                once `firedAt` is set it dims to "Ran once · <firedAt>" with
 *                no next run and no countdown (it will never run again).
 */
function ScheduleEntryCard({ entry, usage, onEdit, onRemove }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const oneShot = isOneShot(entry);
  const fired = isFiredOneShot(entry);
  const missed = isMissedOneShot(entry);
  // Re-evaluated on the 1Hz tick (via `now`) so a card watched across its instant
  // moves from "Runs once" → countdown 0 → "Overdue" without a reload.
  const overdue = useMemo(() => isOverdueOneShot(entry), [entry, now]); // eslint-disable-line react-hooks/exhaustive-deps

  const nextRun = useMemo(() => {
    if (isOneShot(entry)) {
      // A one-shot's `at` IS the next run — and a fired one has none.
      if (entry.firedAt) return null;
      const d = new Date(entry.at);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (!entry?.cron) return null;
    try {
      const interval = CronExpressionParser.parse(entry.cron, {
        currentDate: new Date(),
        tz: entry.timezone || 'UTC',
      });
      return interval.next().toDate();
    } catch (err) {
      console.warn('[schedule] cron parse failed:', err.message);
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entry?.cron, entry?.at, entry?.firedAt, entry?.timezone, entry?.updatedAt]);

  const deltaMs = nextRun ? Math.max(0, nextRun.getTime() - now.getTime()) : null;
  const formatDelta = (ms) => {
    if (ms === null) return '—';
    const s = Math.floor(ms / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    if (h > 0) return `${h}h ${m}m ${sec}s`;
    if (m > 0) return `${m}m ${sec}s`;
    return `${sec}s`;
  };

  let cronDescription = null;
  if (!oneShot) {
    try {
      cronDescription = cronstrue.toString(entry.cron, { use24HourTimeFormat: false, verbose: false });
    } catch { /* malformed cron — just don't show description */ }
  }

  const disabled = entry.enabled === false;
  // A spent one-shot can't be skipped by a quota it will never ask for.
  const quotaExceeded = !!usage?.exceeded && !fired;
  const quotaTooltip = quotaExceeded
    ? `Out of agent-execution quota for this billing period (${usage.used}/${usage.limit}). Schedule will keep ticking in AWS but the run will be skipped on each fire until quota resets or you upgrade.`
    : null;

  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: '12px',
        padding: '12px',
        background: 'rgba(255, 255, 255, 0.03)',
        borderRadius: '6px',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        opacity: fired ? 0.5 : (disabled ? 0.55 : 1),
      }}>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>
            {entry.name ? entry.name : (oneShot ? 'One-time' : 'Cron')}{disabled ? ' · disabled' : ''}
          </div>
          {oneShot ? (
            <>
              <div style={{ fontSize: '14px', marginTop: '4px', color: overdue ? '#f1b94c' : undefined }}>
                {missed
                  ? `Missed · ${formatInstantInZone(entry.at, entry.timezone)}`
                  : fired
                    ? `Ran once · ${formatInstantInZone(entry.firedAt, entry.timezone)}`
                    : overdue
                      ? `Overdue · was due ${formatInstantInZone(entry.at, entry.timezone)}`
                      : `Runs once · ${formatInstantInZone(entry.at, entry.timezone)}`}
              </div>
              {missed && (
                <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.7 }}>
                  Never ran — the scheduler was down at that time and it was too late to catch up.
                </div>
              )}
              {!missed && fired && (
                <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.7 }}>
                  Already ran — it will not run again.
                </div>
              )}
              {overdue && (
                <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.85, color: '#f1b94c' }}>
                  The time has passed and no run was recorded. Re-save it with a new time.
                </div>
              )}
            </>
          ) : (
            <>
              <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px' }}>
                {entry.cron}
              </div>
              {cronDescription && (
                <div style={{ fontSize: '12px', marginTop: '2px', opacity: 0.7 }}>
                  {cronDescription}
                </div>
              )}
            </>
          )}
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Timezone</div>
          <div style={{ fontSize: '14px', marginTop: '4px' }}>{entry.timezone || 'UTC'}</div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            Next run
            {quotaExceeded && (
              <span
                className="pipeline-help-icon"
                data-hint={quotaTooltip}
                style={{ color: '#f1b94c' }}
                tabIndex={0}
                role="tooltip"
                aria-label={quotaTooltip}
                title={quotaTooltip}
              >
                <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true">
                  <path d="M8 1.5 L15 14 H1 Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
                  <line x1="8" y1="6" x2="8" y2="10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
                  <circle cx="8" cy="12" r="0.9" fill="currentColor" />
                </svg>
              </span>
            )}
          </div>
          <div style={{ fontSize: '14px', marginTop: '4px', textDecoration: quotaExceeded ? 'line-through' : undefined, opacity: quotaExceeded ? 0.55 : 1 }}>
            {fired || overdue ? 'None' : (disabled ? '—' : (nextRun ? nextRun.toLocaleString() : '—'))}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '11px', textTransform: 'uppercase', opacity: 0.6, letterSpacing: '0.05em' }}>Countdown</div>
          <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '14px', marginTop: '4px', color: quotaExceeded || overdue ? '#f1b94c' : (!fired && deltaMs !== null && deltaMs < 60_000 ? '#7fdc7f' : undefined) }}>
            {missed ? 'Missed' : fired ? 'Ran' : overdue ? 'Overdue' : (disabled ? 'Disabled' : (quotaExceeded ? 'Paused (no quota)' : formatDelta(deltaMs)))}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
          <PillButton className="pill-button--sm" onClick={onEdit}>Edit</PillButton>
          <PillButton className="pill-button--sm" onClick={onRemove}>Remove</PillButton>
        </div>
      </div>
      {entry.input && Object.keys(entry.input).length > 0 && (
        <p className="pipeline-section-hint" style={{ marginTop: 6 }}>
          <strong>Fixed input:</strong> <code>{JSON.stringify(entry.input)}</code>
        </p>
      )}
    </div>
  );
}

/**
 * ScheduleStatusPanel — live display of a workflow's cron SCHEDULES.
 *
 * MULTI-schedule: GET /workflows/{uuid}/schedule → { schedules: [...] }
 * (older backends: { schedule } — wrapped). Each entry renders its own
 * ScheduleEntryCard; "Add schedule" appends via the declarative replace,
 * Edit targets one entry by its server id, Remove deletes one (?id=).
 * An agent syncing several sources holds one entry per source, each with
 * its own cron + input payload.
 */
function ScheduleStatusPanel({ workflowUuid, inputSchema, effectiveMaxRuntimeMinutes }) {
  const [schedules, setSchedules] = useState([]);
  const [loading,  setLoading]    = useState(true);
  const [error,    setError]      = useState(null);
  const [usage,    setUsage]      = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  // The entry being edited (null = adding a new one).
  const [editing, setEditing] = useState(null);
  // Bumped after a save/clear so the list re-fetches without a reload.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get(`/workflows/${workflowUuid}/schedule`)
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.schedules) ? data.schedules
          : (data?.schedule ? [data.schedule] : []);
        setSchedules(list);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.status === 404 || /404/.test(String(err?.message || ''))) {
          setSchedules([]);
        } else {
          setError(err.message || 'Failed to load schedule');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid, reloadKey]);

  useEffect(() => {
    let cancelled = false;
    usageService.getUsage()
      .then((data) => {
        if (cancelled) return;
        const used  = Number(data?.workflowExecutionsUsed)  || 0;
        const limit = Number(data?.workflowExecutionsLimit) || 0;
        setUsage({ used, limit, exceeded: limit > 0 && used >= limit });
      })
      .catch(() => { /* usage fetch is best-effort — don't block schedule UI */ });
    return () => { cancelled = true; };
  }, [workflowUuid]);

  // Entries that can still fire. A spent one-shot stays VISIBLE (dimmed) but is
  // not "active" — the header count, like the card, derives from the one helper.
  const liveCount = schedules.filter(isLiveSchedule).length;

  const removeEntry = async (entry) => {
    const label = entry.name || (isOneShot(entry)
      ? `run once at ${formatInstantInZone(entry.at, entry.timezone)}`
      : entry.cron);
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the schedule "${label}"? Other schedules keep running.`)) return;
    try {
      await api.delete(`/workflows/${workflowUuid}/schedule?id=${encodeURIComponent(entry.id)}`);
      setReloadKey((k) => k + 1);
    } catch (e) {
      setError(e?.body?.error || e?.message || 'Failed to remove schedule');
    }
  };

  const scheduleBtn = (
    <PillButton className="pill-button--sm" onClick={() => { setEditing(null); setModalOpen(true); }}>
      {schedules.length ? 'Add schedule' : 'Set schedule'}
    </PillButton>
  );
  const scheduleModal = (
    <ScheduleModal
      open={modalOpen}
      onClose={() => setModalOpen(false)}
      workflowUuid={workflowUuid}
      current={editing}
      allSchedules={schedules}
      onSaved={() => setReloadKey((k) => k + 1)}
      inputSchema={inputSchema}
      effectiveMaxRuntimeMinutes={effectiveMaxRuntimeMinutes}
    />
  );

  if (loading || error) return null;

  if (!schedules.length) {
    return (
      <div className="pipeline-section">
        <div className="pipeline-schedule-header">
          <h3 className="pipeline-section-title">
            Active schedule
            <HelpIcon hint={'No schedule is attached to this agent. Click "Set schedule" to add a repeating cron or a one-time run (or use the CLI below).'} />
          </h3>
          {scheduleBtn}
        </div>
        <p className="pipeline-section-hint" style={{ opacity: 0.65, fontStyle: 'italic' }}>
          No schedule attached. Click <strong>Set schedule</strong> above, or use <code>zibby workflow schedule</code> below.
        </p>
        {scheduleModal}
      </div>
    );
  }

  return (
    <div className="pipeline-section">
      <div className="pipeline-schedule-header">
        <h3 className="pipeline-section-title">
          {liveCount === 0 ? 'Schedules' : (liveCount === 1 ? 'Active schedule' : `Active schedules (${liveCount})`)}
          <HelpIcon hint="The schedules attached to this agent — repeating (cron) or one-time runs. Each fires independently with its own input payload (e.g. one per sync source) and counts toward your agent-execution quota. A one-time schedule that has already run is shown dimmed and never fires again." />
        </h3>
        {scheduleBtn}
      </div>
      {scheduleModal}
      {schedules.map((entry) => (
        <ScheduleEntryCard
          key={entry.id || entry.cron || entry.at}
          entry={entry}
          usage={usage}
          onEdit={() => { setEditing(entry); setModalOpen(true); }}
          onRemove={() => removeEntry(entry)}
        />
      ))}
    </div>
  );
}

const TABS = [
  { id: 'overview', label: 'Overview' },
  // Logs — paginated tail of the workflow's container logs (the execution
  // log group `/ecs/zibby-agent-<stage>`, which also carries warm-runner
  // warm-up output). Served by the workflow-logs subdomain RestApi, NOT the
  // main API host — see services/workflowLogs.js. The CLI's `-t` live SSE
  // tail is still the canonical real-time surface; this is the historical
  // browse view that survives sleeps (CloudWatch retains the events).
  { id: 'logs',     label: 'Logs' },
  { id: 'activity', label: 'Activity' },
  // Artifacts — shareable pages this agent published (reports, dashboards, …).
  // The INDEX rides kv-memory at scope "<workflowType>:artifact:<id>" (the same
  // records the artifact skill writes on publish); listed via the SAME
  // /credits/review-memory recall-prefix the self-host tab uses — one contract,
  // both dashboards. Rows open the existing /artifact/:id view page.
  { id: 'artifacts', label: 'Artifacts' },
  { id: 'settings', label: 'Settings' },
  { id: 'env',      label: 'Env' },
];

const PipelineDetailPage = () => {
  const { projectId, workflowType, executionId } = useParams();
  const navigate = useNavigate();
  const { setToast } = useOutletContext() || {};
  const [searchParams, setSearchParams] = useSearchParams();
  // Live agent:model SSOT picker options for the Agent & model modal.
  const { options: agentOptions } = useAgentOptions();
  const queryClient = useQueryClient();

  // ⚠️ Zibby Sentry self-test bug — CONTROLLED + HARMLESS. Fires ONLY when the
  // URL has ?selftest=sentry, so normal users never hit it and the page keeps
  // working. It reports one real error to Sentry to demo triage → auto-fix.
  // The "fix" is to delete this entire useEffect block.
  useEffect(() => {
    if (searchParams.get('selftest') === 'sentry') {
      Sentry.captureException(
        new Error('AgentDetailsPage selftest: failed to read agent node configuration [ZIBBY_SELFTEST_SENTRY_BUG]'),
      );
    }
  }, [searchParams]);

  // Agent/workflow detail — React-Query cached (5min staleTime). Navigating
  // list→detail→list→detail within the staleTime window reads from cache (no
  // refetch). `pipeline` is the cached row; a 404 surfaces as `error` and falls
  // through to the NotFoundPage branch below (pipeline stays null).
  const { data: pipeline = null, isLoading: pipelineLoading, isFetching: pipelineFetching, error: pipelineError, refetch } = useAgent(projectId, workflowType);

  // The vendor this agent's process actually runs under: its own row first;
  // for a dispatched SUB-AGENT whose row declares nothing, the OWNING parent's
  // resolved vendor (bound-uuid membership walk over the cached agents list —
  // inheritedAgentVendor). An in-process child dispatch runs inside the
  // parent's container, whose AGENT_TYPE is exactly that value, so a locked
  // disc on a bare child page ("Fixed" with no vendor) was hiding the vendor
  // the run genuinely uses. List rows carry the RESOLVED vendor (backend list
  // projection runs resolveRunVendor), so a parent configured via node pins
  // answers too.
  const { data: agentRows } = useAgents(projectId);
  const effectiveAgentVendor = useMemo(
    () => (pipeline?.aiAgent || inheritedAgentVendor(pipeline, agentRows) || ''),
    [pipeline, agentRows],
  );

  // Delete mutation — onSuccess invalidates the project's agent LIST and removes
  // THIS agent's cached detail (no ghost). Used by the Settings Danger zone.
  const deleteAgentMutation = useDeleteAgent(projectId);

  // Optimistically patch the cached detail row in place. Mirrors the old
  // `setPipeline((prev) => ...)` but writes to the query cache so the change
  // survives + stays the single source of truth. Children call this through the
  // `onPipelineUpdate` prop with a functional updater.
  const patchPipeline = useCallback((updater) => {
    queryClient.setQueryData(
      queryKeys.agents.detail(projectId, workflowType),
      (prev) => (typeof updater === 'function' ? updater(prev) : updater),
    );
  }, [queryClient, projectId, workflowType]);
  // Warm enabled but the runner isn't actually RUNNING yet → the header shows
  // "Starting" (not "Live"). Polled via the warm-pool status endpoint until
  // the runner is up. See the effect below.
  const [warmProvisioning, setWarmProvisioning] = useState(false);
  // Marketplace "Update available" — open the confirm modal; force-deploy
  // POST is wrapped in setUpdating so the button shows a spinner.
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Trigger modal — collects { input } per workflow.graph.stateSchema and
  // POSTs to the shared trigger endpoint (same gate as the CLI). On success
  // we navigate to the run page so the Activity tab's existing polling
  // picks up live status without an extra hop.
  const [triggerModalOpen, setTriggerModalOpen] = useState(false);
  // Required-integration HARD gate for the header Trigger button: an agent
  // whose REQUIRED integration is unconnected can't produce a useful run (but
  // would still bill), so Trigger is disabled with a "Connect <X> first"
  // tooltip. Reads the SAME per-workflow status source the Settings SKILLS
  // section renders from; fail-open (loading/failed status never blocks).
  const {
    blocked: triggerBlocked,
    missingLabels: triggerMissingLabels,
  } = useWorkflowIntegrationGate(pipeline?.uuid);
  // Per-pipeline agent + model + token editor.
  const [agentModalOpen, setAgentModalOpen] = useState(false);
  // The graph owns the per-node model state, so the Trigger button asks IT
  // whether every node can run rather than re-deriving it here.
  const graphRef = useRef(null);
  const handleTriggered = useCallback((jobId) => {
    setTriggerModalOpen(false);
    navigate(`/projects/${projectId}/agents/${workflowType}/runs/${jobId}`);
  }, [navigate, projectId, workflowType]);

  // Inline edit state for displayName (the pencil icon next to the
  // workflow title). workflowType — the URL slug — stays immutable:
  // renaming it would break per-UUID links, sub-graph references, and
  // the subdomain registration. displayName is the only human-friendly
  // override; defaults to workflowType when absent.
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef(null);

  const tabFromUrl = searchParams.get('tab');
  // Chat/Lambda agents (zibby-copilot) have no Fargate executions, so the
  // Activity tab is meaningless — drop it for them. Normal Fargate workflows
  // keep the full tab strip (chatRuntime is false ⇒ visibleTabs === TABS).
  const chatRuntime = isChatRuntime(pipeline);
  // Tab visibility:
  //  - chat/Lambda agents (zibby-copilot) have no Fargate executions → drop Activity.
  //  - the Logs tab renders LIVE container logs (FargateWorkflowLogsTab). A cold
  //    (non-warm) Fargate agent is Sleeping with no running container, so its Logs
  //    tab is ALWAYS empty AND redundant with Activity (which carries the per-run
  //    logs). Hide it unless the agent is warm (a persistent runner worth tailing)
  //    or chat-runtime (Lambda logs, always available). The FargateWorkflowLogsTab
  //    component stays — it's exactly what a warm instance uses.
  const warmEnabled = !!pipeline?.warmEnabled;
  const visibleTabs = TABS.filter((t) => {
    if (t.id === 'activity' && chatRuntime) return false;
    if (t.id === 'logs' && !warmEnabled && !chatRuntime) return false;
    return true;
  });
  // When the URL carries an executionId (deep-link to a specific run),
  // force the Activity tab — that's where the inline run detail panel
  // renders. Tab querystring only matters when no executionId is set.
  // If a chat agent somehow lands on a now-hidden tab via the querystring,
  // fall back to Overview.
  const activeTab = executionId
    ? 'activity'
    : visibleTabs.some((t) => t.id === tabFromUrl) ? tabFromUrl : 'overview';
  // Clicking a tab when a run is open: navigate back to the plain
  // workflow URL with that tab active. Without this, clicking
  // Overview/Settings/Env did nothing — the executionId override
  // pinned the tab back to Activity.
  const setActiveTab = (id) => {
    if (executionId) {
      navigate(`/projects/${projectId}/agents/${workflowType}?tab=${id}`);
    } else {
      setSearchParams({ tab: id }, { replace: true });
    }
  };

  // Post-action refresh shim. React Query's refetch keeps the existing cached
  // data on screen while it re-reads in the background (it only flips
  // `isFetching`, never `isLoading`/the skeleton) — so it IS the old
  // `{ silent: true }` behavior. The `{ silent }` arg is accepted + ignored so
  // the many existing call sites + child `onChange`/`onSaved` props keep
  // working. A 404 surfaces as the query's `error` (handled in the render
  // branch below), not a toast — same user-facing state as before.
  const fetchPipeline = useCallback(() => refetch(), [refetch]);

  // App-Store-style "Update" — only meaningful when the backend overlay
  // says updateAvailable=true. Calls deploy with force=true, which
  // re-copies the latest bundle into the same workflow row preserving
  // env vars, triggers, displayName, and warm config (server-side).
  const handleUpdate = useCallback(async () => {
    const slug = pipeline?.marketplace?.slug;
    if (!slug || updating) return;
    setUpdating(true);
    try {
      await Marketplace.deployWorkflow(slug, projectId, {
        workflowName: workflowType,
        force: true,
      });
      setUpdateModalOpen(false);
      setToast?.({
        show: true,
        message: `Updated to v${pipeline.marketplace.currentVersion}`,
        color: 'success',
      });
      // Re-fetch so the pill clears + Last deployed timestamp ticks.
      await fetchPipeline();
    } catch (err) {
      console.error('Update failed:', err);
      setToast?.({
        show: true,
        message: `Update failed: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setUpdating(false);
    }
  }, [pipeline, projectId, workflowType, updating, fetchPipeline, setToast]);

  // No mount-time fetch effect — useAgent issues the initial GET (and re-reads
  // on workflowType change) on its own.

  // Header "Starting" → "Live": when this pipeline is warm-enabled, ask the
  // warm-pool status endpoint whether a runner is actually RUNNING yet. While
  // it's still provisioning, keep polling so the header flips on its own once
  // the runner is up — without a manual refresh. Best-effort: any error clears
  // the flag so the header never gets stuck on "Starting".
  useEffect(() => {
    if (!pipeline?.warmEnabled) { setWarmProvisioning(false); return undefined; }
    let cancelled = false;
    let timer = null;
    const check = async () => {
      try {
        const data = await Addons.getWarmPoolStatus({ projectId, workflowType });
        if (cancelled) return;
        const provisioning = data?.enabled === true && data?.provisioning === true;
        setWarmProvisioning(provisioning);
        if (provisioning) timer = setTimeout(check, 5000);
      } catch {
        if (!cancelled) setWarmProvisioning(false);
      }
    };
    check();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [pipeline?.warmEnabled, projectId, workflowType]);

  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text).then(() => {
      setToast?.({ show: true, message: 'Copied to clipboard', color: 'success' });
    });
  };

  const handleStartRename = () => {
    setNameDraft(pipeline?.displayName || workflowType || '');
    setEditingName(true);
    // Defer focus to after paint so the input is actually mounted.
    setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 0);
  };

  const handleCancelRename = () => {
    setEditingName(false);
    setNameDraft('');
  };

  const handleSaveName = async () => {
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot rename: agent has no UUID yet', color: 'danger' });
      setEditingName(false);
      return;
    }
    const trimmed = nameDraft.trim();
    if (trimmed.length > 80) {
      setToast?.({ show: true, message: 'Display name must be 80 characters or fewer', color: 'danger' });
      return;
    }
    setSavingName(true);
    try {
      // Empty string clears displayName (server falls back to
      // workflowType when absent). Non-empty values get trimmed before
      // submission so leading/trailing whitespace can't be persisted
      // (server enforces this too — defense in depth).
      const body = { displayName: trimmed === '' ? null : trimmed };
      await api.patch(`/workflows/${pipeline.uuid}`, body);
      // Patch the cached detail row in place so the title updates with no
      // skeleton flash (mirrors PipelinesPage's setQueryData rename handling).
      patchPipeline((prev) => prev ? { ...prev, displayName: trimmed === '' ? undefined : trimmed } : prev);
      // Refresh the account-wide sidebar so the pinned label updates instantly.
      queryClient.invalidateQueries({ queryKey: ['workspace', 'sidebar'] });
      setEditingName(false);
      setToast?.({ show: true, message: 'Agent name updated', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Rename failed: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingName(false);
    }
  };

  // Confirmation is handled by the styled DangerZone modal — this just
  // performs the delete (called as the modal's onConfirm).
  const handleDelete = async () => {
    if (!pipeline?.uuid) {
      setToast?.({ show: true, message: 'Cannot delete: agent has no UUID yet (deploy it first)', color: 'danger' });
      return;
    }
    try {
      // Backend wires DELETE on `/workflows/{uuid}`; the handler looks
      // up the workflow via UUID GSI and verifies project membership
      // server-side, so projectId is implied. The mutation's onSuccess
      // invalidates the project's agent LIST and removes THIS agent's cached
      // detail (no ghost row / no ghost detail served from cache on back-nav).
      await deleteAgentMutation.mutateAsync({ uuid: pipeline.uuid, workflowType });
      setToast?.({ show: true, message: `Agent "${workflowType}" deleted`, color: 'success' });
      navigate(`/projects/${projectId}/pipelines`);
    } catch (err) {
      setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
    }
  };

  // ── render helpers ─────────────────────────────────────────────────

  // Show the skeleton whenever a request is in flight with nothing to render
  // yet — NOT just on `isLoading`. react-query's `isLoading` is `isPending &&
  // isFetching`, so it's false in edge states (a cached error, `enabled`
  // briefly false, the mount→fetch gap) where `pipeline` is still null; keying
  // only on it let the NotFoundPage branch below flash a 404 before data
  // arrived. Gating on "a fetch is in flight AND we have no row" closes that
  // gap. Background refetches that already have `pipeline` keep showing content
  // (they don't hit this), so the 404 only ever renders once the fetch has
  // SETTLED with no row — i.e. "show 404 after the request comes back".
  // Also skeleton when the (persisted) cache is a `notInstalled` SHELL that's
  // currently being revalidated: a stale "not deployed here yet" payload paints
  // from disk instantly, and rendering the 404 before the in-flight refetch
  // settles flashed the wrong page for ~2s right after a deploy. Wait for the
  // refetch — it either confirms notInstalled (then the 404 below is correct)
  // or returns the freshly-deployed agent.
  const revalidatingNotInstalled = pipeline?.notInstalled === true && pipelineFetching;
  if (revalidatingNotInstalled || (!pipeline && (pipelineLoading || pipelineFetching || !pipelineError))) {
    return (
      <div className="pipeline-detail-page" aria-busy="true" aria-label="Loading agent">
        <div className="pipeline-detail-skel-header">
          <div className="pipeline-detail-skel pipeline-detail-skel--title" />
          <div className="pipeline-detail-skel pipeline-detail-skel--meta" />
        </div>
        <div className="pipeline-detail-skel-tabs">
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
          <div className="pipeline-detail-skel pipeline-detail-skel--tab" />
        </div>
        <div className="pipeline-detail-skel-body">
          <div className="pipeline-detail-skel pipeline-detail-skel--row" />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '85%' }} />
          <div className="pipeline-detail-skel pipeline-detail-skel--row" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  // Two empty states, both routed through the shared NotFoundPage:
  //   1. pipeline === null         → workflow row not in DB AND no
  //                                   marketplace slug match → genuine 404
  //                                   (the useAgent query 404s ⇒ data stays
  //                                   null AND `pipelineError` is set; any
  //                                   other load failure lands here too, since
  //                                   there's no row to render either way)
  //   2. pipeline.notInstalled     → marketplace template exists with
  //                                   this slug but it's not deployed
  //                                   to this project (backend returns
  //                                   a schema-only preview payload so
  //                                   trigger modals work; the rest of
  //                                   the detail page would render with
  //                                   undefined UUID / version / dates
  //                                   which is the "fake page" trap)
  if (pipelineError || !pipeline || pipeline.notInstalled === true) {
    const isMarketplace = pipeline?.notInstalled === true;
    const slug = pipeline?.marketplaceSlug || workflowType;
    return (
      <NotFoundPage
        code="404"
        title={isMarketplace
          ? `'${workflowType}' isn't deployed here yet`
          : 'Agent not found'}
        subtitle={isMarketplace
          ? "This template lives in the marketplace but hasn't been deployed to this project. Install it to start triggering runs."
          : "This agent doesn't exist, or it may have been deleted. Check the URL or browse your project's agents."}
        primaryAction={isMarketplace
          ? { label: 'Install from marketplace', to: `/projects/${projectId}/marketplace?slug=${encodeURIComponent(slug)}`, icon: cubeOutline }
          : { label: 'Back to agents', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
        }
        secondaryAction={isMarketplace
          ? { label: 'Back to agents', to: `/projects/${projectId}/pipelines`, icon: arrowBackOutline }
          : { label: 'Browse marketplace', to: `/projects/${projectId}/marketplace`, icon: cubeOutline }
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{(pipeline?.displayName || workflowType)} · Agents · Zibby</title>
      </Helmet>
      <div className="pipeline-detail-page">
        <div className="pipeline-detail-header">
          <div className="pipeline-detail-titlerow">
            {editingName ? (
              <div className="pipeline-detail-title-edit">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="pipeline-detail-title-input"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSaveName();
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      handleCancelRename();
                    }
                  }}
                  maxLength={80}
                  placeholder={workflowType}
                  disabled={savingName}
                  aria-label="Agent display name"
                />
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-save"
                  onClick={handleSaveName}
                  disabled={savingName}
                  aria-label="Save"
                  title="Save (Enter)"
                >
                  <IonIcon icon={checkmarkOutline} />
                </button>
                <button
                  type="button"
                  className="pipeline-detail-title-btn pipeline-detail-title-cancel"
                  onClick={handleCancelRename}
                  disabled={savingName}
                  aria-label="Cancel"
                  title="Cancel (Esc)"
                >
                  <IonIcon icon={closeOutline} />
                </button>
              </div>
            ) : (
              <>
                <WorkflowDetailIcon
                  marketplaceSlug={pipeline.marketplaceSlug}
                  iconUrl={pipeline.iconUrl}
                  label={pipeline.displayName || workflowType}
                />
                <h1 className="pipeline-detail-title" title={workflowType !== (pipeline.displayName || workflowType) ? `Slug: ${workflowType}` : undefined}>
                  {pipeline.displayName || workflowType}
                </h1>
                <button
                  type="button"
                  className="pipeline-detail-title-edit-btn"
                  onClick={handleStartRename}
                  aria-label="Rename agent"
                  title="Rename"
                >
                  <IonIcon icon={createOutline} />
                </button>
                {/* Status / version / last-deployed sit next to the title
                    on the left side. Right side of the header is reserved
                    for actions (Update pill, Trigger button). */}
                <span className="pipeline-detail-meta-sep">·</span>
                <PipelineStatus
                  bundleStatus={pipeline.bundleStatus}
                  warmEnabled={pipeline.warmEnabled}
                  warmProvisioning={warmProvisioning}
                  activeRuns={pipeline.activeRuns}
                  chatRuntime={chatRuntime}
                  variant="header"
                />
                <span className="pipeline-detail-meta-sep">·</span>
                {/* Show the REAL template version (marketplace installedVersion)
                    — the internal deploy-revision counter ("v6") confused
                    everyone. Non-marketplace agents keep the revision. The
                    revision stays visible in the meta card's VERSION field. */}
                <span
                  className="pipeline-detail-version"
                  title={pipeline.marketplace?.installedVersion
                    ? `Template version ${pipeline.marketplace.installedVersion} · deploy revision ${pipeline.version || 0}`
                    : 'Deploy revision'}
                >
                  {pipeline.marketplace?.installedVersion
                    ? `v${pipeline.marketplace.installedVersion}`
                    : `v${pipeline.version || 0}`}
                </span>
                <span className="pipeline-detail-meta-sep">·</span>
                <span className="pipeline-detail-time">
                  Last deployed {pipeline.updatedAt ? timeAgo(pipeline.updatedAt) : '—'}
                </span>
              </>
            )}
          </div>
          <div className="pipeline-detail-actions">
            {/* Marketplace "Update available" pill — App-Store style.
                Only renders when the backend overlay flags this workflow
                as out-of-date relative to the marketplace listing. Sits
                LEFT of Trigger so the primary action is the rightmost
                element (Fitts' law: corner is easiest to hit). */}
            {pipeline.marketplace?.updateAvailable && (
              <button
                type="button"
                className="pipeline-detail-update-pill"
                onClick={() => setUpdateModalOpen(true)}
                title={`Update available — ${pipeline.marketplace.installedVersion || '?'} → ${pipeline.marketplace.currentVersion || '?'}`}
              >
                <IonIcon icon={sparklesOutline} aria-hidden="true" />
                Update available
                {pipeline.marketplace.currentVersion && (
                  <span className="pipeline-detail-update-pill-ver">
                    v{pipeline.marketplace.currentVersion}
                  </span>
                )}
              </button>
            )}
            <PillButton
              onClick={() => {
                // Never a DISABLED button here: a dead control tells you
                // nothing about why. The check answers instead — it scrolls to
                // the graph, rings every node that can't run, and names them
                // in a toast, and each ring is the control that fixes it.
                if (graphRef.current?.flagUnreadyModels()) return;
                setTriggerModalOpen(true);
              }}
              disabled={(pipeline.bundleStatus && pipeline.bundleStatus !== 'ready') || triggerBlocked}
              title={triggerBlocked
                ? `Connect ${triggerMissingLabels.join(' and ')} first — required integrations must be connected before this agent can run.`
                : `Trigger ${pipeline.displayName || workflowType}`}
            >
              Trigger
            </PillButton>
          </div>
        </div>

        <TriggerModal
          isOpen={triggerModalOpen}
          onClose={() => setTriggerModalOpen(false)}
          pipeline={pipeline}
          projectId={projectId}
          workflowType={workflowType}
          onTriggered={handleTriggered}
        />

        <AgentConfigModal
          isOpen={agentModalOpen}
          onClose={() => setAgentModalOpen(false)}
          variant="pipeline"
          agentOptions={agentOptions}
          seedValue={findAgentOptionValue(
            { aiAgent: pipeline?.aiAgent, aiModel: pipeline?.aiModel },
            agentOptions,
          )}
          seedKey={pipeline?.aiApiKey || ''}
          seedOAuth={pipeline?.aiOAuthToken || ''}
          onSave={async (body) => {
            await api.patch(`/workflows/${pipeline.uuid}`, body);
            // Keep the RUNTIME credential in sync with the structured one the
            // modal just wrote. resolveAgentAuthEnvVars reads envSecret.
            // CLAUDE_CODE_OAUTH_TOKEN (which WINS over the row's aiOAuthToken)
            // at run time, so a modal edit that only touched aiOAuthToken would
            // otherwise be shadowed by a stale env value. `body` carries the
            // raw credential ONLY when the user entered a NEW one (a masked/
            // blank "leave to keep" field sends nothing) — so mirror exactly
            // that fresh value. Claude only (the vendor with the OAuth/env
            // split): oat01 → CLAUDE_CODE_OAUTH_TOKEN, other Claude key →
            // ANTHROPIC_API_KEY. A sync failure must not lose the primary save.
            try {
              if (typeof body.aiOAuthToken === 'string' && /^sk-ant-oat01-/.test(body.aiOAuthToken)) {
                await api.patch(`/workflows/${pipeline.uuid}/env/CLAUDE_CODE_OAUTH_TOKEN`, { value: body.aiOAuthToken });
              } else if (typeof body.aiApiKey === 'string' && /^sk-ant-/.test(body.aiApiKey)) {
                await api.patch(`/workflows/${pipeline.uuid}/env/ANTHROPIC_API_KEY`, { value: body.aiApiKey });
              }
            } catch (err) {
              setToast?.({ show: true, message: `Saved the credential, but syncing it to the runtime env failed: ${err.message}`, color: 'danger' });
            }
          }}
          onSaved={() => fetchPipeline({ silent: true })}
          setToast={setToast}
        />

        {updateModalOpen && pipeline.marketplace && (
          <UpdateAgentModal
            title={pipeline.displayName || pipeline.workflowType}
            marketplace={pipeline.marketplace}
            updating={updating}
            onClose={() => setUpdateModalOpen(false)}
            onUpdate={handleUpdate}
          />
        )}

        {/* Tab strip */}
        <div className="pipeline-detail-tabs" role="tablist">
          {visibleTabs.map((tab) => (
            <button
              key={tab.id}
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`pipeline-detail-tab ${activeTab === tab.id ? 'pipeline-detail-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab body */}
        <div className="pipeline-detail-body">
          {activeTab === 'overview' && (
            <>
              {pipeline?.graph
                && Array.isArray(pipeline.graph.nodes)
                && pipeline.graph.nodes.length > 0 && (
                <div className="pipeline-tab pipeline-graph pipeline-graph--overview">
                  <PipelineGraphView
                    ref={graphRef}
                    graph={pipeline.graph}
                    workflowUuid={pipeline.uuid}
                    webhookEnabled={!!pipeline?.triggers?.api}
                    onSaved={() => fetchPipeline({ silent: true })}
                    setToast={setToast}
                    // Project scope so a sub-workflow node's modal can embed the
                    // CHILD agent's graph (the child is deployed in this project).
                    projectId={projectId}
                    // RAW per-node overrides. `pipeline.graph` already has them
                    // merged into nodeConfigs, so this is the only way the model
                    // disc can tell the operator's own pick from a vendor the
                    // TEMPLATE pinned.
                    nodeConfigOverrides={pipeline.nodeConfigOverrides || null}
                    // The template's DECLARED model blocks. A node whose code
                    // picks its own model picks a per-vendor TIER, and the
                    // deployed graph carries only that function's own text —
                    // not the module holding the map — so the alias is
                    // unknowable from the graph. The template publishes it as
                    // data (`fixedModelByVendor`), joined to the node by role.
                    modelBlocks={pipeline.marketplace?.models || null}
                    // The vendor the executor stamps as AGENT_TYPE for the
                    // process this graph runs in — what a tier-fixed node's
                    // resolveRunVendor() actually sees: this row's own
                    // aiAgent, else (for a dispatched sub-agent) the OWNING
                    // parent's resolved vendor. Without it a locked disc on a
                    // bare child page read as an anonymous "Fixed" while the
                    // in-process run genuinely used the parent's vendor.
                    agentVendor={effectiveAgentVendor}
                    // THE WHOLE ROW. It carries `members` and the `capabilities`
                    // ROLLUP — every `requires` member declared by this agent OR
                    // by anything beneath it — resolved once server-side
                    // (services/workflow-composition.resolveMemberTree), so this
                    // app never re-implements the join the self-host dashboard
                    // also renders.
                    workflow={pipeline}
                  />
                </div>
              )}
              {/* `connect · kind:'oauth'` — the sign-in the agent's template
                  DECLARES (entryPoints.connect). Derived server-side onto
                  resources.endpoints; the card self-gates on the declaration,
                  so every agent without one renders exactly as before. A
                  landed/dropped grant changes the row's endpoint list, so the
                  card asks the page to refetch itself. */}
              <ConnectOAuthCard
                pipeline={pipeline}
                setToast={setToast}
                onChange={() => fetchPipeline({ silent: true })}
              />
              <OverviewTab
                pipeline={pipeline}
                onCopy={copyToClipboard}
                setToast={setToast}
                onPipelineUpdate={patchPipeline}
                projectId={projectId}
                workflowType={workflowType}
                chatRuntime={chatRuntime}
              />
            </>
          )}
          {activeTab === 'activity' && (
            <ActivityTab
              projectId={projectId}
              workflowType={workflowType}
              executionId={executionId}
              pipeline={pipeline}
              setToast={setToast}
              navigate={navigate}
            />
          )}
          {activeTab === 'logs' && (
            <WorkflowLogsTab
              projectId={projectId}
              workflowType={workflowType}
              status={pipeline.bundleStatus}
              chatRuntime={chatRuntime}
            />
          )}
          {activeTab === 'settings' && (
            <SettingsTab
              pipeline={pipeline}
              onDelete={handleDelete}
              setToast={setToast}
              projectId={projectId}
              workflowType={workflowType}
              onChange={() => fetchPipeline({ silent: true })}
              chatRuntime={chatRuntime}
            />
          )}
          {activeTab === 'env' && (
            <EnvTab uuid={pipeline.uuid} setToast={setToast} />
          )}
          {activeTab === 'artifacts' && (
            <ArtifactsTab projectId={projectId} workflowType={workflowType} />
          )}
        </div>
      </div>
    </>
  );
};

// ─── Overview ────────────────────────────────────────────────────────

// AuthCard is now the shared atomic in components/shared/AuthCard — imported
// at the top of this file and reused by the managed-app access-control panel.

// Loose IPv4 / IPv6 / CIDR check — gates the IP-allowlist Save button so an
// empty or obviously-malformed line can't be submitted. The backend remains
// the authoritative validator; this is just UI affordance.
const isValidIpOrCidr = (value) => {
  const s = (value || '').trim();
  if (!s) return false;
  const parts = s.split('/');
  if (parts.length > 2) return false;
  const [addr, prefix] = parts;
  if (prefix !== undefined) {
    if (!/^\d{1,3}$/.test(prefix)) return false;
    const max = addr.includes(':') ? 128 : 32;
    if (Number(prefix) > max) return false;
  }
  if (addr.includes(':')) {
    // IPv6 — minimal shape: hex groups separated by colons, at least one "::"
    // or two colons. Not a full RFC parse, just enough to reject junk.
    return /^[0-9a-fA-F:]+$/.test(addr) && (addr.match(/:/g) || []).length >= 2;
  }
  const octets = addr.split('.');
  return octets.length === 4 && octets.every((o) => /^\d{1,3}$/.test(o) && Number(o) <= 255);
};

const OverviewTab = ({ pipeline, onCopy, setToast, onPipelineUpdate, projectId, workflowType, chatRuntime = false }) => {
  // Promote-to-account-default confirm (the shared ConfirmModal).
  const [confirmDefaultOpen, setConfirmDefaultOpen] = useState(false);
  const triggerCmd = `zibby workflow trigger ${pipeline.rn || pipeline.uuid}`;
  // Schedule trigger commands — set/get/clear, identical to the CLI surface.
  // The set cron is just an illustrative weekday-9am default; user copies and
  // edits to taste. Cron format is standard Unix 5-field; backend converts to
  // EventBridge form so users never need to know AWS's 6-field flavor.
  const scheduleSetCmd   = `zibby workflow schedule ${pipeline.rn || pipeline.uuid} set "0 9 * * 1-5"`;
  const scheduleShowCmd  = `zibby workflow schedule ${pipeline.rn || pipeline.uuid}`;
  const scheduleClearCmd = `zibby workflow schedule ${pipeline.rn || pipeline.uuid} clear`;

  // Customer-facing webhook URL is the per-workflow subdomain
  // (`<slug>-<hash>.workflows.zibby.app/trigger`), NOT the main API path.
  // The subdomain is computed in workflow-service.computeSubdomain and
  // stored on the row at deploy time. Domain suffix depends on the stage
  // the CURRENT frontend is talking to — derive it from the API base
  // host so dev / staging / prod all point at the right tier:
  //   api-prod.zibby.app           → workflows.zibby.app
  //   api-staging.zibby.app        → workflows-staging.zibby.app
  //   api-dev.zibby.app | local    → workflows-dev.zibby.app
  // If the workflow doesn't have a subdomain on its row (shouldn't
  // happen post-deploy, but legacy / partial-save rows might), the
  // section is hidden — no URL is better than a wrong URL.
  const apiBase = process.env.REACT_APP_API_BASE_URL || 'https://api-prod.zibby.app';
  const apiHost = (() => { try { return new URL(apiBase).hostname; } catch { return ''; } })();
  // Local dev has no `*.workflows-dev.zibby.app` subdomain to hit, so the
  // public per-workflow URL is meaningless on localhost. When talking to a
  // local API, surface the direct project-scoped trigger route the local
  // Express server actually serves (server.js:
  // `POST /projects/:projectId/workflows/:type/trigger`) so the shown URL is
  // copy-pasteable and works. dev/staging/prod keep the public subdomain URL.
  const isLocal = apiHost === 'localhost' || apiHost === '127.0.0.1';
  const workflowsDomain = apiHost.startsWith('api-staging.')
    ? 'workflows-staging.zibby.app'
    : apiHost.startsWith('api-prod.')
      ? 'workflows.zibby.app'
      : 'workflows-dev.zibby.app';
  const subdomain  = pipeline.subdomain || null;
  const webhookUrl = isLocal
    ? `${apiBase.replace(/\/$/, '')}/projects/${projectId}/workflows/${workflowType}/trigger`
    : subdomain ? `https://${subdomain}.${workflowsDomain}/trigger` : null;
  const curlExample = webhookUrl
    ? `curl -X POST '${webhookUrl}' \\\n` +
      `  -H 'Authorization: Bearer YOUR_API_TOKEN' \\\n` +
      `  -H 'Content-Type: application/json' \\\n` +
      `  -d '{}'`
    : null;

  // Webhook (HTTP POST) trigger is OPT-IN: live only when
  // `triggers.api === true`. Missing/false ⇒ disabled, and a call to the
  // endpoint 404s server-side. The toggle PATCHes the workflow's triggers
  // and reflects the result locally so the section flips immediately.
  const webhookApiEnabled = pipeline.triggers?.api === true;
  const [togglingWebhook, setTogglingWebhook] = useState(false);
  // Optimistic override so the toggle flips INSTANTLY (no spinner → no button
  // height jump that pushes the section down). null ⇒ follow the prop; once the
  // patched pipeline lands we reset to null and the prop drives it again.
  const [optimisticWebhook, setOptimisticWebhook] = useState(null);
  const webhookEnabled = optimisticWebhook === null ? webhookApiEnabled : optimisticWebhook;

  const handleToggleWebhook = async () => {
    if (togglingWebhook) return;
    const nextValue = !webhookEnabled;
    setOptimisticWebhook(nextValue);   // flip UI now — optimistic, no spinner
    setTogglingWebhook(true);
    try {
      // Backend merges this into the existing triggers map and returns the
      // updated workflow row; lift it straight into page state so every
      // overview field stays in sync (not just `triggers`).
      // PATCH by UUID (`/workflows/{uuid}`) — that's the ONLY registered
      // PATCH route (prod CDK + local server.js); the project-scoped
      // `/projects/{id}/workflows/{type}` resource only exposes GET/PUT, so a
      // PATCH there 404s (this is why Enable silently failed locally).
      const updated = await api.patch(
        `/workflows/${pipeline.uuid}`,
        { triggers: { api: nextValue } },
      );
      // Merge ONLY `triggers` — the PATCH response is the raw DDB row and lacks
      // the GET-computed fields (subdomain → webhookUrl, deployedVersion,
      // updateAvailable). Replacing the whole pipeline with it would blank the
      // webhook section, drop the version to v0, and hide the update badge.
      onPipelineUpdate?.((prev) => {
        if (!prev) return prev;
        const nextTriggers = (updated && typeof updated === 'object' && updated.triggers)
          ? updated.triggers
          : { ...(prev.triggers || {}), api: nextValue };
        return { ...prev, triggers: nextTriggers };
      });
      setOptimisticWebhook(null);   // prop now reflects it — drop the override
    } catch (err) {
      setOptimisticWebhook(null);   // roll back to the prop value
      setToast?.({
        show: true,
        message: `Could not ${nextValue ? 'enable' : 'disable'} webhook: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setTogglingWebhook(false);
    }
  };

  // gitlab/github review agents ONLY get an editable "Mention" cell — the
  // @name that triggers a full review when it appears in an MR/PR comment
  // (per-agent reviewMentionToken, saved via PATCH). No other agent type has a
  // review-mention concept, so the field is hidden for them. Match on the
  // route slug (`workflowType`), falling back to the row's own fields.
  // Match on ANY identifier — the stable template `marketplaceSlug` first, since a
  // SECOND/renamed deploy has a deduped workflowType like `gitlab-code-review-2`
  // that fails an exact slug match (which hid the Overview MentionStat on it while
  // the self-gated TriggersStat still showed).
  const isReviewAgent = [pipeline.marketplaceSlug, pipeline.workflowType, workflowType]
    .some((s) => ['gitlab-code-review', 'github-code-review'].includes(s));

  return (
    <div className="pipeline-tab pipeline-overview">
      <div className="pipeline-stat-grid">
        {/* `Type` removed — it's just the page title duplicated. The
            workflowType slug is shown in the breadcrumb / heading. */}
        {/* The agent's RESOURCE NAME (rn — north-star #10): the qualified,
            pasteable identifier. The bare uuid is never displayed (user call
            2026-08-02); until the backend emits rn the cell shows a dash. */}
        <Stat label="ID" value={pipeline.rn || '—'} copyable onCopy={onCopy} />
        {isReviewAgent && (
          <MentionStat
            pipeline={pipeline}
            setToast={setToast}
            onPipelineUpdate={onPipelineUpdate}
          />
        )}
        {/* Review triggers — which webhook events fire a review. Self-gates on a
            non-empty `reviewTriggerMenu` (present ONLY for code-review agents),
            so it renders nothing for every other agent type. Sits next to the
            Mention cell, same visual area. */}
        <TriggersStat
          pipeline={pipeline}
          setToast={setToast}
          onPipelineUpdate={onPipelineUpdate}
        />
        <Stat label="Version"          value={`v${pipeline.version || 0}`} />
        {pipeline.marketplace?.installedVersion && (
          <Stat
            label="Template"
            value={`${pipeline.marketplace.slug}@v${pipeline.marketplace.installedVersion}`}
          />
        )}
        {/* `Model` removed — the model is chosen PER NODE on the graph above
            (each model-running node carries its own disc), so one agent-wide
            value cannot describe a graph whose nodes deliberately run
            different vendors: a cheap triage tier next to an expensive
            reviewer. Showing one of them here named the wrong model for every
            other node. */}
        <Stat label="Last update"      value={pipeline.updatedAt ? new Date(pipeline.updatedAt).toLocaleString() : '—'} />
      </div>

      {/* Copilot scope — ACCOUNT DEFAULT switch. The account's default Copilot
          answers account-level chat and operates every project; every other
          Copilot is confined to its own project (the flag drives the per-turn
          token's projectScope server-side, PATCH accountDefault). */}
      {chatRuntime && (
        <div className="pipeline-section">
          <h3 className="pipeline-section-title">
            Copilot scope
            <HelpIcon hint="One Copilot per project. Exactly one of them is the account default: it answers account-level Slack/Lark and can operate every project; the rest are confined to their own project." />
          </h3>
          {pipeline.accountDefault ? (
            <div className="pipeline-meta-row">
              <span className="pill">Account default</span>
              <span style={{ fontSize: 12, opacity: 0.75, marginLeft: 8 }}>
                This Copilot answers account-level chat and can operate every project. Promote another Copilot to move the flag.
              </span>
            </div>
          ) : (
            <div className="pipeline-meta-row" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 12, opacity: 0.75 }}>
                Project Copilot — confined to this project.
              </span>
              <button type="button" className="btn-secondary" onClick={() => setConfirmDefaultOpen(true)}>
                Set default
              </button>
            </div>
          )}
          {/* Promote confirm — the shared ConfirmModal, not a native
              window.confirm (browser chrome says "studio.zibby.dev says…"). */}
          {confirmDefaultOpen && (
            <ConfirmModal
              open
              onClose={() => setConfirmDefaultOpen(false)}
              title="Make this the account-default Copilot?"
              ariaLabel="Make account default"
              primaryLabel="Set as default"
              onPrimary={async () => {
                try {
                  await api.patch(`/workflows/${pipeline.uuid}`, { accountDefault: true });
                  onPipelineUpdate((prev) => (prev ? { ...prev, accountDefault: true } : prev));
                  setToast({ kind: 'ok', title: 'Account default moved', body: 'This Copilot now answers account-level chat.' });
                } catch (e) {
                  setToast({ kind: 'danger', title: 'Switch failed', body: e.message });
                }
              }}
              secondaryLabel="Cancel"
            >
              <p style={{ fontSize: 13, lineHeight: 1.55, textAlign: 'left' }}>
                This Copilot will answer account-level chat and operate every project.
                The current default becomes confined to its own project.
              </p>
            </ConfirmModal>
          )}
        </div>
      )}

      {/* Fargate-only trigger surfaces (CLI / schedule / webhook). A chat
          agent is driven by inbound Slack/Lark messages through its Lambda
          runtime, NOT by `zibby workflow trigger` / cron / HTTP POST — so
          these are meaningless for it and hidden. Normal Fargate workflows
          (chatRuntime === false) render the full set unchanged. */}
      {!chatRuntime && (
      <>
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via CLI
          <HelpIcon hint="Trigger a run from your terminal. The resource name is baked into the command — copy and paste." />
        </h3>
        <CmdRow command={triggerCmd} onCopy={onCopy} />
      </div>

      {/* Live status of the currently-attached schedule. Sits between
          "Trigger via CLI" (manual one-off) and "Trigger via schedule"
          (the helper commands to set/change/clear) so the user reads:
          "Manual trigger → What's running automatically → How to change it". */}
      {pipeline.uuid && (
        <ScheduleStatusPanel
          workflowUuid={pipeline.uuid}
          inputSchema={pipeline?.graph?.inputSchema}
          effectiveMaxRuntimeMinutes={
            Number.isInteger(pipeline?.effectiveMaxRuntimeMinutes)
              ? pipeline.effectiveMaxRuntimeMinutes
              : (Number.isInteger(pipeline?.defaultMaxRuntimeMinutes) ? pipeline.defaultMaxRuntimeMinutes : 25)
          }
        />
      )}

      {/* TRIGGER VIA SCHEDULE — cron-based recurring runs.
          Identical CLI flavor to "Trigger via CLI" above so users get the
          same copy-paste UX. The example uses `0 9 * * 1-5` (9am Mon-Fri UTC);
          users edit to taste. Backend (workflows-schedule.js) converts the
          Unix 5-field cron to EventBridge's 6-field form internally. */}
      <div className="pipeline-section">
        <h3 className="pipeline-section-title">
          Trigger via schedule
          <HelpIcon hint="Run on a cron schedule. Standard Unix 5-field: minute hour day-of-month month day-of-week. Add --tz America/Los_Angeles for a timezone, or -p key=value for fixed input params." />
        </h3>
        <CmdRow command={scheduleSetCmd}   onCopy={onCopy} />
        <CmdRow command={scheduleShowCmd}  onCopy={onCopy} />
        <CmdRow command={scheduleClearCmd} onCopy={onCopy} />
      </div>

      {/* TRIGGER VIA WEBHOOK — HTTP POST variant. OPT-IN: live only when
          `triggers.api === true`; missing/false ⇒ the endpoint 404s. The
          section is always shown for deployed workflows (those with a
          `subdomain`) so users can enable it from here. Hidden only when
          there's no subdomain yet (not deployed / partial save) — no URL is
          better than a wrong URL. */}
      {webhookUrl && (
        <div className="pipeline-section pipeline-section--separated">
          {/* Header row: title + state badge on the left, the enable/disable
              action button right-aligned (mirrors the page-header Trigger
              button). The button used to sit at the bottom under the URL. */}
          <div className="pipeline-webhook-header">
            <h3 className="pipeline-section-title pipeline-webhook-header-title">
              Trigger via webhook
              <HelpIcon hint="POST to this endpoint with your API token to trigger a run from CI / CD or any other system. Disabled by default — enable it here. See docs.zibby.app/cloud/triggering for full reference." />
            </h3>
            {/* Same IonToggle the auth cards use — the switch position IS the
                state, so no separate Enabled/Disabled badge. Optimistic flip. */}
            <IonToggle
              className="pipeline-auth-switch"
              checked={webhookEnabled}
              disabled={togglingWebhook}
              onIonChange={(e) => {
                if (e.detail.checked !== webhookEnabled) handleToggleWebhook();
              }}
              aria-label={webhookEnabled ? 'Disable webhook' : 'Enable webhook'}
            />
          </div>

          {webhookEnabled ? (
            <>
              <p className="pipeline-section-note">
                POST to this endpoint with your API token to trigger a run.
              </p>
              <CmdRow command={webhookUrl}  onCopy={onCopy} />
              <CmdRow command={curlExample} onCopy={onCopy} />
            </>
          ) : (
            <p className="pipeline-section-note pipeline-webhook-disabled-note">
              Webhook disabled — calls to this endpoint return 404. Enable it
              to accept HTTP triggers.
            </p>
          )}

          {/* Webhook AUTHENTICATION — only meaningful while the webhook is
              live. Three independently-toggleable methods (basic / IP / token),
              each PATCHing triggers.webhook.<method> and lifting the updated
              workflow row back into page state. */}
          {webhookEnabled && (
            <WebhookAuthConfig
              pipeline={pipeline}
              onPipelineUpdate={onPipelineUpdate}
              onCopy={onCopy}
              setToast={setToast}
            />
          )}
        </div>
      )}
      </>
      )}
    </div>
  );
};

/**
 * WebhookAuthConfig — three independent webhook auth methods that layer on
 * top of the always-required Bearer API token. Each method owns its own
 * draft state + save spinner and PATCHes only its own sub-object so the
 * backend merge stays narrow:
 *
 *   triggers.webhook.basic = { enabled, username, password? }  (password
 *       plaintext ONLY when the user typed a new one; omitted otherwise so
 *       an enable/disable toggle or username edit never clobbers the hash)
 *   triggers.webhook.ip    = { enabled, allowList: string[] }
 *   triggers.webhook.token = { enabled, generate?: true }
 *
 * GET never returns secrets — basic.password / token raw are write-only.
 * The token PATCH response carries `webhookToken` ONCE; we surface it in a
 * copyable field with a "shown once" note, then fall back to the masked
 * `token.tokenMasked` on every subsequent render.
 */
// Sentinel that renders as dots in the password field when a password is
// already on file (the server never sends the real one back). Cleared on focus;
// treated as "keep existing" on save, never sent.
const PW_SENTINEL = '••••••••';

const WebhookAuthConfig = ({ pipeline, onPipelineUpdate, onCopy, setToast }) => {
  const webhookCfg = pipeline.triggers?.webhook || {};
  const basicCfg = webhookCfg.basic || {};
  const ipCfg = webhookCfg.ip || {};
  const tokenCfg = webhookCfg.token || {};

  // ── Basic auth ──────────────────────────────────────────────────────
  const [basicEnabled, setBasicEnabled] = useState(!!basicCfg.enabled);
  const [basicUsername, setBasicUsername] = useState(basicCfg.username || '');
  // The server never returns the password (hashed). When one is on file we
  // pre-fill this sentinel so the field RENDERS as filled (dots); it's cleared
  // on focus for clean editing and treated as "keep existing" on save.
  const [basicPassword, setBasicPassword] = useState(basicCfg.enabled ? PW_SENTINEL : '');
  const [savingBasic, setSavingBasic] = useState(false);
  // A password is "already set" if the server says basic is enabled — the
  // hash exists server-side even though GET never returns it.
  const passwordOnFile = !!basicCfg.enabled;

  // ── IP allowlist ────────────────────────────────────────────────────
  const [ipEnabled, setIpEnabled] = useState(!!ipCfg.enabled);
  const [ipText, setIpText] = useState((ipCfg.allowList || []).join('\n'));
  const [savingIp, setSavingIp] = useState(false);

  // ── Token ───────────────────────────────────────────────────────────
  const [tokenEnabled, setTokenEnabled] = useState(!!tokenCfg.enabled);
  const [savingToken, setSavingToken] = useState(false);
  // The plaintext token, shown EXACTLY once right after (re)generation.
  // Never persisted, never re-fetched — only the masked value survives a
  // reload (it comes off the workflow row via tokenCfg.tokenMasked).
  const [plaintextToken, setPlaintextToken] = useState(null);

  // Shared PATCH helper — sends only the webhook sub-object passed in,
  // lifts the updated workflow row into page state, and returns the raw
  // response so the token flow can read `webhookToken` off it.
  const patchWebhook = async (webhookPatch) => {
    // PATCH by UUID — same reason as handleToggleWebhook: the project-scoped
    // workflow resource has no PATCH method, only `/workflows/{uuid}` does.
    const updated = await api.patch(
      `/workflows/${pipeline.uuid}`,
      { triggers: { webhook: webhookPatch } },
    );
    // Merge ONLY `triggers` — the PATCH response is the raw DDB row without the
    // GET-computed fields (subdomain/webhookUrl, deployedVersion, updateAvailable).
    // Replacing the whole pipeline would blank the webhook section + reset the
    // version. See handleToggleWebhook for the same fix.
    if (updated && typeof updated === 'object' && updated.triggers) {
      onPipelineUpdate?.((prev) => prev ? { ...prev, triggers: updated.triggers } : prev);
    }
    return updated;
  };

  const handleSaveBasic = async () => {
    if (savingBasic) return;
    if (basicEnabled && !basicUsername.trim()) {
      setToast?.({ show: true, message: 'Username is required for basic auth', color: 'danger' });
      return;
    }
    // Require a password when enabling for the first time (none on file).
    if (basicEnabled && !passwordOnFile && !basicPassword) {
      setToast?.({ show: true, message: 'Set a password to enable basic auth', color: 'danger' });
      return;
    }
    setSavingBasic(true);
    try {
      const basic = { enabled: basicEnabled, username: basicUsername.trim() };
      // Only send a password when the user actually typed one — an untouched
      // sentinel (or blank) tells the backend to keep the existing hash.
      if (basicPassword && basicPassword !== PW_SENTINEL) basic.password = basicPassword;
      await patchWebhook({ basic });
      // Never keep plaintext around. If basic is still enabled a password is now
      // on file → show the field as filled again; otherwise clear it.
      setBasicPassword(basicEnabled ? PW_SENTINEL : '');
      setToast?.({ show: true, message: 'Basic auth saved', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Could not save basic auth: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingBasic(false);
    }
  };

  const handleSaveIp = async () => {
    if (savingIp) return;
    // Split on newlines/commas, trim, drop empties.
    const allowList = ipText
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (ipEnabled && allowList.length === 0) {
      setToast?.({ show: true, message: 'Add at least one IP or CIDR to enable the allowlist', color: 'danger' });
      return;
    }
    setSavingIp(true);
    try {
      await patchWebhook({ ip: { enabled: ipEnabled, allowList } });
      // Reflect the normalized list back into the textarea.
      setIpText(allowList.join('\n'));
      setToast?.({ show: true, message: 'IP allowlist saved', color: 'success' });
    } catch (err) {
      setToast?.({ show: true, message: `Could not save IP allowlist: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingIp(false);
    }
  };

  // Top-right Enable/Disable for the Basic card. Enabling just expands the
  // card so the user can type a username + password (committed via Save —
  // basic auth can't be enabled server-side without credentials). Disabling
  // PATCHes the enabled flag off immediately; the config stays on file so
  // re-enabling later doesn't force a re-entry.
  const handleToggleBasic = async (nextEnabled) => {
    if (nextEnabled) {
      setBasicEnabled(true);   // expand only — Save commits the credentials
      return;
    }
    // Optimistic collapse: hide the card body immediately so the Save button
    // never flashes a spinner mid-toggle (no shared-loading flicker). Roll the
    // card back open if the PATCH fails.
    setBasicEnabled(false);
    setBasicPassword('');
    try {
      await patchWebhook({ basic: { enabled: false } });
    } catch (err) {
      setBasicEnabled(true);
      setToast?.({ show: true, message: `Could not disable basic auth: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // Top-right Enable/Disable for the IP allowlist card. Same shape as Basic:
  // enabling expands (the allowlist is committed via Save — an empty list
  // can't be enabled), disabling PATCHes the flag off immediately.
  const handleToggleIp = async (nextEnabled) => {
    if (nextEnabled) {
      setIpEnabled(true);   // expand only — Save commits the allowlist
      return;
    }
    // Optimistic collapse — same rationale as Basic above.
    setIpEnabled(false);
    try {
      await patchWebhook({ ip: { enabled: false } });
    } catch (err) {
      setIpEnabled(true);
      setToast?.({ show: true, message: `Could not disable IP allowlist: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // Toggle token on/off WITHOUT regenerating. Used by the on/off switch.
  const handleToggleToken = async (nextEnabled) => {
    // Optimistic flip — update the card immediately, reconcile in the
    // background, roll back on failure. Keeps the toggle from flashing the
    // Generate/Regenerate spinner mid-flight.
    const prev = tokenEnabled;
    setTokenEnabled(nextEnabled);
    // Turning the method off invalidates any one-time token still on screen.
    if (!nextEnabled) setPlaintextToken(null);
    try {
      await patchWebhook({ token: { enabled: nextEnabled } });
    } catch (err) {
      setTokenEnabled(prev);
      setToast?.({ show: true, message: `Could not update token auth: ${err.message || 'unknown error'}`, color: 'danger' });
    }
  };

  // (Re)generate — PATCH with generate:true; the response carries the raw
  // `webhookToken` exactly once. Show it; afterwards only the mask remains.
  const handleGenerateToken = async () => {
    if (savingToken) return;
    setSavingToken(true);
    try {
      const updated = await patchWebhook({ token: { enabled: true, generate: true } });
      setTokenEnabled(true);
      const raw = updated?.webhookToken || null;
      setPlaintextToken(raw);
      setToast?.({
        show: true,
        message: raw ? 'Token generated — copy it now' : 'Token generated',
        color: 'success',
      });
    } catch (err) {
      setToast?.({ show: true, message: `Could not generate token: ${err.message || 'unknown error'}`, color: 'danger' });
    } finally {
      setSavingToken(false);
    }
  };

  // Save-button gating — don't allow submitting an incomplete config. Basic
  // needs a username + (a freshly-typed password, or one already on file). IP
  // needs at least one well-formed IP/CIDR line.
  const basicSaveDisabled =
    !basicUsername.trim() || (!passwordOnFile && !basicPassword);
  const ipLines = ipText.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
  const ipSaveDisabled = ipLines.length === 0 || !ipLines.every(isValidIpOrCidr);

  return (
    <div className="pipeline-webhook-auth">
      <div className="pipeline-auth-cards">
      {/* ── Basic auth ─────────────────────────────────────────────── */}
      <AuthCard
        name="Basic auth"
        description="Require an HTTP Basic username + password."
        hint="Basic or Bearer — either one passes. Both on ≠ both required."
        enabled={basicEnabled}
        onToggle={handleToggleBasic}
        toggling={savingBasic}
      >
        <input
          type="text"
          className="pipeline-env-add-input"
          placeholder="Username"
          value={basicUsername}
          autoComplete="off"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          onChange={(e) => setBasicUsername(e.target.value)}
          aria-label="Basic auth username"
        />
        <input
          type="password"
          className="pipeline-env-add-input"
          placeholder="Password"
          value={basicPassword}
          autoComplete="new-password"
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          onFocus={() => { if (basicPassword === PW_SENTINEL) setBasicPassword(''); }}
          onChange={(e) => setBasicPassword(e.target.value)}
          aria-label="Basic auth password"
        />
        <div className="pipeline-auth-block-actions">
          <PillButton loading={savingBasic} disabled={basicSaveDisabled} onClick={handleSaveBasic}>
            Save
          </PillButton>
        </div>
      </AuthCard>

      {/* ── IP allowlist ───────────────────────────────────────────── */}
      <AuthCard
        name="IP allowlist"
        description="Only accept webhook calls from these IPs / CIDRs."
        hint="Separate gate (AND) — stacks on top of any credential check."
        enabled={ipEnabled}
        onToggle={handleToggleIp}
        toggling={savingIp}
      >
        <textarea
          className="pipeline-env-add-textarea"
          rows={4}
          placeholder={'One IP or CIDR per line, e.g.\n203.0.113.7\n198.51.100.0/24\n2001:db8::/32'}
          value={ipText}
          onChange={(e) => setIpText(e.target.value)}
          aria-label="Allowed IPs and CIDR ranges"
        />
        <div className="pipeline-auth-block-actions">
          <PillButton loading={savingIp} disabled={ipSaveDisabled} onClick={handleSaveIp}>
            Save
          </PillButton>
        </div>
      </AuthCard>

      {/* ── Token ──────────────────────────────────────────────────── */}
      <AuthCard
        name="Bearer token"
        description="Require a bearer token (Authorization: Bearer or X-Webhook-Token)."
        hint="Basic or Bearer — either one passes. Both on ≠ both required."
        enabled={tokenEnabled}
        onToggle={handleToggleToken}
        toggling={savingToken}
      >
        {plaintextToken ? (
          <>
            <p className="pipeline-auth-hint pipeline-auth-token-warn">
              Shown once — copy it now. You won't be able to see it again.
            </p>
            <CmdRow command={plaintextToken} onCopy={onCopy} />
          </>
        ) : tokenCfg.tokenMasked ? (
          <div className="pipeline-auth-token-masked">
            <code className="pipeline-auth-token-masked-code">{tokenCfg.tokenMasked}</code>
            <PillButton loading={savingToken} onClick={handleGenerateToken}>
              Regenerate
            </PillButton>
          </div>
        ) : (
          <p className="pipeline-auth-hint">
            No token yet — generate one to require it on every request.
          </p>
        )}
        {!tokenCfg.tokenMasked && (
          <div className="pipeline-auth-block-actions">
            <PillButton loading={savingToken} onClick={handleGenerateToken}>
              {plaintextToken ? 'Regenerate' : 'Generate'}
            </PillButton>
          </div>
        )}
      </AuthCard>
      </div>
    </div>
  );
};

const Stat = ({ label, value, copyable, onCopy }) => (
  <div className="pipeline-stat">
    <div className="pipeline-stat-label">{label}</div>
    <div className="pipeline-stat-value">
      <span className="pipeline-stat-value-text">{value}</span>
      {copyable && value && value !== '—' && (
        <button
          type="button"
          className="pipeline-stat-copy"
          onClick={() => onCopy(value)}
          title="Copy"
        >
          <IonIcon icon={copyOutline} />
        </button>
      )}
    </div>
  </div>
);

// Default @name a code-review agent responds to when it has no per-agent
// override. Mirrors the backend/self-host fallback (webhook falls back here
// when reviewMentionToken is null/empty).
const MENTION_DEFAULT = '@zibby';

/**
 * Editable per-agent review "Mention" cell (gitlab/github review agents only).
 *
 * Styled like a read-only `Stat` (same label/value structure + classNames) but
 * the value is an inline `<input>`. Editing saves the workflow's
 * `reviewMentionToken` via `PATCH /workflows/:uuid` on Enter or blur; Escape
 * reverts to the last-saved value. An empty input clears the override (sends
 * `null` → the webhook falls back to the box default `@zibby-review`).
 *
 * On success we adopt the NORMALIZED token the backend echoes (`r.reviewMentionToken`,
 * which forces a leading `@`), reset the input to it, and optimistically patch
 * the cached pipeline so a tab switch / refetch shows the saved value.
 */
const MentionStat = ({ pipeline, setToast, onPipelineUpdate }) => {
  const resolve = (tok) => (tok && String(tok).trim()) || MENTION_DEFAULT;
  const [value, setValue] = useState(resolve(pipeline.reviewMentionToken));
  const [savedValue, setSavedValue] = useState(resolve(pipeline.reviewMentionToken));
  const [busy, setBusy] = useState(false);

  // Re-sync when the cached pipeline's token changes underneath us (an
  // optimistic patch, a tab switch, or a background refetch that now returns
  // reviewMentionToken). Keeps the input authoritative without clobbering an
  // in-flight edit — busy guards the write path, not this passive sync.
  useEffect(() => {
    const next = resolve(pipeline.reviewMentionToken);
    setSavedValue(next);
    setValue(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline.reviewMentionToken]);

  const save = useCallback(async () => {
    const trimmed = value.trim();
    // No-op / re-entrancy guard: nothing changed, or a save is already flying.
    if (busy || trimmed === savedValue) { setValue(savedValue); return; }
    setBusy(true);
    try {
      // '' → null clears the override; backend validates (leading '@', ≤60
      // chars, [A-Za-z0-9._-]) and echoes the normalized token.
      const r = await api.patch(
        `/workflows/${pipeline.uuid}`,
        { reviewMentionToken: trimmed || null },
      );
      const next = (r && typeof r.reviewMentionToken === 'string' && r.reviewMentionToken) || MENTION_DEFAULT;
      setSavedValue(next);
      setValue(next);
      // Optimistically update the cached row so other tabs/refetches agree.
      onPipelineUpdate?.((prev) => (prev
        ? { ...prev, reviewMentionToken: (r && r.reviewMentionToken) || null }
        : prev));
      setToast?.({ show: true, message: 'Mention saved', color: 'success' });
    } catch (err) {
      setValue(savedValue);   // revert the input to the last good value
      setToast?.({
        show: true,
        message: `Could not save mention: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setBusy(false);
    }
  }, [value, savedValue, busy, pipeline.uuid, onPipelineUpdate, setToast]);

  return (
    <div className="pipeline-stat">
      <div className="pipeline-stat-label">Mention</div>
      <div className="pipeline-stat-value">
        <input
          type="text"
          className="pipeline-stat-mention-input"
          value={value}
          spellCheck={false}
          autoComplete="off"
          maxLength={61}
          disabled={busy}
          title="The @name that triggers this review agent when it appears in a GitHub/GitLab comment. Edit and press Enter to save."
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); e.target.blur(); }
            else if (e.key === 'Escape') { setValue(savedValue); e.target.blur(); }
          }}
          onBlur={save}
        />
      </div>
    </div>
  );
};

// ─── Review triggers ─────────────────────────────────────────────────
//
// Per-agent picker for WHICH webhook events fire a code-review (to save
// money — e.g. @mention-only). Mirrors the self-host dashboard exactly
// (backend/selfhosted/ui/index.html: REVIEW_TRIG_MENU / reviewRowEvents /
// reviewTrigSummary / openTriggersModal). The workflow row carries two
// additive fields, present ONLY for code-review agents:
//   reviewTriggerMenu   — the FULL tickable set of event ids
//                         (e.g. ['gitlab.mr.opened','gitlab.mr.updated',
//                          'gitlab.mr.mentioned','gitlab.mr.commented'])
//   reviewTriggerEvents — the currently-ON subset
// Saving PATCHes { reviewTriggerEvents: [...] }; the backend recomputes the
// subscription and echoes back reviewTriggerEvents + reviewTriggerMenu.
//
// The menu maps onto 3 GROUPED rows (not one row per event) by the event
// id's action SUFFIX (the part after the last '.'):
//   opened                → row 1 "New MR/PR opened"
//   updated               → row 2 "New commits pushed"
//   mentioned + commented → row 3 "@mention or reply" (ONE row on purpose)
const REVIEW_TRIGGER_ROWS = [
  { key: 'opened',  actions: ['opened'],                 auto: true,  label: 'New MR/PR opened',   hint: 'Review as soon as a merge / pull request is created' },
  { key: 'commit',  actions: ['updated'],                auto: true,  label: 'New commits pushed', hint: 'Re-review every time new code is pushed to an open MR/PR — fires most during development, so it costs the most' },
  { key: 'mention', actions: ['mentioned', 'commented'], auto: false, label: '@mention or reply',  hint: 'Only when someone @-mentions the agent or replies to its comment (cheapest — it stays quiet until asked)' },
];
// The action suffix of an event id ('gitlab.mr.opened' → 'opened').
const reviewTrigAction = (ev) => {
  const parts = String(ev).split('.');
  return parts[parts.length - 1];
};
// The declared menu events that fall under a row (present in THIS agent's menu).
const reviewRowEvents = (row, menu) =>
  (menu || []).filter((ev) => row.actions.includes(reviewTrigAction(ev)));
// Compact summary label for the stat cell (mirrors the self-host reference).
function reviewTrigSummary(on, menu) {
  on = on || [];
  menu = menu || [];
  if (!menu.length) return '—';
  if (on.length === 0) return 'Off';
  const isAuto = (e) => { const a = reviewTrigAction(e); return a === 'opened' || a === 'updated'; };
  const autoOn = on.filter(isAuto).length;
  if (autoOn === 0) return '@mention only';
  if (on.length === menu.length) return 'All events';
  const rowsOn = REVIEW_TRIGGER_ROWS.filter((r) => {
    const evs = reviewRowEvents(r, menu);
    return evs.length && evs.some((e) => on.includes(e));
  }).length;
  const rowsTotal = REVIEW_TRIGGER_ROWS.filter((r) => reviewRowEvents(r, menu).length).length;
  return `${rowsOn} of ${rowsTotal}`;
}

/**
 * Editable per-agent "Review triggers" cell (code-review agents only).
 *
 * Styled like `AgentStat` (a clickable `pipeline-stat` cell showing a compact
 * summary + pencil) — clicking opens a modal with 3 grouped checkbox rows.
 * Rendered ONLY when the workflow row carries a non-empty `reviewTriggerMenu`
 * (self-gating → additive + zero regression for every other agent type).
 *
 * On Save it collects the event ids of all checked rows and
 * `PATCH /workflows/:uuid { reviewTriggerEvents: [...] }` (same PATCH helper as
 * the mention cell), then optimistically patches the cached row so the summary
 * + any refetch agree — no full reload. Modal chrome reuses `ConfirmModal`
 * (marketplace modal styles) with its built-in buttons suppressed, exactly like
 * the Schedule modal on this page.
 */
const TriggersStat = ({ pipeline, setToast, onPipelineUpdate }) => {
  const menu = useMemo(
    () => (Array.isArray(pipeline.reviewTriggerMenu) ? pipeline.reviewTriggerMenu : []),
    [pipeline.reviewTriggerMenu],
  );
  const on = useMemo(
    () => (Array.isArray(pipeline.reviewTriggerEvents) ? pipeline.reviewTriggerEvents : []),
    [pipeline.reviewTriggerEvents],
  );
  // The rows this agent actually declares (has ≥1 event for in its menu). A row
  // with no matching menu events is never rendered (e.g. a template with no
  // 'commented' event won't show the @mention half of that row differently —
  // the row simply drops if it has zero events).
  const rows = useMemo(
    () => REVIEW_TRIGGER_ROWS
      .map((row) => ({ ...row, events: reviewRowEvents(row, menu) }))
      .filter((row) => row.events.length),
    [menu],
  );

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Draft checked state keyed by row.key — seeded from the current ON set on open.
  const [draft, setDraft] = useState({});

  const openModal = () => {
    const seed = {};
    for (const row of rows) seed[row.key] = row.events.some((e) => on.includes(e));
    setDraft(seed);
    setOpen(true);
  };

  const closeModal = () => { if (!saving) setOpen(false); };

  const save = async () => {
    if (saving) return;
    // Collect the events of every CHECKED row.
    const sel = [];
    for (const row of rows) if (draft[row.key]) sel.push(...row.events);
    setSaving(true);
    try {
      const r = await api.patch(
        `/workflows/${pipeline.uuid}`,
        { reviewTriggerEvents: sel },
      );
      const nextEvents = (r && Array.isArray(r.reviewTriggerEvents)) ? r.reviewTriggerEvents : sel;
      const nextMenu = (r && Array.isArray(r.reviewTriggerMenu)) ? r.reviewTriggerMenu : menu;
      // Optimistically update the cached row so the summary + other tabs agree.
      onPipelineUpdate?.((prev) => (prev
        ? { ...prev, reviewTriggerEvents: nextEvents, reviewTriggerMenu: nextMenu }
        : prev));
      setToast?.({
        show: true,
        message: `Triggers saved — ${reviewTrigSummary(nextEvents, nextMenu)}`,
        color: 'success',
      });
      setOpen(false);
    } catch (err) {
      setToast?.({
        show: true,
        message: `Could not save triggers: ${err.message || 'unknown error'}`,
        color: 'danger',
      });
    } finally {
      setSaving(false);
    }
  };

  // Nothing to render for a non-review agent (no menu) → additive/zero-regression.
  if (!menu.length) return null;

  const mentionName = String(pipeline.reviewMentionToken || MENTION_DEFAULT).replace(/^@+/, '');

  return (
    <>
      <div className="pipeline-stat">
        <div className="pipeline-stat-label">Triggers</div>
        <div className="pipeline-stat-value">
          <button
            type="button"
            className="pipeline-stat-agent-edit"
            onClick={openModal}
            title="Choose which webhook events run a review"
          >
            <span className="pipeline-stat-value-text">{reviewTrigSummary(on, menu)}</span>
            <IonIcon icon={createOutline} />
          </button>
        </div>
      </div>

      <ConfirmModal
        open={open}
        onClose={closeModal}
        title="Review triggers"
        ariaLabel="Choose which events run a review"
        secondaryLabel=""
        maxWidth={460}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ fontSize: 12.5, lineHeight: 1.5, opacity: 0.7, margin: 0 }}>
            Choose when this agent runs a review. Untick the auto events to save
            money — keep <b>@mention</b> on so <code>@{mentionName}</code> still
            triggers it.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {rows.map((row) => (
              <label
                key={row.key}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10,
                  padding: '10px 12px', borderRadius: 10,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.02)',
                  cursor: saving ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!draft[row.key]}
                  disabled={saving}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [row.key]: e.target.checked }))}
                  style={{ marginTop: 3, flex: '0 0 auto', cursor: saving ? 'default' : 'pointer' }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <span style={{ fontSize: 13.5, color: '#eee' }}>{row.label}</span>
                  <span style={{ fontSize: 12, lineHeight: 1.45, opacity: 0.62 }}>{row.hint}</span>
                </span>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={closeModal}
              disabled={saving}
              style={{ fontSize: 13, color: '#aaa', background: 'transparent', border: 'none', cursor: saving ? 'default' : 'pointer' }}
            >
              Cancel
            </button>
            <PillButton
              className="pill-button--sm"
              onClick={save}
              disabled={saving}
              loading={saving}
            >
              Save
            </PillButton>
          </div>
        </div>
      </ConfirmModal>
    </>
  );
};

/**
 * Per-workflow AI-agent + model. Editable: clicking opens the shared
 * AgentConfigModal, which changes THIS pipeline's agent + model + (per-vendor)
 * API key. Each
 * pipeline owns an independent snapshot of the AI config (taken from the
 * project at create), so the old "picking Codex on a Claude-only project
 * silently fails" footgun is gone — the modal collects that vendor's key
 * right there and stores it encrypted on the workflow row.
 *
 * Resolves the workflow's own override first; falls back to the project
 * default label when both aiAgent + aiModel are absent.
 */
const CmdRow = ({ command, onCopy }) => {
  // Whole row is clickable — clicking anywhere on the row copies the
  // command (not just the small icon at the end). The dedicated icon
  // button stays for affordance + keyboard reachability, but
  // stopPropagation prevents the click from double-firing onCopy.
  return (
    <div
      className="pipeline-cmd-row"
      role="button"
      tabIndex={0}
      onClick={() => onCopy(command)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCopy(command);
        }
      }}
      title="Click to copy"
      style={{ cursor: 'pointer' }}
    >
      <code className="pipeline-cmd">{command}</code>
      <button
        type="button"
        className="pipeline-cmd-copy"
        onClick={(e) => { e.stopPropagation(); onCopy(command); }}
        title="Copy"
      >
        <IonIcon icon={copyOutline} />
      </button>
    </div>
  );
};

// ─── Activity ────────────────────────────────────────────────────────

const ActivityTab = ({ projectId, workflowType, executionId, pipeline, setToast, navigate }) => {
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  // Pagination state — backend returns { executions, nextKey, hasMore }.
  // We append on subsequent fetches and stop when hasMore goes false.
  const [nextKey, setNextKey] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const sentinelRef = useRef(null);
  const fetchMoreRef = useRef(null);   // latest fetchMore — for the observer effect

  // When a specific run is selected via URL (/runs/:executionId), fetch
  // its full detail and render the per-node pipeline inline below the
  // list. Avoids the orphan "separate page" + lets the sidebar /
  // breadcrumb keep the workflow context.
  const [selectedRun, setSelectedRun] = useState(null);
  const [selectedRunLoading, setSelectedRunLoading] = useState(false);
  const pollRef = useRef(null);

  // Composite-agent (wrapper) support. Which of this agent's graph nodes
  // dispatch a child workflow — { nodeName: [childWorkflowType, ...] } from the
  // graph's static `dispatchesWorkflow` markers. `null` = graph unknown
  // (legacy row / fetch raced) → we still probe for children once so
  // wrapper runs never silently lose them.
  //
  // The value is a LIST because a node's marker is a string OR an array of
  // strings: a ROSTER node dispatches every declared member from one place, and
  // reading only the string shape left every one of that node's child runs
  // unmatched — they all fell into the trailing "unattached" group.
  const dispatchNodeMap = useMemo(() => {
    const cfgs = pipeline?.graph?.nodeConfigs;
    if (!cfgs || typeof cfgs !== 'object') return null;
    return dispatchNodeMapOf(cfgs);
  }, [pipeline?.graph?.nodeConfigs]);

  // Child runs this execution dispatched (rows stamped with
  // parentExecutionId by the sub-graph begin path). Fetched alongside the
  // run poll — but ONLY for agents that can actually have children
  // (dispatch markers in the graph, or composedOf on the row, or graph
  // unknown), so plain agents pay zero extra requests.
  const [childRuns, setChildRuns] = useState([]);
  const mayHaveChildren = dispatchNodeMap === null
    || Object.keys(dispatchNodeMap).length > 0
    || (Array.isArray(pipeline?.composedOf) && pipeline.composedOf.length > 0);

  // Jump to the wrapper run that dispatched a child. Prefers the
  // denormalized parentWorkflowType stamped on newer child rows; falls
  // back to resolving the parent execution (older rows), then to the
  // uuid-only legacy run route which needs no workflowType.
  const goToParentRun = useCallback(async (e) => {
    const pid = e?.parentExecutionId;
    if (!pid) return;
    let wt = e.parentWorkflowType;
    if (!wt) {
      try {
        const parent = await api.get(`/executions/${pid}`);
        wt = parent?.workflowType || parent?.workflow_type;
      } catch { /* fall through to the uuid-only route */ }
    }
    if (wt) navigate(`/projects/${projectId}/agents/${wt}/runs/${pid}`);
    else navigate(`/projects/${projectId}/runs/${pid}`);
  }, [projectId, navigate]);

  useEffect(() => {
    if (!executionId) {
      setSelectedRun(null);
      setChildRuns([]);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    let cancelled = false;
    const fetchOne = async () => {
      try {
        const data = await api.get(`/executions/${executionId}`);
        if (cancelled) return;
        setSelectedRun(data);
        if (mayHaveChildren) {
          // Progressive enhancement — a failed child-list fetch must never
          // break the run view, so it's fire-and-forget with a swallow.
          try {
            const qs = new URLSearchParams({ projectId, parentExecutionId: executionId, limit: '50' });
            const kids = await api.get(`/executions?${qs.toString()}`);
            if (!cancelled) {
              const rows = Array.isArray(kids?.executions) ? kids.executions : [];
              rows.sort((a, b) => (a.createdAtMs || 0) - (b.createdAtMs || 0));
              setChildRuns(rows);
            }
          } catch { /* keep last known children */ }
        }
        // Same terminal-status gate as the legacy run page — once done,
        // stop polling. Avoids the infinite-fetch bug.
        if (TERMINAL_STATUSES.has(data?.status) && pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (err) {
        if (!cancelled) setToast?.({ show: true, message: `Could not load run: ${err.message}`, color: 'danger' });
      } finally {
        if (!cancelled) setSelectedRunLoading(false);
      }
    };
    setSelectedRunLoading(true);
    fetchOne();
    pollRef.current = setInterval(fetchOne, 1500);
    return () => {
      cancelled = true;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
    // mayHaveChildren flips once the agent's graph loads — restarting the
    // poll then is cheap and keeps the child-run probe accurate.
  }, [executionId, setToast, projectId, mayHaveChildren]);

  // Children grouped under the dispatch step that spawned them (matched
  // node.dispatchesWorkflow CONTAINS child.workflowType — a roster node
  // declares several). Anything unmatched (graph unavailable, node renamed)
  // lands in the trailing group so a child run is never unreachable from its
  // wrapper.
  const { childRunsByStep, unattachedChildRuns } = useMemo(() => {
    if (!childRuns.length) return { childRunsByStep: null, unattachedChildRuns: [] };
    const entries = Object.entries(dispatchNodeMap || {});
    const byStep = {};
    const unattached = [];
    childRuns.forEach((c) => {
      const hit = entries.find(([, childTypes]) => childTypes.includes(c.workflowType || c.workflow_type));
      if (hit) (byStep[hit[0]] = byStep[hit[0]] || []).push(c);
      else unattached.push(c);
    });
    return { childRunsByStep: byStep, unattachedChildRuns: unattached };
  }, [childRuns, dispatchNodeMap]);

  // Page-fetch helper — used for both initial load and infinite-scroll
  // appends. Returns the merged data so we can swap `executions` in one
  // setState (avoids the "two re-renders" flash).
  const fetchPage = useCallback(async (cursor) => {
    const qs = new URLSearchParams({ projectId, workflowType, limit: '50' });
    if (cursor) qs.set('nextKey', cursor);
    const data = await api.get(`/executions?${qs.toString()}`);
    const list = Array.isArray(data?.executions) ? data.executions
              : Array.isArray(data) ? data
              : [];
    // The server now filters by `workflowType` AND returns a createdAtMs
    // cursor (`nextKey`), so each page is this agent's runs only and infinite
    // scroll resumes exactly where it left off. Keep a defensive client-side
    // filter for any legacy row the server slipped through.
    const filtered = list.filter(
      (e) => e.workflowType === workflowType || e.workflow_type === workflowType,
    );
    return { rows: filtered, nextKey: data?.nextKey || null, hasMore: !!data?.hasMore };
  }, [projectId, workflowType]);

  // Initial load — reset everything when projectId/workflowType changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setExecutions([]);
      setNextKey(null);
      try {
        const { rows, nextKey: nk } = await fetchPage(null);
        if (!cancelled) {
          setExecutions(rows);
          setNextKey(nk);
        }
      } catch (err) {
        if (!cancelled) {
          setToast?.({ show: true, message: `Could not load runs: ${err.message}`, color: 'danger' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fetchPage, setToast]);

  // Infinite scroll — IntersectionObserver on a sentinel below the
  // table fetches the next page when it scrolls into view. Stops when
  // backend reports no more pages (nextKey === null after a fetch).
  const fetchMore = useCallback(async () => {
    if (!nextKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const { rows, nextKey: nk } = await fetchPage(nextKey);
      setExecutions((prev) => [...prev, ...rows]);
      setNextKey(nk);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load more: ${err.message}`, color: 'danger' });
    } finally {
      setLoadingMore(false);
    }
  }, [nextKey, loadingMore, fetchPage, setToast]);
  fetchMoreRef.current = fetchMore;

  useEffect(() => {
    if (executionId) return undefined;        // inline panel open → no auto-load
    if (!nextKey) return undefined;            // no more pages
    const node = sentinelRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) fetchMoreRef.current?.();
    }, { rootMargin: '120px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [nextKey, executionId]);

  const columns = [
    {
      header: 'STATUS',
      key: 'status',
      render: (e) => <span className={`pipeline-run-status pipeline-run-status-${e.status || 'unknown'}`}>{e.status || '—'}</span>,
    },
    {
      header: 'EXECUTION ID',
      key: 'executionId',
      // Child runs (dispatched by a composite/wrapper agent's run) carry a
      // "child of <wrapper-run>" chip linking to the parent run — they only
      // appear in THIS (the child agent's) activity list, never mixed into
      // the wrapper's, because the list is workflowType-scoped. Badging
      // beats indenting/nesting here: the parent run lives on a DIFFERENT
      // agent's paginated list, so there is no parent row to nest under.
      render: (e) => (
        <span className="pipeline-run-id-cell">
          <code className="pipeline-run-id">{(e.executionId || e.id || '').slice(0, 12)}</code>
          {e.parentExecutionId && (
            <button
              type="button"
              className="pipeline-run-childof"
              title={`Dispatched by wrapper run ${e.parentExecutionId}`}
              onClick={(ev) => { ev.stopPropagation(); goToParentRun(e); }}
            >
              child of {String(e.parentExecutionId).slice(0, 8)} →
            </button>
          )}
        </span>
      ),
    },
    { header: 'STARTED',      key: 'startedAt',   render: (e) => (e.createdAt || e.startedAt) ? timeAgo(e.createdAt || e.startedAt) : '—' },
    { header: 'DURATION',     key: 'duration',    render: (e) => formatDuration(e) },
    // No COST column: agents run on a Claude subscription (OAuth), which
    // doesn't meter per-token usage, so cost is always $0 here — a column
    // that's always $0.0000 is misleading, so it's omitted. (Per-run token
    // usage, when reported, still shows in the run detail panel.)
  ];

  // Same trick as EnvTab: pass an actions array so DataTable adds the
  // 48px-wide actions <th> at the end. Without it, DURATION becomes
  // last-child and gets `width: 48px; text-align: center` from the
  // generic table CSS.
  // Navigate Activity rows to the new pipeline-scoped run detail page
  // (PipelineRunDetailPage). The legacy `/runs/:id` route is kept intact
  // for the existing test-recipe flow — we just don't use it from here.
  const goToRun = (e) =>
    navigate(`/projects/${projectId}/agents/${workflowType}/runs/${e.executionId || e.id}`);

  const actions = [
    {
      label: 'View run',
      icon: documentTextOutline,
      onClick: goToRun,
    },
  ];

  // Steps array for ExecutionPipeline. The activity panel renders the
  // canonical workflow shape (every node, always) and overlays reported
  // status on top — same as AnalysisDetailsPage.
  //
  // Node-list source precedence:
  //   1. selectedRun.workflowNodes  — FROZEN snapshot stamped at run
  //                                   start. This is the canonical
  //                                   "what this run actually was"
  //                                   shape, immune to later edits of
  //                                   the template's graph.mjs. Always
  //                                   use this when present.
  //   2. pipeline.graph.nodes       — LIVE shape from the marketplace
  //                                   row overlay. Only reached for
  //                                   legacy runs that pre-date the
  //                                   executor's workflowNodes stamp.
  //                                   May misrepresent old runs if the
  //                                   template's graph has since
  //                                   changed — best-effort fallback.
  //
  // Reported steps fall into two buckets:
  //   - "node" steps         — name matches a workflow node → overlay status
  //   - "lifecycle" steps    — ECS-task phases (Setup, etc.) that don't
  //                            match any workflow node → prepended ahead
  //                            of workflow nodes so the user sees the
  //                            full execution timeline
  //
  // Status promotion logic at the bottom keeps statuses sane against
  // the overall execution status (terminal-success promotes pendings,
  // failed promotes runnings, etc.).
  const inlineSteps = useMemo(() => {
    if (!selectedRun) return [];
    const reported = selectedRun.steps || [];
    const reportedByName = new Map(reported.map((s) => [s.name, s]));

    // Seed the node list from the run's per-run snapshot (the executor
    // freezes the workflow's node list onto the execution row at run start).
    // The executions GET handler now preferentially returns that stamped
    // snapshot (fixed in executions.js — it used to clobber it with a
    // graphJson-only lookup that's null for bundle templates), so this is the
    // full, run-accurate node list for every workflow. graph.nodes is the
    // fallback for older rows with no stamped snapshot. Fully dynamic — node
    // names come from the workflow's own graph, nothing hardcoded here.
    const graphNodes = Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes : [];
    const workflowNodesRaw = Array.isArray(selectedRun.workflowNodes) && selectedRun.workflowNodes.length > 0
      ? selectedRun.workflowNodes
      : graphNodes.map((n) => ({ name: n.name || n.id, displayName: n.displayName || n.label }));
    // DISPLAY-ONLY graph furniture is NOT a step. `START`, `END__<n>` and
    // `<node>__branch` are synthesized by the engine purely so the graph viewer
    // can draw where flow enters, terminates and forks — "the runtime ignores
    // them" (agent-workflow graph.ts). They can therefore never report a step,
    // so they rendered forever as pending/skipped rows claiming the workflow
    // "short-circuited before this step ran" — false by construction, and every
    // completed run showed an unticked Start plus one row per End. Matched on
    // the engine's exact synthesized ids (upper-case), so a template's own node
    // called "start"/"end" is untouched.
    const workflowNodes = workflowNodesRaw.filter((n) => {
      const id = n?.name != null ? String(n.name) : '';
      const t = String(n?.type || n?.nodeType || '').toLowerCase();
      if (t === 'start' || t === 'end' || t === 'decision') return false;
      return !(id === 'START' || /^END(__\d+)?$/.test(id) || /__branch$/.test(id));
    });

    // Build the node-row list. Every workflow node renders — pending
    // until a matching reported step shows up to overlay status / logs.
    const workflowNodeNames = new Set(workflowNodes.map((n) => n.name));
    const nodeRows = workflowNodes.map((node, idx) => {
      const r = reportedByName.get(node.name);
      const base = r ? { ...r } : { name: node.name };
      return {
        ...base,
        displayName: node.displayName ||
          base.displayName ||
          base.name?.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) ||
          `Step ${idx + 1}`,
        status: base.status || 'pending',
      };
    });

    // Canonical lifecycle phases that happen BEFORE the workflow's own
    // nodes run. Render them as placeholders from t=0 so the user sees
    // the full timeline immediately ("Setup → fetch_issues → …") rather
    // than Setup popping in on the next poll once the backend stamps
    // it onto execution.steps. Reported status overlays the placeholder
    // when it lands. Currently just Setup (ECS task provisioning +
    // container bootstrap); add 'Bundle Sync' / 'Egress Check' here if
    // future executor stages report under those names.
    //
    // CRITICAL: `name` must match the lowercase name the backend stamps
    // (analyze.js / workflow-executor.js write `name: 'setup'`). A
    // case-mismatched placeholder doesn't dedup against the reported
    // step → renders twice.
    const LIFECYCLE_PHASES = [
      { name: 'setup', displayName: 'Setup' },
    ];
    const lifecycleRows = LIFECYCLE_PHASES.map((phase) => {
      const r = reportedByName.get(phase.name);
      return {
        ...(r || {}),
        name: phase.name,
        displayName: r?.displayName || phase.displayName,
        status: r?.status || 'pending',
      };
    });

    // Reported steps that match neither a workflow node nor a known
    // lifecycle phase (future executor stages we haven't enumerated):
    // append AFTER lifecycle phases, BEFORE workflow nodes, so they
    // still surface visually instead of being silently dropped.
    const knownNames = new Set([
      ...workflowNodeNames,
      ...LIFECYCLE_PHASES.map((p) => p.name),
    ]);
    const orphanLifecycleRows = reported
      .filter((s) => !knownNames.has(s.name))
      .map((s) => ({
        ...s,
        displayName: s.displayName || s.name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
        status: s.status || 'pending',
      }));

    const out = [...lifecycleRows, ...orphanLifecycleRows, ...nodeRows];
    if (!out.length) return [];

    const exStatus = selectedRun.status;
    if (['completed', 'success', 'succeeded'].includes(exStatus)) {
      // When the workflow as a whole succeeded, distinguish two cases
      // for any node we never received a progress event for:
      //   1. The node ran but its progress message was dropped/raced —
      //      treat as completed (green ✓). Detection: it appears in the
      //      reported `steps` array, just with `status: 'pending'`.
      //   2. The graph SKIPPED the node via a conditional edge — e.g.
      //      sentry-triage routes `fetch_issues -> END` when 0 issues,
      //      bypassing classify + dispatch_alerts entirely. Detection:
      //      the node is in `workflowNodes` but never made it into
      //      `reported` (we built the row from the snapshot placeholder).
      //      Render as `skipped` (gray) so the operator can tell at a
      //      glance "nothing to do" from "ran clean".
      const reportedNames = new Set(reported.map((s) => s.name));
      out.forEach((s) => {
        if (s.status === 'pending' || s.status === 'queued') {
          s.status = reportedNames.has(s.name) ? 'completed' : 'skipped';
        }
      });
    }
    if (exStatus === 'failed') {
      out.forEach((s) => {
        if (s.status === 'running' || s.status === 'in_progress') s.status = 'failed';
        else if (s.status === 'queued' || s.status === 'pending')  s.status = 'cancelled';
      });
    }
    return out;
  }, [selectedRun, pipeline?.graph?.nodes]);

  return (
    <div className="pipeline-tab">
      {/* Activity list — hidden when an execution is selected so the
          focus is fully on the run detail below. Close button on the
          detail panel brings the list back. */}
      {!executionId && (
        <>
          <DataTable
            data={executions}
            columns={columns}
            loading={loading}
            rowKey="executionId"
            actions={actions}
            onRowClick={goToRun}
            emptyState={{
              title: 'No runs yet',
              description: `Trigger this agent with: zibby workflow trigger <uuid>`,
            }}
          />
          {/* Infinite-scroll sentinel — when this scrolls into view, the
              IntersectionObserver above kicks the next page fetch.
              Renders three states:
                - "Loading more…"  during a page fetch
                - blank            when more pages exist but not yet
                                   loading (sentinel is the trigger)
                - "End of runs"    when nextKey is null (no more pages)
              Always kept in the tree so users see the bottom marker. */}
          {!loading && executions.length > 0 && (
            <div
              ref={sentinelRef}
              className="pipeline-activity-sentinel"
              aria-hidden="true"
            >
              {loadingMore ? 'Loading more…' : (nextKey ? '' : '— End of runs —')}
            </div>
          )}
        </>
      )}

      {/* Inline run detail — shown when a row is selected via URL. The
          standalone PipelineRunDetailPage used to render this on a
          separate route, but the separate page lost the sidebar +
          breadcrumb context. Rendering inline keeps the workflow page
          intact and avoids one more route to maintain. */}
      {executionId && (
        <div className="pipeline-run-inline">
          {/* Run header — restored to match the standalone
              PipelineRunDetailPage's chrome (status pill + execution id
              + copy button + Started <ago>). The "Run <id>" h3 alone was
              too sparse once the ExecutionPipeline below renders
              placeholder steps for executions that haven't reported
              status yet; the meta row gives the user actual context
              (is it running? when did it start?). */}
          <div className="pipeline-run-inline-header">
            <div className="pipeline-run-meta">
              {selectedRun?.status && (
                <>
                  <span className={`pipeline-run-statuspill pipeline-run-statuspill-${selectedRun.status}`}>
                    {selectedRun.status}
                  </span>
                  <span className="pipeline-run-meta-sep">·</span>
                </>
              )}
              <code className="pipeline-run-id">{executionId.slice(0, 12)}</code>
              <button
                type="button"
                className="pipeline-run-copy"
                onClick={() => navigator.clipboard?.writeText(executionId).then(() => setToast?.({ show: true, message: 'Copied execution ID', color: 'success' }))}
                title="Copy execution ID"
              >
                <IonIcon icon={copyOutline} />
              </button>
              {(selectedRun?.createdAt || selectedRun?.startedAt) && (
                <>
                  <span className="pipeline-run-meta-sep">·</span>
                  <span className="pipeline-run-time">
                    Started {timeAgo(selectedRun.createdAt || selectedRun.startedAt)}
                  </span>
                </>
              )}
            </div>
            {selectedRun?.totalUsage && (Number(selectedRun.totalUsage.input_tokens || 0) + Number(selectedRun.totalUsage.output_tokens || 0)) > 0 && (
              <div className="pipeline-run-total-usage">
                <span className="pipeline-run-total-usage-tokens">
                  {formatTokensCompact(Number(selectedRun.totalUsage.input_tokens || 0))} in
                  {' · '}
                  {formatTokensCompact(Number(selectedRun.totalUsage.output_tokens || 0))} out
                  {Number(selectedRun.totalUsage.cache_read_tokens || 0) > 0 && (
                    <>{' · '}{formatTokensCompact(Number(selectedRun.totalUsage.cache_read_tokens))} cached</>
                  )}
                </span>
                {Number(selectedRun.totalUsage.estimated_cost_usd || 0) > 0 && (
                  <span className="pipeline-run-total-usage-cost">
                    {' · $'}{Number(selectedRun.totalUsage.estimated_cost_usd).toFixed(4)}
                  </span>
                )}
              </div>
            )}
          </div>
          {/* This run is itself a CHILD dispatched by a wrapper run —
              surface the parent link so the hierarchy is walkable both
              ways (wrapper → child via the nested rows, child → wrapper
              here). */}
          {selectedRun?.parentExecutionId && (
            <div className="pipeline-run-childof-banner">
              ↰ Dispatched by wrapper run
              <button
                type="button"
                className="pipeline-run-childof"
                title={`Open wrapper run ${selectedRun.parentExecutionId}`}
                onClick={() => goToParentRun(selectedRun)}
              >
                {String(selectedRun.parentExecutionId).slice(0, 8)} →
              </button>
            </div>
          )}
          {/* In-process children don't report per-node steps to their own
              row — their node-by-node logs stream into the WRAPPER's
              dispatch step. Explain that instead of showing a silently
              empty/placeholder pipeline. */}
          {selectedRun?.parentExecutionId
            && selectedRun?.dispatchMode === 'inprocess'
            && !(Array.isArray(selectedRun?.steps) && selectedRun.steps.length > 0) && (
            <div className="pipeline-run-childof-banner pipeline-run-inprocess-note">
              This run executed in-process inside its wrapper run — its node-by-node
              logs are captured on the wrapper run&apos;s dispatch step. Open the wrapper
              run above and expand that step to read them.
            </div>
          )}
          {selectedRunLoading && !selectedRun ? (
            // Skeleton rows that mirror the eventual layout: one
            // wide "status banner" placeholder + 3-4 step rows
            // matching workflowNodes length when known. Same shape
            // as the real ExecutionPipeline so the form doesn't
            // jump when the data arrives — replaces the old plain
            // "Loading run…" text which looked broken next to the
            // populated header.
            <div className="pipeline-run-inline-skeleton" aria-busy="true">
              <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--wide" />
              {Array.from({
                length: Math.max(
                  3,
                  (Array.isArray(pipeline?.graph?.nodes) ? pipeline.graph.nodes.length : 0) + 1,
                ),
              }).map((_, i) => (
                <div key={i} className="pipeline-run-inline-skeleton-row">
                  <div className="pipeline-run-inline-skeleton-dot" />
                  <div className="pipeline-run-inline-skeleton-bar pipeline-run-inline-skeleton-bar--narrow" />
                </div>
              ))}
            </div>
          ) : !selectedRun ? (
            <div className="pipeline-run-inline-empty">Run not found.</div>
          ) : (
            <>
              {/* Run-level error banner. For setup-time failures (state
                  validation, missing inputs, etc.) the per-step logs are
                  often a 2-line stub — the actual reason lives on the
                  execution row. Surface it up top so the user can see
                  WHY the run failed without hunting through CloudWatch. */}
              {(selectedRun.status === 'failed' || selectedRun.status === 'cancelled') &&
               (selectedRun.error || selectedRun.failureReason) && (
                <div className="pipeline-run-error-banner">
                  <div className="pipeline-run-error-banner__label">Failure</div>
                  <pre className="pipeline-run-error-banner__msg">{selectedRun.error || selectedRun.failureReason}</pre>
                </div>
              )}
              <ExecutionPipeline
                steps={inlineSteps}
                executionStatus={selectedRun.status}
                childRunsByStep={childRunsByStep}
                unattachedChildRuns={unattachedChildRuns}
                onOpenChildRun={(c) => navigate(`/projects/${projectId}/agents/${c.workflowType || c.workflow_type}/runs/${c.executionId}`)}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Compact K/M token formatter — mirrors ExecutionPipeline's helper,
 * duplicated here because the run-header total chip lives outside that
 * component. Trim trailing .0/.00 so "12.0K" → "12K" reads cleaner.
 */
function formatTokensCompact(n) {
  if (!n || n <= 0) return '0';
  if (n < 1000) return String(n);
  if (n < 10000)   return (n / 1000).toFixed(2).replace(/\.?0+$/, '') + 'K';
  if (n < 100000)  return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  if (n < 1000000) return Math.round(n / 1000) + 'K';
  return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
}

const formatDuration = (e) => {
  const start = e.createdAt || e.startedAt;
  const end   = e.completedAt || e.endedAt;
  if (!start || !end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (Number.isNaN(ms) || ms < 0) return '—';
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
};

// ─── Custom MCP servers ──────────────────────────────────────────────
//
// Read-only list + delete of the per-agent custom MCP servers the user
// attaches via the Copilot ("add an MCP server …"). We deliberately do NOT
// build an add form here — creation flows through chat (the Copilot collects
// the URL + any auth secret and stores it encrypted). This section only lists
// what's attached and lets the user remove one.
//
// Backend contract (standard authorizer, same authed fetch as the other
// /workflows/:uuid/* calls):
//   GET    /workflows/:uuid/custom-mcp        → { servers: [{ id, name, url,
//                                                 transport:'http', hasAuth,
//                                                 createdAt }] }  (never a token)
//   DELETE /workflows/:uuid/custom-mcp/:id     → { removed:true, id }
//
// `hasAuth` is a boolean only — a lock glyph, never the secret itself.
// ─── Storage ─────────────────────────────────────────────────────────
//
// The stores THIS agent owns, provisioned at deploy from the `stores:[…]` its
// template declares. Ownership is read off the provisioning KEY, which
// store-provisioning.js writes as `<stable workflow id>:<node>:<store name>`
// (the stable id is the workflow TYPE when the row has one, else the uuid) —
// so nothing here names a specific agent, and an agent that declares no store
// renders the empty line.
//
// Renders the SAME StoresTable the Storage page does, over just this agent's
// rows: same columns, same chips, same click-through to the same store detail
// page (/projects/:projectId/stores/:storeId).
function AgentStoresSection({ pipeline }) {
  const { projectId } = useParams();
  const { data: projects } = useProjects();
  const projectToken = projects?.find((p) => p.projectId === projectId)?.apiToken;
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error' | 'noaccess'
  const [stores, setStores] = useState([]);

  const ownerType = String(pipeline?.workflowType || '');
  const ownerUuid = String(pipeline?.uuid || '');

  useEffect(() => {
    let alive = true;
    if (!projectToken) return undefined;
    Stores.list(projectToken)
      .then((res) => {
        if (!alive) return;
        // No Data-view permission → say so, rather than "no stores" (which
        // would read as "this agent has none").
        if (res?.noDataAccess === true) { setStatus('noaccess'); return; }
        setStores((res?.stores || []).filter((s) => {
          // The owner rn (stamped at provision) is authoritative; the key's
          // first segment covers rows that predate the binding. The rn's
          // final segment is the owning agent's uuid.
          const rnUuid = s.owner ? (String(s.owner).split(':')[5] || '') : '';
          if (rnUuid && rnUuid === ownerUuid) return true;
          const parts = String(s.key || '').split(':');
          const seg = parts[0] === 'agent' ? (parts[1] || '') : parts[0];
          return seg && (seg === ownerType || seg === ownerUuid);
        }));
        setStatus('ready');
      })
      .catch(() => { if (alive) setStatus('error'); });
    return () => { alive = false; };
  }, [projectToken, ownerType, ownerUuid]);

  const storageHref = projectId ? `/projects/${encodeURIComponent(projectId)}/stores` : '/stores';

  return (
    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Storage</h3>

      {status === 'noaccess' ? (
        <p className="pipeline-section-hint">
          Your access token doesn&apos;t have <b>Data view</b> permission for this
          project, so this agent&apos;s stores can&apos;t be listed.
        </p>
      ) : status === 'error' ? (
        <p className="pipeline-section-hint">
          Couldn&apos;t load this agent&apos;s stores. They&apos;re listed on the{' '}
          <a href={storageHref}>Storage &amp; Database</a> page.
        </p>
      ) : (
        <>
          <p className="pipeline-section-hint">
            Data stores provisioned for this agent when it deployed. Open one for
            its details, schema and data.
          </p>
          <StoresTable
            stores={stores}
            projectToken={projectToken}
            loading={status === 'loading'}
            emptyState={{
              title: 'No stores',
              description:
                "This agent declares no data store. Stores are declared by an agent's template and provisioned when it deploys.",
            }}
          />
        </>
      )}
    </div>
  );
}

function CustomMcpSection({ uuid, setToast }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error' | 'unavailable'
  const [servers, setServers] = useState([]);
  // The server pending delete-confirmation ({ id, name } | null).
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [busyId, setBusyId] = useState(null); // id currently being deleted

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get(`/workflows/${uuid}/custom-mcp`);
        if (!alive) return;
        setServers(Array.isArray(data?.servers) ? data.servers : []);
        setStatus('ready');
      } catch (err) {
        if (!alive) return;
        // "Route not present on this deployment" = custom-MCP is self-host-only
        // today (cloud CDK doesn't register the routes). API Gateway answers an
        // unknown route with 403 "Missing Authentication Token" (NOT 404), and a
        // CORS/network reject surfaces as no status at all — so treat 404 / 403 /
        // no-status as "unavailable" and hide the section quietly rather than a
        // scary error toast. When cloud gains the routes this GET succeeds and
        // the section appears with no frontend change. A real 5xx still errors.
        if (err.status === 404 || err.status === 403 || err.status == null) { setStatus('unavailable'); return; }
        setStatus('error');
        setToast?.({ show: true, message: `Could not load custom MCP servers: ${err.message}`, color: 'danger' });
      }
    })();
    return () => { alive = false; };
  }, [uuid, setToast]);

  const handleDelete = async (server) => {
    if (busyId) return;
    setBusyId(server.id);
    // Optimistic removal — drop it from the list immediately, restore on failure.
    const prev = servers;
    setServers((list) => list.filter((s) => s.id !== server.id));
    setConfirmTarget(null);
    try {
      await api.delete(`/workflows/${uuid}/custom-mcp/${encodeURIComponent(server.id)}`);
      setToast?.({ show: true, message: `Removed MCP server “${server.name}”.`, color: 'success' });
    } catch (err) {
      setServers(prev); // restore
      setToast?.({ show: true, message: `Could not remove MCP server: ${err.message}`, color: 'danger' });
    } finally {
      setBusyId(null);
    }
  };

  // Self-host-only today: on a deployment without the routes (cloud) the section
  // hides itself entirely rather than showing a broken/unusable panel.
  if (status === 'unavailable') return null;

  return (
    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Custom MCP servers</h3>
      <p className="pipeline-section-hint">
        Remote MCP tool servers attached to this agent. To add one, ask the
        Copilot: <code>add an MCP server https://…</code>. If the server needs
        auth, its token is stored encrypted and injected server-side — never
        shown here and never placed in the agent container.
      </p>

      {status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, color: 'rgba(255,255,255,0.55)', fontSize: 13 }}>
          <IonSpinner name="crescent" style={{ width: 16, height: 16 }} />
          Loading…
        </div>
      )}

      {status === 'error' && (
        <p className="pipeline-section-hint" style={{ color: 'rgba(248,113,113,0.85)', marginTop: 10 }}>
          Status unavailable — reload to try again.
        </p>
      )}

      {status === 'ready' && servers.length === 0 && (
        <div
          style={{
            marginTop: 12, padding: '16px 18px', borderRadius: 10,
            border: '1px dashed rgba(255,255,255,0.16)', background: 'rgba(255,255,255,0.03)',
            color: 'rgba(255,255,255,0.6)', fontSize: 13, lineHeight: 1.55,
          }}
        >
          No custom MCP servers attached yet.{' '}
          <span style={{ color: 'rgba(255,255,255,0.82)' }}>
            Ask the Copilot to add one — e.g. <code>add an MCP server https://…</code>.
          </span>
        </div>
      )}

      {status === 'ready' && servers.length > 0 && (
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {servers.map((s) => (
            <li
              key={s.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '10px 14px', borderRadius: 10,
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                opacity: busyId === s.id ? 0.5 : 1,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ color: '#fff', fontSize: 14, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.name || 'Untitled server'}
                  </span>
                  {s.hasAuth && (
                    <IonIcon
                      icon={lockClosedOutline}
                      title="Authenticated (token stored encrypted)"
                      aria-label="Authenticated"
                      style={{ fontSize: 14, color: 'rgba(120,220,160,0.9)', flexShrink: 0 }}
                    />
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 3 }}>
                  <span
                    title={s.url}
                    style={{
                      fontSize: 12, color: 'rgba(255,255,255,0.5)', fontFamily: 'monospace',
                      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320,
                    }}
                  >
                    {s.url}
                  </span>
                  {s.createdAt && (
                    <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', flexShrink: 0 }}>
                      · added {timeAgo(s.createdAt)}
                    </span>
                  )}
                </div>
              </div>
              {/* PLATFORM-MANAGED entry (`managedBy: 'requires:<as>'`) — the
                  deploy materialised it from the template's own `requires`
                  declaration and re-attaches it on every Update, so a Remove
                  here would only come back (the two-deciders bug). Say where
                  it came from instead. */}
              {typeof s.managedBy === 'string' && s.managedBy.startsWith('requires:') ? (
                <span
                  title="Declared by this agent's template; installed and kept attached by the deploy"
                  style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap', flexShrink: 0 }}
                >
                  installed with this agent
                </span>
              ) : (
              <button
                type="button"
                onClick={() => setConfirmTarget({ id: s.id, name: s.name || 'this server' })}
                disabled={!!busyId}
                aria-label={`Remove MCP server ${s.name || s.id}`}
                title="Remove"
                style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 30, height: 30, flexShrink: 0, borderRadius: 8, cursor: busyId ? 'default' : 'pointer',
                  border: '1px solid rgba(255,255,255,0.14)', background: 'transparent',
                  color: 'rgba(255,255,255,0.6)',
                }}
              >
                <IonIcon icon={closeOutline} style={{ fontSize: 16 }} />
              </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {confirmTarget && (
        <div
          className="pipeline-env-add-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="custom-mcp-confirm-title"
        >
          <div className="pipeline-env-add-modal">
            <header className="pipeline-env-add-header">
              <h3 id="custom-mcp-confirm-title">Remove MCP server?</h3>
            </header>
            <div className="pipeline-env-add-body">
              <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14, lineHeight: 1.55, marginTop: 0 }}>
                This detaches <strong style={{ color: '#fff' }}>{confirmTarget.name}</strong> from
                this agent and deletes any stored auth token. You can re-add it later via the Copilot.
              </p>
            </div>
            <footer className="pipeline-env-add-footer">
              <button
                type="button"
                className="pipeline-env-add-cancel"
                onClick={() => setConfirmTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="pipeline-env-add-save"
                onClick={() => handleDelete(confirmTarget)}
              >
                Remove
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Settings ────────────────────────────────────────────────────────

/**
 * MaxRuntimeSection — per-agent "max run time" control rendered in the
 * Settings tab. Sets the wall-clock ceiling a single run is allowed before
 * the runner force-stops it.
 *
 * Backend contract:
 *   - The workflow row carries `maxRuntimeMinutes` (the explicit per-agent
 *     setting, or null = "use the default"), plus the resolved
 *     `effectiveMaxRuntimeMinutes` and the platform `defaultMaxRuntimeMinutes`
 *     (25) for the empty-field placeholder.
 *   - PATCH /workflows/:uuid { maxRuntimeMinutes: <int|null> } — a number sets
 *     it, null clears it back to the default. Reuses the same PATCH the model /
 *     skills settings use, so save UX (saving spinner → fetchPipeline) matches.
 *
 * Empty field ⇒ send null (revert to default). A value ⇒ validate [1,60] int
 * client-side before saving (the backend is still the authoritative validator).
 */
function MaxRuntimeSection({ pipeline, setToast, onChange }) {
  const stored = Number.isInteger(pipeline?.maxRuntimeMinutes) ? pipeline.maxRuntimeMinutes : null;
  const fallback = Number.isInteger(pipeline?.defaultMaxRuntimeMinutes)
    ? pipeline.defaultMaxRuntimeMinutes
    : 25;
  // Empty string = "no explicit override" (we'll send null). A digit string
  // = the chosen value. Seeded from the stored setting only.
  const [value, setValue] = useState(stored == null ? '' : String(stored));
  const [saving, setSaving] = useState(false);

  // Re-seed when the row changes underneath us (e.g. after a save refetch or
  // navigating between agents).
  useEffect(() => {
    setValue(stored == null ? '' : String(stored));
  }, [stored]);

  const trimmed = value.trim();
  const parsed = trimmed === '' ? null : Number(trimmed);
  const invalid =
    trimmed !== '' &&
    (!Number.isInteger(parsed) || parsed < 1 || parsed > 60);
  // Dirty = the field differs from what's stored (empty maps to "no setting").
  const dirty = (stored == null ? '' : String(stored)) !== trimmed;

  const handleSave = async () => {
    if (saving || invalid || !dirty) return;
    setSaving(true);
    try {
      await api.patch(`/workflows/${pipeline.uuid}`, {
        maxRuntimeMinutes: trimmed === '' ? null : parsed,
      });
      setToast?.({
        show: true,
        message: trimmed === ''
          ? `Max run time cleared — using the default (${fallback} min).`
          : `Max run time set to ${parsed} min.`,
        color: 'success',
      });
      if (typeof onChange === 'function') await onChange();
    } catch (err) {
      const msg = err?.body?.error || err?.body?.message || err?.message || 'Failed to save max run time';
      setToast?.({ show: true, message: msg, color: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Max run time</h3>
      <p className="pipeline-section-hint">
        The longest a single run may take before the agent is stopped. Leave
        blank to use the default ({fallback} min). Allowed range: 1–60 minutes.
        You can also override it per-run from the Trigger dialog.
      </p>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ width: 140 }}>
          <Field
            type="number"
            min={1}
            max={60}
            step={1}
            inputMode="numeric"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
            placeholder={`Default: ${fallback} min`}
            disabled={saving}
            status={invalid ? 'error' : null}
            aria-label="Max run time in minutes"
          />
        </div>
        <PillButton
          loading={saving}
          disabled={invalid || !dirty}
          onClick={handleSave}
        >
          Save
        </PillButton>
      </div>
      {invalid && (
        <p className="pipeline-section-hint" style={{ color: 'rgba(248, 113, 113, 0.85)', margin: '8px 0 0' }}>
          Enter a whole number of minutes between 1 and 60.
        </p>
      )}
    </div>
  );
}

const SettingsTab = ({ pipeline, onDelete, setToast, projectId, workflowType, onChange, chatRuntime = false }) => (
  <div className="pipeline-tab">
    <div className="pipeline-section">
      <h3 className="pipeline-section-title">Repository</h3>
      <p className="pipeline-section-hint">
        Sources are uploaded with each <code>zibby workflow deploy</code>. The current bundle
        was built from version <strong>v{pipeline.version || 0}</strong>.
      </p>
    </div>

    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Skills</h3>
      <p className="pipeline-section-hint">
        Click a row to toggle whether this agent can use that skill. Connected
        integrations and no-connection skills (like codebase memory) both toggle
        here. Required skills stay on; for an either/or pair (e.g. GitHub or
        GitLab) you can switch which one — just keep at least one. Saves instantly.
      </p>
      <IntegrationsConnectList
        workflowUuid={pipeline.uuid}
        selectable={!!pipeline.uuid}
        stored={pipeline.enabledIntegrations}
        setToast={setToast}
        onSaved={onChange}
      />
    </div>

    {pipeline.uuid && (
      <CustomMcpSection uuid={pipeline.uuid} setToast={setToast} />
    )}

    <AgentStoresSection pipeline={pipeline} />

    <MaxRuntimeSection pipeline={pipeline} setToast={setToast} onChange={onChange} />

    {/* The per-agent review @mention name lives in the OVERVIEW MentionStat for
        BOTH gitlab + github review agents (consistent with the self-host UI). The
        old gitlab-only ReviewMentionSection here was a duplicate — removed. */}

    {/* No control-plane access toggle: since the stateless-JWT refactor the
        Copilot's control-plane token is minted PER TURN for the bound account's
        owner (copilot-runtime creds.cjs) — a stored full-account PAT is exactly
        the standing credential that model retired, so the grant switch that
        minted one is gone with it. */}

    {/* Warm agent is a Fargate warm-worker addon ($0.03/hr) — it keeps a hot
        container ready for the next workflow run. A chat agent runs on Lambda
        (cold-start-free at this scale, no Fargate runner), so there's nothing
        to keep warm. Hidden for chat agents; Fargate workflows keep it. */}
    {!chatRuntime && (
      <WarmPoolSection
        setToast={setToast}
        projectId={projectId}
        workflowType={workflowType}
        onChange={onChange}
      />
    )}

    <DangerZone
      label={workflowType}
      hint="Permanently delete this agent. This can't be undone."
      buttonLabel="Delete agent"
      modalTitle={`Delete agent ${workflowType}?`}
      modalWarning={(
        <p style={{ color: 'rgba(255,255,255,0.78)', fontSize: 14, lineHeight: 1.55, marginTop: 0, marginBottom: 12 }}>
          Permanently deletes this agent.{' '}
          <strong style={{ color: '#fff' }}>This cannot be undone.</strong>
        </p>
      )}
      confirmLabel="Delete agent"
      busyLabel="Deleting…"
      onConfirm={onDelete}
    />
  </div>
);

/**
 * WarmPoolSection — account-scoped addon control rendered inside the
 * workflow Settings tab. The warm pool is currently an account-level
 * resource (not per-workflow) — same control surfaces on the Billing
 * page; we mirror it here so users discovering the per-workflow cold
 * start can fix it without a second navigation step.
 *
 * Three render states:
 *   loading  → spinner only
 *   inactive → "Off" + N=1..5 enable buttons
 *   active   → quantity + per-min rate + Disable button
 *
 * 402 on enable → InsufficientCreditsModal, parameterised with the
 * requested quantity / required + balance milli-cents from the response
 * body. Other 4xx → toast.
 */
function WarmPoolSection({ setToast, projectId, workflowType, onChange }) {
  const [status, setStatus] = useState('loading'); // 'loading' | 'inactive' | 'active' | 'error'
  const [quantity, setQuantity] = useState(0);
  // perInstancePerMinuteMilliCents — fetched from the status response so
  // we don't hard-code the rate in the frontend (the backend's
  // pricing-rates.js is the source of truth and supports per-account
  // overrides; baking the number here would silently desync any VIP
  // discount). Falls back to 58 (the published default = $0.06/hr × ~58)
  // ONLY for the brief moment between mount and first response, so the
  // UI doesn't render "$0/hr" before status resolves.
  const [perInstanceRate, setPerInstanceRate] = useState(58);
  // SSOT for the user-selectable counts. Backend echoes its config on
  // the status response (`pricing.quantityOptions`); we keep the static
  // import as the cold-start fallback so the menu renders before the
  // first fetch lands. Adding/removing a count = one edit in
  // backend/src/config/plans.js → here via API.
  const [quantityOptions, setQuantityOptions] = useState(WARM_POOL_QUANTITY_OPTIONS);
  const [busy, setBusy] = useState(false);
  // Insufficient-credits modal state — opened on a 402 from enable.
  const [credsModal, setCredsModal] = useState({
    open: false, requestedQty: 0, required: null, balance: null,
  });
  // Confirm-before-enable modal state — opened on any "Create N workers"
  // click (main button OR dropdown item). Forces a deliberate confirm
  // since enabling kicks off per-minute billing immediately.
  const [confirmEnable, setConfirmEnable] = useState({ open: false, qty: 0 });
  // Disable confirm — styled modal (NOT a native window.confirm), mirroring
  // the enable flow.
  const [confirmDisable, setConfirmDisable] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      // Per-pipeline status — read THIS pipeline's own warm state (the
      // workflow row), not the caller account's pool. The warm-pool resource
      // now lives on the project-owner account, so the bare account-level
      // status would wrongly show "Off" for a warm pipeline.
      const data = await Addons.getWarmPoolStatus({ projectId, workflowType });
      const rate = Number(data?.pricing?.perInstancePerMinuteMilliCents);
      if (Number.isFinite(rate) && rate > 0) setPerInstanceRate(rate);
      const opts = data?.pricing?.quantityOptions;
      if (Array.isArray(opts) && opts.length > 0) {
        setQuantityOptions(opts.filter((n) => Number.isInteger(n) && n > 0));
      }
      if (data?.enabled) {
        setStatus('active');
        setQuantity(Number(data.quantity) || 0);
      } else {
        setStatus('inactive');
        setQuantity(0);
      }
    } catch (err) {
      console.error('[warm-pool] status fetch failed:', err);
      setStatus('error');
    }
  }, [projectId, workflowType]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const enableWarmPool = async (n) => {
    if (busy) return;
    setBusy(true);
    try {
      // Per-pipeline mode: backend writes BOTH workflow.warmEnabled and
      // account.warmPool atomically. Without the workflow identifiers,
      // billing flips on but no runners actually launch — that's the
      // bug this whole call site is here to avoid.
      await Addons.enableWarmPool(n, { projectId, workflowType });
      Credits.fireChangedEvent();
      setToast?.({ show: true, message: `Warm worker enabled with ${n} runner${n > 1 ? 's' : ''}.`, color: 'success' });
      await fetchStatus();
      // Refetch the pipeline row so the header status pill (which reads
      // workflow.warmEnabled) flips "Sleeping" → "Live".
      if (typeof onChange === 'function') await onChange();
    } catch (err) {
      // 402 → dedicated modal with requested-context. The handler's
      // response body has the shape { error, requiredMilliCents,
      // balanceMilliCents, topUpUrl } — pass through.
      if (err?.status === 402) {
        const body = err.body || {};
        setCredsModal({
          open: true,
          requestedQty: n,
          required: Number.isFinite(Number(body.requiredMilliCents)) ? Number(body.requiredMilliCents) : null,
          balance: Number.isFinite(Number(body.balanceMilliCents)) ? Number(body.balanceMilliCents) : null,
        });
      } else {
        const msg = err?.body?.error || err?.body?.message || err?.message || 'Enable failed';
        setToast?.({ show: true, message: msg, color: 'danger' });
      }
    } finally {
      setBusy(false);
    }
  };

  // Confirmation handled by the styled ConfirmModal (same as enable) — this
  // just performs the disable once the user confirms.
  const disableWarmPool = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await Addons.disableWarmPool({ projectId, workflowType });
      Credits.fireChangedEvent();
      setToast?.({ show: true, message: 'Warm worker disabled for this agent.', color: 'success' });
      await fetchStatus();
      if (typeof onChange === 'function') await onChange();
    } catch (err) {
      const msg = err?.body?.error || err?.body?.message || err?.message || 'Disable failed';
      setToast?.({ show: true, message: msg, color: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  // Per-quantity rate string — actual total, not per-worker × N. So
  // 2 workers = "$0.06/hr", not "$0.03/hr × 2". formatRate takes
  // milli-cents-per-minute and renders as "$X/hr" via the shared helper.
  const rateForQty = (n) => formatRate(perInstanceRate * n);

  // Open the confirm modal instead of calling the API directly. The
  // modal's primary CTA invokes enableWarmPool with the chosen quantity.
  const requestEnable = (n) => setConfirmEnable({ open: true, qty: n });

  return (
    <div className="pipeline-section pipeline-section--separated">
      <h3 className="pipeline-section-title">Warm agent</h3>
      <p className="pipeline-section-hint">
        Keep this agent always-warm — skip the ~60s cold-start by running
        pre-warmed runners. Paid feature — per-minute charges begin when
        enabled and stop when disabled.
      </p>

      {status === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <IonSpinner name="dots" />
          <span className="pipeline-section-hint" style={{ margin: 0 }}>Loading…</span>
        </div>
      )}

      {status === 'error' && (
        <p className="pipeline-section-hint" style={{ color: 'rgba(248, 113, 113, 0.85)' }}>
          Could not load warm-agent status. <button
            type="button"
            className="warm-pool-link-btn"
            onClick={fetchStatus}
          >Retry</button>
        </p>
      )}

      {status === 'inactive' && (
        <>
          <p className="pipeline-section-hint">
            Current state: <strong>Off</strong> · Rate when enabled: <strong>{formatRate(perInstanceRate)} per worker</strong>
          </p>
          <SplitButton
            primaryLabel={`Create 1 warm worker (${rateForQty(1)})`}
            primaryIcon={flashOutline}
            onPrimary={() => requestEnable(1)}
            disabled={busy}
            size="lg"
            menuDirection="down"
            menuHeader={{ icon: flashOutline, label: 'Warm agent' }}
            menuHelp="Pick how many pre-warmed runners to keep running. Per-minute charges apply to all of them."
            options={quantityOptions.map((n) => ({
              value: n,
              label: `Create ${n} warm worker${n === 1 ? '' : 's'}`,
              hint: rateForQty(n),
            }))}
            onSelect={(n) => requestEnable(n)}
          />
        </>
      )}

      {status === 'active' && (
        <>
          <p className="pipeline-section-hint" style={{ margin: '0 0 4px' }}>
            Current state: <strong>{quantity} warm worker{quantity > 1 ? 's' : ''}</strong>
          </p>
          <p className="pipeline-section-hint" style={{ margin: '0 0 12px' }}>
            Rate: <strong>{rateForQty(quantity)}</strong>
            {' '}· Per-day estimate: <strong>{formatBalance(perInstanceRate * quantity * 60 * 24)}</strong>
          </p>
          <DangerButton
            icon={trashOutline}
            onClick={() => setConfirmDisable(true)}
            disabled={busy}
          >
            Disable warm agent
          </DangerButton>
        </>
      )}

      <InsufficientCreditsModal
        open={credsModal.open}
        onClose={() => setCredsModal((s) => ({ ...s, open: false }))}
        title="Add credits to enable warm pool"
        ariaLabel="Insufficient credits to enable warm pool"
        actionLabel={`enable warm pool with ${credsModal.requestedQty} worker${credsModal.requestedQty > 1 ? 's' : ''}`}
        rateLabel={rateForQty(credsModal.requestedQty || 1)}
        minRuntimeLabel="1 day"
        minRequiredMilliCents={credsModal.required}
        balanceMilliCents={credsModal.balance}
        primaryLabel="Go to Billing"
        primaryTo="/billing"
        secondaryLabel="Cancel"
      />

      {/* Confirm modal — fires before the API call so the user has a
          chance to back out. Reuses the existing ConfirmModal atom that
          AppsMarketplaceSection / billing flow already use. */}
      <ConfirmModal
        open={confirmEnable.open}
        onClose={() => setConfirmEnable({ open: false, qty: 0 })}
        title="Enable warm agent?"
        ariaLabel="Confirm warm agent enable"
        primaryLabel={`Yes, create ${confirmEnable.qty} warm worker${confirmEnable.qty > 1 ? 's' : ''}`}
        onPrimary={() => {
          const n = confirmEnable.qty;
          setConfirmEnable({ open: false, qty: 0 });
          enableWarmPool(n);
        }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, marginBottom: 12 }}>
          You're about to enable warm agent with{' '}
          <strong style={{ color: '#fff' }}>
            {confirmEnable.qty} warm worker{confirmEnable.qty > 1 ? 's' : ''}
          </strong>.
        </p>
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          Per-minute charges begin immediately at{' '}
          <strong style={{ color: '#fff' }}>{rateForQty(confirmEnable.qty)}</strong>
          {' '}(~{formatBalance(perInstanceRate * (confirmEnable.qty || 1) * 60 * 24)} per day).
          {' '}You can disable any time on this page to stop charges.
        </p>
      </ConfirmModal>

      {/* Disable confirm — styled modal (was a native window.confirm),
          mirroring the enable flow above. */}
      <ConfirmModal
        open={confirmDisable}
        onClose={() => setConfirmDisable(false)}
        title="Disable warm agent?"
        ariaLabel="Confirm warm agent disable"
        primaryLabel="Yes, disable"
        onPrimary={() => { setConfirmDisable(false); disableWarmPool(); }}
        secondaryLabel="Cancel"
      >
        <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.55, margin: 0 }}>
          The warm pool resizes (or shuts down if no other warm agents remain)
          and <strong style={{ color: '#fff' }}>per-minute charges stop</strong>.
          Existing runners drain on the next reconcile.
        </p>
      </ConfirmModal>
    </div>
  );
}

// ─── Stores ──────────────────────────────────────────────────────────
//
// Deployed agents can own data stores. On deploy the platform auto-creates
// each one and records the mapping as an env var on the workflow:
//   ZIBBY_STORE__<name> = <storeId>      (one per store)
// Rather than leave those buried in the raw ENV list, the Stores section
// surfaces them: the <name> is a read-only code contract baked into the
// template; the <storeId> is editable so you can REPOINT a store (e.g.
// share another agent's store). Clearing the id reverts to the
// auto-provisioned store. Saving writes back through the SAME env-update
// API the Env tab uses (PATCH /workflows/{uuid}/env/{key}) — no new
// endpoint.
//
// Store ids are non-secret identifiers, but they live in the env store
// where the list response masks values. So we reveal each one (same
// authorized endpoint the Env tab's eye button uses) to seed the
// editable inputs.

const STORE_ENV_PREFIX = 'ZIBBY_STORE__';

// Loose client-side check; the server owns the canonical rules. Empty =
// revert to the auto-provisioned store; otherwise it must look like a
// store id.
const isValidStoreId = (v) => v === '' || v.startsWith('store_');

const StoresSection = ({ uuid, entries, setToast }) => {
  // entries: [{ key, name, description? }] for each ZIBBY_STORE__* var.
  // `saved[key]` = last-persisted id; `drafts[key]` = current input text
  // (presence of both drives the dirty check).
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loadingVals, setLoadingVals] = useState(true);
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingVals(true);
      const next = {};
      await Promise.all(entries.map(async ({ key }) => {
        try {
          const data = await api.get(`/workflows/${uuid}/env/${encodeURIComponent(key)}/reveal`);
          next[key] = data?.value ?? '';
        } catch {
          // A reveal failure shouldn't blank the field silently as if the
          // store were unset — leave it empty but the input stays editable.
          next[key] = '';
        }
      }));
      if (cancelled) return;
      setSaved(next);
      setDrafts(next);
      setLoadingVals(false);
    })();
    return () => { cancelled = true; };
  }, [uuid, entries]);

  const save = useCallback(async (key) => {
    const value = (drafts[key] ?? '').trim();
    if (!isValidStoreId(value)) {
      setToast?.({ show: true, message: 'Store id must be empty or start with "store_"', color: 'danger' });
      return;
    }
    setSavingKey(key);
    try {
      await api.patch(`/workflows/${uuid}/env/${encodeURIComponent(key)}`, { value });
      setSaved((c) => ({ ...c, [key]: value }));
      setDrafts((c) => ({ ...c, [key]: value }));
      setToast?.({
        show: true,
        message: value ? `Repointed store ${key.slice(STORE_ENV_PREFIX.length)}` : `Reset store ${key.slice(STORE_ENV_PREFIX.length)} to auto`,
        color: 'success',
      });
    } catch (err) {
      setToast?.({ show: true, message: `Save failed: ${err.message}`, color: 'danger' });
    } finally {
      setSavingKey(null);
    }
  }, [uuid, drafts, setToast]);

  if (!entries.length) return null;

  return (
    <div className="pipeline-stores">
      <h3 className="pipeline-stores-title">
        <IonIcon icon={cubeOutline} aria-hidden="true" />
        Stores
      </h3>
      <p className="pipeline-section-hint pipeline-stores-hint">
        Data stores this agent owns. The name is fixed by the template; edit
        the id to point at a different store, or clear it to revert to the
        auto-provisioned one.
      </p>
      <div className="pipeline-stores-list">
        {entries.map(({ key, name, description }) => {
          const val = drafts[key] ?? '';
          const dirty = val !== (saved[key] ?? '');
          const valid = isValidStoreId(val.trim());
          const saving = savingKey === key;
          return (
            <div className="pipeline-store-row" key={key}>
              <div className="pipeline-store-meta">
                <code className="pipeline-store-name">{name}</code>
                {description ? <span className="pipeline-store-desc">{description}</span> : null}
              </div>
              <input
                type="text"
                className={`pipeline-env-add-input pipeline-store-input${val && !valid ? ' pipeline-store-input-invalid' : ''}`}
                value={val}
                placeholder="store_… (empty = auto-provisioned)"
                onChange={(e) => setDrafts((c) => ({ ...c, [key]: e.target.value }))}
                spellCheck={false}
                disabled={loadingVals || saving}
              />
              <IonButton
                size="small"
                onClick={() => save(key)}
                disabled={!dirty || !valid || saving || loadingVals}
              >
                {saving ? 'Saving…' : 'Save'}
              </IonButton>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Env ─────────────────────────────────────────────────────────────

// ─── Artifacts ───────────────────────────────────────────────────────
// Lists the agent's published artifacts from its kv-memory index
// ("<workflowType>:artifact:<id>"), FOLLOWING the recall-prefix cursor until
// exhausted: the backend pages at 25 rows in SK (artifact-uuid) order — an
// ARBITRARY 25, not the newest — so a single page silently dropped a random
// subset past 25 published pages. Bounded at 40 pages (1000 artifacts) with an
// honest "more exist" note instead of silent truncation (no-silent-caps).
// Sorted newest-first after the full fetch. Mirrors the self-host tab exactly.
// Type chip label for an artifact index row. contentType class wins, then
// kind, then format; the fallback is 'Page'. Old index rows carry none of
// contentType/format — they resolve through kind or the fallback.
// MIRRORED, NOT SHARED: the self-host dashboard's artifacts tab in
// selfhosted/ui/index.html carries the same derivation — the two UIs share
// zero code by design and must be changed in pairs.
// Size only where the index row recorded it (binary/text/new-doc publishes);
// old rows have no `bytes` and show nothing. MIRRORED, NOT SHARED: the
// self-host twin lives in selfhosted/ui/index.html (.art-size in the
// Artifacts tab rows).
const artifactSizeLabel = (rec) => {
  const n = rec && typeof rec.bytes === 'number' && rec.bytes >= 0 ? rec.bytes : null;
  if (n === null) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const artifactTypeLabel = (rec) => {
  const ct = String(rec.contentType || '');
  if (ct.startsWith('video/')) return 'Video';
  if (ct.startsWith('audio/')) return 'Audio';
  if (ct.startsWith('image/')) return 'Image';
  if (ct === 'application/pdf') return 'PDF';
  if (ct === 'application/json') return 'JSON';
  if (ct === 'text/csv') return 'CSV';
  if (ct === 'text/plain') {
    // Name the LANGUAGE when the title's extension identifies one
    // ("JavaScript", "Python", …) — "Code" only when it doesn't.
    const li = artifactLangInfo(String(rec.title || ''), ct);
    if (li.name !== 'Text') return li.name;
    return rec.kind === 'code' ? 'Code' : 'Text';
  }
  if (ct === 'application/zip') return 'ZIP';
  if (ct) return 'File';
  const kind = String(rec.kind || '').trim();
  if (kind) {
    const label = kind.charAt(0).toUpperCase() + kind.slice(1);
    return label.length > 12 ? `${label.slice(0, 12)}…` : label;
  }
  return rec.format === 'markdown' ? 'Markdown' : 'Page';
};

// Endless pagination — on the RENDER side only. The fetch must still drain
// every backend page (SK order is arbitrary; newest-first exists only after
// the full sort), but a several-hundred-row DOM helps nobody: render 50, and
// a 1px sentinel + IntersectionObserver reveals 50 more each time the bottom
// approaches. Data is already in memory, so revealing is instant.
// MIRRORED, NOT SHARED with selfhosted/ui/index.html renderArtifactsTab.
const ART_RENDER_PAGE = 50;

const ArtifactsTab = ({ projectId, workflowType }) => {
  const [items, setItems] = useState(null); // null = loading
  const [capped, setCapped] = useState(false);
  const [error, setError] = useState('');
  const [visibleCount, setVisibleCount] = useState(ART_RENDER_PAGE);
  const sentinelRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !items || visibleCount >= items.length) return undefined;
    if (!('IntersectionObserver' in window)) {
      // No IntersectionObserver → exactly the pre-pagination behaviour.
      setVisibleCount(items.length);
      return undefined;
    }
    const obs = new IntersectionObserver((entries) => {
      if (entries.some((en) => en.isIntersecting)) {
        setVisibleCount((c) => Math.min(c + ART_RENDER_PAGE, items.length));
      }
    }, { rootMargin: '600px 0px' });
    obs.observe(node);
    return () => obs.disconnect();
  }, [items, visibleCount]);

  useEffect(() => {
    let dead = false;
    setVisibleCount(ART_RENDER_PAGE); // a new agent's list starts back at page one
    const PAGE_CAP = 40;
    const all = [];
    const fetchPage = (cursor, pageNo) => api
      .post('/credits/review-memory', {
        op: 'recall-prefix',
        scopePrefix: `${workflowType}:artifact:`,
        projectId,
        ...(cursor ? { cursor } : {}),
      })
      .then(({ data }) => {
        if (dead) return;
        (data && data.memories ? data.memories : []).forEach((m) => {
          try {
            const rec = JSON.parse(m.content);
            if (rec && rec.id) all.push(rec);
          } catch (e) { /* skip malformed index rows */ }
        });
        if (data && data.nextCursor && pageNo < PAGE_CAP) return fetchPage(data.nextCursor, pageNo + 1);
        if (data && data.nextCursor) setCapped(true);
        all.sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        setItems(all.slice());
      });
    fetchPage(null, 1).catch((e) => {
      if (dead) return;
      setError(e?.response?.data?.error || e.message || 'Failed to load artifacts');
      setItems([]);
    });
    return () => { dead = true; };
  }, [projectId, workflowType]);

  if (items === null) return <div className="pipeline-detail-loading">Loading artifacts…</div>;
  if (error) return <div className="pipeline-detail-error">{error}</div>;
  if (!items.length) {
    return (
      <div className="pipeline-detail-empty">
        <p>No artifacts yet.</p>
        <p style={{ color: 'var(--text-secondary, #8b8b93)', fontSize: 13 }}>
          When this agent publishes a shareable page (a report, plan, dashboard, …) it will appear here.
        </p>
      </div>
    );
  }
  return (
    <div className="pipeline-stores-list">
      {items.slice(0, visibleCount).map((r) => (
        <div
          key={r.id}
          className="pipeline-store-row"
          role="link"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
          title={r.summary || r.title || ''}
          onClick={() => navigate(`/artifact/${encodeURIComponent(r.id)}`)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/artifact/${encodeURIComponent(r.id)}`); }}
        >
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {r.title || 'Untitled'}
          </span>
          <span className="pipeline-artifact-chip">{artifactTypeLabel(r)}</span>
          {artifactSizeLabel(r) && (
            <span style={{ color: 'var(--text-secondary, #8b8b93)', fontSize: 12, marginLeft: 12, flexShrink: 0 }}>
              {artifactSizeLabel(r)}
            </span>
          )}
          <span style={{ color: 'var(--text-secondary, #8b8b93)', fontSize: 12, marginLeft: 12, flexShrink: 0 }}>
            {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
          </span>
          <span aria-hidden style={{ marginLeft: 8, color: 'var(--text-secondary, #8b8b93)' }}>›</span>
        </div>
      ))}
      {visibleCount < items.length && <div ref={sentinelRef} style={{ height: 1 }} aria-hidden />}
      {capped && (
        <div style={{ color: 'var(--text-secondary, #8b8b93)', fontSize: 12, padding: '8px 4px' }}>
          Showing the first {items.length} artifacts — more exist beyond the page cap.
        </div>
      )}
    </div>
  );
};

const EnvTab = ({ uuid, setToast }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [addKey, setAddKey] = useState('');
  const [addValue, setAddValue] = useState('');
  const [adding, setAdding] = useState(false);
  // Per-key reveal state. `revealed[key]` holds the decrypted plaintext once
  // fetched (presence = shown); `revealing[key]` is the in-flight flag.
  const [revealed, setRevealed] = useState({});
  const [revealing, setRevealing] = useState({});

  const fetchKeys = useCallback(async () => {
    if (!uuid) { setLoading(false); return; }
    setLoading(true);
    try {
      // Server returns `{ keys: [...], entries: [{key,lastFour}] }`.
      // `entries` is preferred (carries the masked suffix); fall back to
      // `keys` for older API responses.
      const data = await api.get(`/workflows/${uuid}/env`);
      const list = Array.isArray(data?.entries) ? data.entries
                : Array.isArray(data?.keys)    ? data.keys
                : Array.isArray(data)          ? data
                : [];
      setKeys(list);
    } catch (err) {
      setToast?.({ show: true, message: `Could not load env: ${err.message}`, color: 'danger' });
    } finally {
      setLoading(false);
    }
  }, [uuid, setToast]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  // Reveal / hide a single value. Hide is local (drop the cached plaintext);
  // reveal hits the authorized decrypt endpoint and caches the result.
  const toggleReveal = useCallback(async (key) => {
    if (key in revealed) {
      setRevealed((c) => { const n = { ...c }; delete n[key]; return n; });
      return;
    }
    setRevealing((c) => ({ ...c, [key]: true }));
    try {
      const data = await api.get(`/workflows/${uuid}/env/${encodeURIComponent(key)}/reveal`);
      setRevealed((c) => ({ ...c, [key]: data?.value ?? '' }));
    } catch (err) {
      setToast?.({ show: true, message: `Reveal failed: ${err.message}`, color: 'danger' });
    } finally {
      setRevealing((c) => { const n = { ...c }; delete n[key]; return n; });
    }
  }, [uuid, revealed, setToast]);

  // Submit handler for the Add modal. Server-side validation owns the
  // canonical rules (key shape, length, reserved-prefix list) — the
  // client just gates on "non-empty key" so the user gets a friendly
  // error instead of a 400 on whitespace-only input. Everything else
  // surfaces through the toast on failure.
  const submitAdd = useCallback(async () => {
    const k = addKey.trim();
    const v = addValue;
    if (!k) {
      setToast?.({ show: true, message: 'Key is required', color: 'danger' });
      return;
    }
    setAdding(true);
    try {
      await api.patch(`/workflows/${uuid}/env/${encodeURIComponent(k)}`, { value: v });
      setToast?.({ show: true, message: `Set ${k}`, color: 'success' });
      setAddOpen(false);
      setAddKey('');
      setAddValue('');
      fetchKeys();
    } catch (err) {
      setToast?.({ show: true, message: `Set failed: ${err.message}`, color: 'danger' });
    } finally {
      setAdding(false);
    }
  }, [uuid, addKey, addValue, fetchKeys, setToast]);

  const allRows = keys.map((k) => (typeof k === 'string' ? { key: k, lastFour: '' } : k));
  // Pull the ZIBBY_STORE__* entries out into the Stores section so they
  // don't also show up as raw env vars (no double-listing). `name` is the
  // suffix after the prefix — the read-only code contract from the template.
  const storeEntries = useMemo(
    () => allRows
      .filter((r) => r.key.startsWith(STORE_ENV_PREFIX) && r.key.length > STORE_ENV_PREFIX.length)
      .map((r) => ({ key: r.key, name: r.key.slice(STORE_ENV_PREFIX.length) })),
    // allRows is derived from `keys`; key on `keys` so the memo (and the
    // child's reveal effect) only re-run when the key set actually changes.
    [keys], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const rows = allRows.filter((r) => !r.key.startsWith(STORE_ENV_PREFIX));
  const columns = [
    { header: 'KEY',   key: 'key',   render: (r) => <code className="pipeline-env-key">{r.key}</code> },
    {
      header: 'VALUE',
      key: 'value',
      render: (r) => {
        const isRevealed = r.key in revealed;
        const isRevealing = !!revealing[r.key];
        return (
          <span className="pipeline-env-mask">
            {isRevealed ? (
              <code className="pipeline-env-value-plain">{revealed[r.key] || '(empty)'}</code>
            ) : (
              <>
                <span className="pipeline-env-mask-dots" aria-hidden="true">
                  {'•'.repeat(24)}
                </span>
                {r.lastFour ? <span className="pipeline-env-mask-suffix">{r.lastFour}</span> : null}
              </>
            )}
            <button
              type="button"
              className="pipeline-env-reveal-btn"
              onClick={() => toggleReveal(r.key)}
              disabled={isRevealing}
              title={isRevealed ? 'Hide value' : 'Reveal value'}
              aria-label={isRevealed ? 'Hide value' : 'Reveal value'}
            >
              <IonIcon icon={isRevealed ? eyeOffOutline : eyeOutline} />
            </button>
          </span>
        );
      },
    },
  ];

  // Adding actions for two reasons:
  //   1. Delete is genuinely useful (env API supports DELETE /workflows/{uuid}/env/{key})
  //   2. Without an actions column, DataTable's `th:last-child` CSS rule
  //      (`width: 48px; text-align: center`) hijacks the VALUE column,
  //      squeezing the masked dots into a 48px box and wrapping them.
  const actions = [
    {
      label: 'Delete key',
      icon: trashOutline,
      danger: true,
      onClick: async (row) => {
        if (!window.confirm(`Delete env var "${row.key}"? You'll need to re-set it from the CLI.`)) return;
        try {
          await api.delete(`/workflows/${uuid}/env/${encodeURIComponent(row.key)}`);
          setToast?.({ show: true, message: `Deleted ${row.key}`, color: 'success' });
          fetchKeys();
        } catch (err) {
          setToast?.({ show: true, message: `Delete failed: ${err.message}`, color: 'danger' });
        }
      },
    },
  ];

  return (
    <div className="pipeline-tab">
      {!loading && storeEntries.length > 0 && (
        <StoresSection uuid={uuid} entries={storeEntries} setToast={setToast} />
      )}
      <div className="pipeline-env-header">
        <p className="pipeline-section-hint">
          Environment variables for this agent. Values are write-only — also settable via{' '}
          <code>zibby workflow env set {`<KEY>`} {`<value>`} --workflow {uuid}</code>.
        </p>
        {/* Header Add button: only show when there's at least one row.
            The empty-state card has its own centered Add button (the
            canonical "no data → big primary CTA" pattern from the
            Projects page), so rendering the header button too gave us
            doubled affordances overlapping the same skeleton lines
            DataTable still paints under the empty card. */}
        {!loading && rows.length > 0 && (
          <PillButton className="pill-button--sm" onClick={() => setAddOpen(true)}>
            <IonIcon slot="start" icon={addOutline} />
            Add
          </PillButton>
        )}
      </div>
      <DataTable
        data={rows}
        columns={columns}
        loading={loading}
        rowKey="key"
        actions={actions}
        emptyState={{
          title: 'No env vars set',
          description: 'Click Add to set the first one, or use the CLI: zibby workflow env set <KEY> <value>',
          actionLabel: 'Add',
          onAction: () => setAddOpen(true),
        }}
      />
      {addOpen && (
        <div
          className="pipeline-env-add-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pipeline-env-add-title"
          onClick={(e) => { if (e.target === e.currentTarget) setAddOpen(false); }}
        >
          <div className="pipeline-env-add-modal">
            <header className="pipeline-env-add-header">
              <h3 id="pipeline-env-add-title">Add environment variable</h3>
              <button
                type="button"
                className="pipeline-env-add-close"
                aria-label="Close"
                onClick={() => setAddOpen(false)}
              >
                <IonIcon icon={closeOutline} />
              </button>
            </header>
            <div className="pipeline-env-add-body">
              <label className="pipeline-env-add-field">
                <span>Key</span>
                <input
                  type="text"
                  className="pipeline-env-add-input"
                  placeholder="SLACK_CHANNEL"
                  value={addKey}
                  onChange={(e) => setAddKey(e.target.value)}
                  // Letters / digits / underscore is the env-var convention.
                  // Strip invalid chars on input so the user can't accidentally
                  // type lowercase + dash and only learn on submit. The server
                  // also validates, so any drift in the rules surfaces there.
                  onBlur={(e) => setAddKey(e.target.value.trim())}
                  spellCheck={false}
                  autoFocus
                />
              </label>
              <label className="pipeline-env-add-field">
                <span>Value</span>
                <textarea
                  className="pipeline-env-add-textarea"
                  placeholder="#alerts-prod"
                  value={addValue}
                  onChange={(e) => setAddValue(e.target.value)}
                  rows={3}
                  spellCheck={false}
                />
              </label>
              <p className="pipeline-env-add-hint">
                Values are write-only — once saved, the server never returns
                them. To rotate, set the same key again.
              </p>
            </div>
            <footer className="pipeline-env-add-footer">
              <button
                type="button"
                className="pipeline-env-add-cancel"
                onClick={() => setAddOpen(false)}
                disabled={adding}
              >
                Cancel
              </button>
              <IonButton onClick={submitAdd} disabled={adding || !addKey.trim()}>
                {adding ? 'Saving…' : 'Save'}
              </IonButton>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Logs tab ────────────────────────────────────────────────────────
//
// Paginated viewer for the workflow's CONTAINER logs (the `/ecs/zibby-
// agent-<stage>` execution log group — includes the warm-runner "warming
// up" lines as well as per-run output), served by the dedicated
// workflow-logs API (see services/workflowLogs.js). Mirrors the apps'
// LogsTab UX in AppDetailPage.js: a monospace buffer, infinite-scroll to
// load OLDER pages (prepend + restore scroll), and a live-tail poll while
// any run is executing.
//
// Shape differences from the apps LogsTab that drive this implementation:
//   - getWorkflowLogs returns `lines` as objects { timestamp, message,
//     jobId } (NOT pre-formatted "<iso>  <msg>" strings), so we render
//     timestamp + message ourselves.
//   - Paging is a TIME-BOUNDED tail, NOT a forward CloudWatch cursor (that
//     cursor walked the wrong direction — see BUG B). Initial/live fetch
//     omits `before` → the NEWEST window (chronological, newest at bottom).
//     Scroll-up passes `before=<oldest-loaded-ts>` → the page of OLDER
//     events, which we PREPEND. `hasOlder` (boolean) says whether scrolling
//     further up yields still-older events.
//   - `hasRunning` gates the live-tail poll: while a run is executing we
//     refresh the newest page every 5s; once nothing is running we stop.
//   - `message` is surfaced as the empty state when there are no lines
//     yet (e.g. "No tasks have started yet").
//
// The `status` prop is the pipeline's bundleStatus. We DON'T hard-gate
// "no logs" on a sleeping/stopped bundle — workflow container logs persist
// in CloudWatch across sleeps, so the empty state shows only when the API
// genuinely returns zero lines.

const WORKFLOW_LOGS_POLL_MS = 5000;
const WORKFLOW_LOGS_PAGE = 200;

function formatWorkflowLogTime(ts) {
  if (ts == null) return '';
  try {
    // CloudWatch event timestamps are epoch MILLISECONDS. Coerce to a number
    // so a numeric STRING ("1749...") doesn't get mis-parsed by Date's
    // string path (which would yield UTC/Invalid). toLocaleTimeString then
    // renders in the BROWSER's local timezone.
    const ms = typeof ts === 'number' ? ts : Number(ts);
    const d = new Date(Number.isFinite(ms) ? ms : ts);
    if (Number.isNaN(d.getTime())) return '';
    // Include the DATE (browser-local), not just the time. The Logs tab can
    // interleave a days-old execution with today's warm-runner output; with a
    // time-only gutter a "05-31 11:49" log looks like a FUTURE "11:49" today.
    const date = d.toLocaleDateString(undefined, { month: '2-digit', day: '2-digit' });
    const time = d.toLocaleTimeString(undefined, {
      hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    return `${date} ${time}.${String(d.getMilliseconds()).padStart(3, '0')}`;
  } catch {
    return '';
  }
}

// Stable key for a log line — CloudWatch can return overlapping events
// across polls, so key on (timestamp, jobId, message) rather than index
// to keep React reconciliation honest when we prepend older pages.
function workflowLogKey(line, i) {
  return `${line.timestamp || ''}:${line.jobId || ''}:${i}`;
}

/**
 * WorkflowLogsTab — dispatches the Logs tab body by runtime.
 *
 *   chat agent  → ChatRuntimeLogsPanel: a message + deep link to the Lambda
 *                 runtime's CloudWatch log group. (Empty Fargate execution
 *                 logs would be misleading for a Lambda chat agent.)
 *   Fargate wf  → FargateWorkflowLogsTab: the existing container-log viewer.
 *
 * Split into two components (not a branch inside one) so the Fargate viewer's
 * polling hooks never run for a chat agent — Rules of Hooks stay satisfied
 * because each branch is its own component with its own fixed hook order.
 */
const WorkflowLogsTab = ({ projectId, workflowType, status, chatRuntime = false }) => {
  if (chatRuntime) {
    return <ChatRuntimeLogsPanel projectId={projectId} workflowType={workflowType} />;
  }
  // `status` (bundleStatus) is intentionally not forwarded — the Fargate logs
  // viewer gates polling on the API's `hasRunning`, not bundleStatus.
  void status;
  return <FargateWorkflowLogsTab projectId={projectId} workflowType={workflowType} />;
};

// Live-poll cadence for the chat runtime tail. The runtime is a shared Lambda;
// 4s keeps the dashboard close to real-time without hammering FilterLogEvents.
const CHAT_RUNTIME_POLL_MS = 4000;
const CHAT_RUNTIME_PAGE = 300;

// Defense-in-depth client sanitizer for a chat-runtime log message. The backend
// curates these lines server-side (handlers/workflow-logs.js curateChatLine —
// internal `mode:*` mechanics + raw CLI/ANSI noise are dropped before they're
// ever returned), so what arrives here is already clean, user-meaningful text.
// We still strip any stray ANSI escape / control chars and normalize carriage
// returns here so that even a historical or edge line renders as readable text
// rather than a garbled fragment — while KEEPING real newlines so a multi-line
// reply shows in full (the `.wf-logs-body` CSS wraps + preserves them).
// eslint-disable-next-line no-control-regex
const CHAT_LOG_ANSI_RE = /[\u001b\u009b][[()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-PR-TZcf-nq-uy=><~]/g;
// eslint-disable-next-line no-control-regex
const CHAT_LOG_CTRL_RE = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f]/g;
function sanitizeChatLogMessage(message) {
  return String(message == null ? '' : message)
    .replace(CHAT_LOG_ANSI_RE, '')
    .replace(/\r\n?/g, '\n')   // normalize CR / CRLF → LF (keep line breaks)
    .replace(CHAT_LOG_CTRL_RE, ''); // drop remaining non-printable control chars
}

// Stable key for a chat-runtime log line. The runtime tail has no per-line jobId
// (it's a single shared Lambda), so key on (timestamp, message-prefix, index)
// to keep React reconciliation honest as we append new polled lines.
function chatRuntimeLogKey(line, i) {
  return `${line.timestamp || ''}:${i}`;
}

// ── Silent-gap hints inside a turn ──────────────────────────────────────────
// WHY THIS EXISTS: the panel prints one line when the model FINISHES emitting a
// tool call and another when that call's result returns — and NOTHING at all
// while the model is generating the NEXT call's arguments. So a tool that
// answered in 2ms, followed by 86 seconds of black screen, reads as "that tool
// hung". Verified example from a real box:
//
//   04:39:05.608  Used tool: browser_resize {"width":1100,"height":1600}
//   04:39:05.610    ↳ browser_resize ok · 125 chars      ← returned in 2ms
//   ──────────────── 86.270s, not one line ────────────────
//   04:40:31.881  Used tool: browser_set_content {"html":"<!doctype html>…
//
// Nothing hung; the model was emitting a 22,000-character HTML document as a
// tool ARGUMENT. That display cost the founder time twice and produced a formal
// "browser_resize hangs" investigation. So a gap longer than CHAT_LOG_GAP_MS
// gets one muted row NAMING the silence. It is derived purely from the
// `timestamp` every line already carries — no new event type, no engine change
// — which means it also explains HISTORICAL logs, where most of its value is.
//
// MIRRORED, NOT SHARED: the self-host dashboard renders the same feed via
// chatLogGapHint() in selfhosted/ui/index.html. The two UIs share zero code by
// design (CLAUDE.md "TWO SEPARATE UIs") — change one, change the other.
const CHAT_LOG_GAP_MS = 10000;          // under 10s, silence is unremarkable
const CHAT_LOG_GAP_MINUTES_MS = 120000; // at/over 2min, read as "3m 20s"

// What KIND of turn event a curated line is. It reads the RENDERED TEXT because
// that is all the client is given: the backend curates `turn:tool` → "Used tool:
// <name> <input>", `turn:tool-result` → "  ↳ <name> ok · <n> chars" / "❌ <name>
// failed: …", and the answer → "Replied: …" (backend/src/handlers/
// workflow-logs.js curateChatLine).
function chatLogLineKind(msg) {
  const s = String(msg == null ? '' : msg);
  if (/^Used tool: /.test(s)) return 'tool-call';
  if (/^\s*↳ /.test(s) || /^❌ .+ failed/.test(s)) return 'tool-result';
  if (/^Replied: /.test(s) || /^No reply\b/.test(s)) return 'reply';
  return 'other';
}

function chatLogGapText(ms) {
  if (ms < CHAT_LOG_GAP_MINUTES_MS) return `${(ms / 1000).toFixed(1)}s`;
  const total = Math.round(ms / 1000);
  return `${Math.floor(total / 60)}m ${String(total % 60).padStart(2, '0')}s`;
}

// The hint for the gap BETWEEN two consecutive lines, or null when the silence
// is short or we cannot name it honestly. The two statements are deliberately
// DIFFERENT sentences — conflating them IS the bug this fixes:
//   result → next call : the MODEL was writing the next call's arguments
//   call   → its result: the TOOL itself was slow
// Anything else (a turn boundary, a header rule, a question) is left silent
// rather than guessed at.
function chatLogGapHint(prev, cur) {
  if (!prev || !cur) return null;
  const a = Number(prev.timestamp);
  const b = Number(cur.timestamp);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  const gap = b - a;
  if (!(gap >= CHAT_LOG_GAP_MS)) return null;
  const from = chatLogLineKind(prev.message);
  const to = chatLogLineKind(cur.message);
  let what = null;
  if (from === 'tool-result' && to === 'tool-call') what = 'model generating the next call';
  else if (from === 'tool-result' && to === 'reply') what = 'model generating the reply';
  else if (from === 'tool-call' && to === 'tool-result') what = 'tool running';
  if (!what) return null;
  return `⏳ ${chatLogGapText(gap)} — ${what}`;
}

/**
 * ChatRuntimeLogsPanel — the Logs tab for a Lambda-hosted chat agent.
 *
 * The chat runtime's logs live in a SHARED CloudWatch log group
 * (`/aws/lambda/zibby-copilot-runtime`, ap-southeast-2), not in this workflow's
 * Fargate execution log group. We tail this agent's slice of that group via the
 * tenant-filtered backend endpoint (GET /chat-logs/{projectId}?workflow=…&since=…,
 * see WorkflowLogs.getChatRuntimeLogs / handlers/workflow-logs.js
 * getChatRuntimeLogsHandler) — the backend resolves THIS agent's workflow UUID
 * from the (authorized) projectId+workflowType and filters server-side to its
 * `[tenant:<uuid>]`-stamped lines, so the client can never see another tenant.
 *
 * Polling model: a bounded newest-window fetch on mount, then incremental
 * `since`-cursor polls every 4s. New lines are APPENDED chronologically and the
 * cursor advances from the response's `nextSince`. Auto-scroll sticks to the
 * bottom while the user is anchored there (so live lines stream into view); if
 * they scroll up to read history we freeze the viewport. The interval is cleared
 * on unmount. (No raw AWS-console deep-link is exposed — tenants can't access our
 * CloudWatch, and the curated feed is the whole tenant-facing surface.)
 */
const ChatRuntimeLogsPanel = ({ projectId, workflowType }) => {
  // Flat, chronological buffer of { timestamp, message }. Appended to on each
  // poll; the `since` cursor (sinceRef) advances from the response's nextSince
  // so each poll only pulls strictly-newer events.
  const [lines, setLines] = useState([]);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);     // a poll is actively scheduled
  const [hasLoaded, setHasLoaded] = useState(false); // first fetch resolved

  const bodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  const sinceRef = useRef(null);  // last-seen event ts (epoch ms); poll cursor

  // Initial load + incremental live-tail poll. The first fetch omits `since`
  // (bounded newest window); subsequent ticks send the advancing cursor and
  // APPEND only strictly-newer lines. Interval cleared on unmount.
  useEffect(() => {
    if (!projectId || !workflowType) return undefined;
    let cancelled = false;
    let timer = null;

    const poll = async () => {
      // Snapshot sticky-bottom BEFORE we mutate the buffer so the auto-scroll
      // effect knows whether to follow the tail or leave the user put.
      const node = bodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current =
          (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      try {
        const data = await WorkflowLogs.getChatRuntimeLogs(projectId, workflowType, {
          limit: CHAT_RUNTIME_PAGE,
          // Omit on the very first fetch (sinceRef null) for the newest window;
          // thereafter send the advancing cursor for incremental polling.
          since: sinceRef.current,
        });
        if (cancelled) return;
        const fresh = Array.isArray(data?.lines) ? data.lines : [];
        if (fresh.length > 0) {
          // Guard against any boundary overlap (since is +1 server-side, but
          // belt-and-suspenders keeps the buffer strictly increasing).
          const prevSince = sinceRef.current;
          const strictlyNew = prevSince == null
            ? fresh
            : fresh.filter((l) => l.timestamp > prevSince);
          if (strictlyNew.length > 0) {
            setLines((prev) => [...prev, ...strictlyNew]);
          }
        }
        // Advance the cursor from nextSince (newest event ts; falls back to the
        // prior cursor when empty, so we never page backwards).
        if (data?.nextSince != null) sinceRef.current = data.nextSince;
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to fetch chat runtime logs');
      } finally {
        if (!cancelled) {
          setHasLoaded(true);
          schedule();
        }
      }
    };

    function schedule() {
      if (cancelled) return;
      if (timer) { clearTimeout(timer); timer = null; }
      setLive(true);
      timer = setTimeout(poll, CHAT_RUNTIME_POLL_MS);
    }

    poll();
    return () => {
      cancelled = true;
      setLive(false);
      if (timer) clearTimeout(timer);
    };
  }, [projectId, workflowType]);

  // Keep the sticky-bottom flag current on every manual scroll so scrolling up
  // to read history freezes the viewport (no yank-to-bottom on the next poll).
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      const slack = 24;
      stickyBottomRef.current =
        (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, []);

  // Auto-scroll to the bottom on new lines only when anchored at the bottom.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = bodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [lines]);

  const gutterWidth = Math.max(2, String(lines.length).length);

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <span className="pipeline-section-hint" style={{ margin: 0 }}>
            Live runtime logs for this chat agent — streamed from the Zibby
            Copilot Lambda runtime, filtered to this agent only.
          </span>
          <div className="app-logs-controls">
            {live && !error && (
              <span className="app-logs-live" title="Tailing the chat runtime — polling every 4s">
                Live
              </span>
            )}
          </div>
        </div>

        <pre ref={bodyRef} className="app-logs-body wf-logs-body">
          {!hasLoaded ? (
            // Initial-load skeleton — same shimmer rows the Fargate viewer uses.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : error ? (
            <span className="app-logs-error">⚠ {error}</span>
          ) : lines.length === 0 ? (
            <span className="app-logs-empty">
              No runtime activity yet — chat this agent and its turns will stream
              in here live.
            </span>
          ) : (
            lines.map((line, i) => {
              const ts = formatWorkflowLogTime(line.timestamp);
              // The backend fully curates chat lines to clean, user-facing text
              // — the Lambda system prefix (ISO timestamp, request-id, INFO/
              // ERROR level) is already stripped server-side, and CLI box-frame
              // noise dropped. So the chat panel renders a MINIMAL row —
              // `<time> <message>` only: NO request-id column, NO level badge,
              // NO raw JSON parsing (unlike the columnar Fargate viewer). We
              // just sanitize stray control chars and colour our own curated
              // "Error:" / "Setup needed" lines.
              const clean = sanitizeChatLogMessage(line.message);
              const severity = /^Error:/.test(clean)
                ? 'error'
                : /^Setup needed/.test(clean)
                  ? 'warn'
                  : 'info';
              // Silent-gap hint for the space ABOVE this line (see
              // chatLogGapHint) — quiet on purpose: faint, italic, unnumbered
              // and untimed, because it is a hint about the log rather than an
              // event in it.
              const gapHint = chatLogGapHint(lines[i - 1], line);
              return (
                <React.Fragment key={chatRuntimeLogKey(line, i)}>
                  {gapHint && (
                    <div className="app-logs-gap">
                      <span
                        className="app-logs-lineno"
                        style={{ width: `${gutterWidth}ch` }}
                        aria-hidden
                      />
                      <span className="app-logs-gap-txt">{gapHint}</span>
                    </div>
                  )}
                  <LogLine
                    lineNo={i + 1}
                    gutterWidth={gutterWidth}
                    ts={ts}
                    level={null}
                    msg={clean}
                    body={null}
                    severity={severity}
                  />
                </React.Fragment>
              );
            })
          )}
        </pre>
      </div>
    </div>
  );
};

const FargateWorkflowLogsTab = ({ projectId, workflowType }) => {
  // `lines` is the flat buffer of { timestamp, message, jobId } objects,
  // always chronological (oldest at index 0, newest at the bottom).
  // `hasOlder` says whether scrolling up will yield events EARLIER than
  // lines[0]; the older-page request is keyed off lines[0].timestamp
  // (oldestTsRef), not a server cursor. Live-tail polling only runs while
  // the user is anchored at the bottom (stickyBottomRef) AND a run is
  // executing (hasRunningRef) — once they scroll up to read history we
  // freeze the buffer so their viewport doesn't get stomped.
  const [lines, setLines] = useState([]);
  const [hasOlder, setHasOlder] = useState(false);     // older events exist?
  const [hasRunning, setHasRunning] = useState(false); // any run executing?
  const [emptyMessage, setEmptyMessage] = useState(''); // API `message` (no streams yet)
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [error, setError] = useState(null);
  // Skeleton gate — true once the first fetch resolves (success or error).
  // Subsequent polls / load-older fetches don't flip it back, so the
  // skeleton only flashes on initial mount.
  const [hasLoaded, setHasLoaded] = useState(false);

  const bodyRef = useRef(null);
  const stickyBottomRef = useRef(true);
  // Refs mirror state for callbacks (scroll handler, polling loop) that
  // close over the initial render's values.
  const hasOlderRef = useRef(hasOlder);
  const loadingOlderRef = useRef(loadingOlder);
  const hasRunningRef = useRef(hasRunning);
  // Oldest currently-loaded event timestamp — the cursor for "load older"
  // (we ask the backend for the page strictly EARLIER than this). Kept in a
  // ref so the scroll/poll callbacks read the live value without re-binding.
  const oldestTsRef = useRef(null);
  useEffect(() => { hasOlderRef.current = hasOlder; }, [hasOlder]);
  useEffect(() => { loadingOlderRef.current = loadingOlder; }, [loadingOlder]);
  useEffect(() => { hasRunningRef.current = hasRunning; }, [hasRunning]);
  // Track the oldest loaded timestamp off `lines` so both the initial/live
  // replace and the load-older prepend keep the cursor in sync.
  useEffect(() => {
    oldestTsRef.current = lines.length ? lines[0].timestamp : null;
  }, [lines]);

  // Initial load + live-tail poll. The first fetch always runs so the
  // panel populates; subsequent interval ticks honour the stickyBottom
  // gate (don't clobber a user reading history) and stop entirely once
  // nothing is running. The pipeline being "active" (bundleStatus) is
  // NOT a polling gate — `hasRunning` from the API is the source of truth
  // for whether new lines can still arrive.
  useEffect(() => {
    if (!projectId || !workflowType) return undefined;
    let cancelled = false;
    let firstFetchDone = false;
    let timer = null;

    const fetchLatest = async () => {
      const node = bodyRef.current;
      if (node) {
        const slack = 24;
        stickyBottomRef.current =
          (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      }
      // After the first fetch, don't replace the buffer if the user has
      // scrolled up — fetchLatest pulls only the newest page, which would
      // wipe any older pages they loaded and jump the viewport.
      if (firstFetchDone && !stickyBottomRef.current) {
        schedule();
        return;
      }
      try {
        const data = await WorkflowLogs.getWorkflowLogs(projectId, workflowType, {
          limit: WORKFLOW_LOGS_PAGE,
        });
        if (cancelled) return;
        // Newest window — backend returns lines chronological (oldest→newest)
        // so the newest line sits at the bottom of the buffer. This REPLACES
        // the buffer (only runs when sticky-bottom), so any older pages the
        // user loaded are intentionally dropped to re-anchor on the tail.
        const next = Array.isArray(data?.lines) ? data.lines : [];
        setLines(next);
        setHasOlder(!!data?.hasOlder);
        setHasRunning(!!data?.hasRunning);
        setEmptyMessage(next.length === 0 ? (data?.message || '') : '');
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err?.message || 'Failed to fetch logs');
      } finally {
        if (!cancelled) {
          setHasLoaded(true);
          firstFetchDone = true;
          schedule();
        }
      }
    };

    // Re-arm the poll only while a run is still executing. Once hasRunning
    // is false the loop goes quiet (no fixed interval) — the tab will pick
    // back up on remount / tab re-entry.
    function schedule() {
      if (cancelled) return;
      if (timer) { clearTimeout(timer); timer = null; }
      if (hasRunningRef.current) {
        timer = setTimeout(fetchLatest, WORKFLOW_LOGS_POLL_MS);
      }
    }

    fetchLatest();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId, workflowType]);

  // Load-older — prepend the next older page and restore scroll position
  // so the viewport stays anchored on the line the user was reading.
  // Guarded by hasOlder + loadingOlder to avoid duplicate fan-out.
  const loadOlder = useCallback(async () => {
    if (loadingOlderRef.current) return;
    if (!hasOlderRef.current) return;
    // Need a reference timestamp to page BEFORE. If the buffer is somehow
    // empty there's nothing older to anchor to.
    const before = oldestTsRef.current;
    if (before == null) return;
    setLoadingOlder(true);
    const node = bodyRef.current;
    const prevScrollHeight = node ? node.scrollHeight : 0;
    const prevScrollTop = node ? node.scrollTop : 0;
    try {
      // Fetch the page of events strictly EARLIER than the oldest line we
      // already have, then PREPEND it — so scroll-up always yields OLDER
      // timestamps above the current top, never newer.
      const data = await WorkflowLogs.getWorkflowLogs(projectId, workflowType, {
        limit: WORKFLOW_LOGS_PAGE,
        before,
      });
      const older = Array.isArray(data?.lines) ? data.lines : [];
      // Guard against any boundary overlap: drop events that aren't strictly
      // older than our current oldest (CloudWatch endTime is exclusive, but
      // belt-and-suspenders keeps the buffer strictly increasing).
      const strictlyOlder = older.filter((l) => l.timestamp < before);
      if (strictlyOlder.length > 0) {
        setLines((prev) => [...strictlyOlder, ...prev]);
      }
      setHasOlder(!!data?.hasOlder && strictlyOlder.length > 0);
      requestAnimationFrame(() => {
        const n2 = bodyRef.current;
        if (!n2) return;
        const delta = n2.scrollHeight - prevScrollHeight;
        n2.scrollTop = prevScrollTop + delta;
      });
    } catch (err) {
      setError(err?.message || 'Failed to fetch older logs');
    } finally {
      setLoadingOlder(false);
    }
  }, [projectId, workflowType]);

  // Scroll-position detector — within 50px of the top requests the next
  // older page.
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;
    const onScroll = () => {
      // Keep the sticky-bottom flag CURRENT on every scroll. Without this it
      // only updates during a poll, so scrolling up to read history leaves it
      // stale-true — and the next `lines` change (a load-older prepend or a
      // 5s live-tail tick) makes the auto-scroll effect yank the viewport back
      // down. Updating it here means "scrolled up" → sticky=false → no jump.
      const slack = 24;
      stickyBottomRef.current =
        (node.scrollHeight - node.scrollTop - node.clientHeight) <= slack;
      if (node.scrollTop < 50) loadOlder();
    };
    node.addEventListener('scroll', onScroll);
    return () => node.removeEventListener('scroll', onScroll);
  }, [loadOlder]);

  // Auto-scroll to bottom on live-tail updates only when the user is
  // already near the bottom (sticky-tail). If they've scrolled up to
  // browse history, leave them put.
  useEffect(() => {
    if (!stickyBottomRef.current) return;
    const node = bodyRef.current;
    if (!node) return;
    node.scrollTop = node.scrollHeight;
  }, [lines]);

  const gutterWidth = Math.max(2, String(lines.length).length);

  return (
    <div className="pipeline-tab">
      <div className="page-card app-detail-card">
        <div className="card-header app-logs-header">
          <span className="pipeline-section-hint" style={{ margin: 0 }}>
            Container logs for this workflow — includes warm-runner warm-up
            as well as each run's execution output.
          </span>
          <div className="app-logs-controls">
            {hasRunning && (
              <span className="app-logs-live" title="A run is executing — polling every 5s">
                Live
              </span>
            )}
          </div>
        </div>

        <pre ref={bodyRef} className="app-logs-body wf-logs-body">
          {/* Top sentinel — load-older indicator / end-of-history mark. */}
          {hasLoaded && lines.length > 0 && (
            <div className="app-logs-loader">
              {loadingOlder
                ? 'Loading older lines…'
                : (hasOlder ? 'Scroll up to load older lines…' : '— End of history —')}
            </div>
          )}
          {!hasLoaded ? (
            // Initial-load skeleton — same eight shimmer rows the apps
            // LogsTab uses so there's no layout shift when logs paint.
            <div className="app-logs-skeleton" aria-hidden>
              {[66, 82, 74, 90, 58, 78, 86, 64].map((w, i) => (
                <div key={i} className="app-logs-line app-logs-skel-line">
                  <span
                    className="app-logs-lineno app-logs-skel-lineno"
                    style={{ width: '3ch' }}
                  />
                  <span
                    className="app-logs-linetext app-logs-skel-bar"
                    style={{ width: `${w}%` }}
                  />
                </div>
              ))}
            </div>
          ) : error ? (
            <span className="app-logs-error">⚠ {error}</span>
          ) : lines.length === 0 ? (
            <span className="app-logs-empty">
              {emptyMessage
                || 'No tasks have started yet — no logs available.'}
            </span>
          ) : (
            lines.map((line, i) => {
              const ts = formatWorkflowLogTime(line.timestamp);
              // Parse the raw container message for level/severity so the
              // workflow logs highlight identically to the apps logs.
              // `sniffPlain` is on because workflow messages are plain stdout
              // (not JSON-wrapped), so "Warning"/"error" must be sniffed from
              // the text. The whole message is the primary line, so it goes in
              // the bright `msg` slot (with any JSON body kept dimmed after).
              const p = parseLogPayload(line.message, { sniffPlain: true });
              return (
                <LogLine
                  key={workflowLogKey(line, i)}
                  lineNo={i + 1}
                  gutterWidth={gutterWidth}
                  ts={ts}
                  level={p.level}
                  msg={p.msg || p.body}
                  body={p.msg ? p.body : null}
                  severity={p.severity}
                />
              );
            })
          )}
        </pre>
      </div>
    </div>
  );
};

export default PipelineDetailPage;

// Test-only exports — kept under a __test_only_* prefix so any non-test
// consumer immediately stands out. The Settings tab's WarmPoolSection
// is exported so the smoke test can mount it without dragging in the
// full router + outlet context the page needs.
export { WarmPoolSection as __test_only_WarmPoolSection };
// The Copilot Logs tab, so the silent-gap-hint test can drive the REAL renderer
// with a fixture instead of re-implementing its loop.
export { ChatRuntimeLogsPanel as __test_only_ChatRuntimeLogsPanel };
